'use strict';

let gtmUtils = require('./gtmUtils');
let sanitizeLinkText = gtmUtils.sanitizeLinkText;
let trackingMarkupToPlainText = gtmUtils.trackingMarkupToPlainText;
let trackingEditorialAttrSerialized = gtmUtils.trackingEditorialAttrSerialized;
let normalizeWidgetToken = gtmUtils.normalizeWidgetToken;

/** Core helpers */

/**
 * Editorial card layouts (squared / large / small / full): section title lives on the container
 * (`data-tealium-card-section-title`) for GTM card tracking.
 * @param {JQuery} $from
 * @returns {JQuery}
 */
function findLavazzaEditorialCardContainer($from) {
	if (!$from || !$from.length) {
		return $();
	}
	return $from.closest('.lvz-squared-card-container, .lvz-large-card, .lvz-card-container, [data-lvz-full-card="true"]');
}

/** First non-empty `data-tealium-card-*` between click root and layout container. */
function lavazzaEditorialCardAttr($root, $container, name) {
	return String($root.attr(name) || $container.attr(name) || '').trim();
}

/**
 * Editorial card `category`
 * (magazine cards set `data-tealium-card-category` on the card root).
 * @param {JQuery} $container
 * @param {JQuery} $root
 * @returns {string}
 */
function resolveEditorialCardCategory($container, $root) {
	const sectionTitle = ($container.attr('data-tealium-card-section-title') || '').trim();
	if (sectionTitle) {
		return sectionTitle;
	}
	return ($root.attr('data-tealium-card-category') || '').trim();
}

/**
 * Magazine / stories widgets use dedicated `type`
 * @param {JQuery} $container
 * @param {JQuery} $root
 * @returns {string}
 */
function resolveEditorialCardType($container, $root) {
	if ($root.closest('[data-lvz-magazine-topics]').length) {
		return 'magazine_category_list';
	}

	if ($root.closest('[data-lvz-lavazza-stories-carousel]').length) {
		return 'lavazza_stories_carousel';
	}

	if ($root.closest('[data-lvz-related-article-list]').length) {
		return 'magazine_article_list';
	}

	if ($root.closest('[data-lvz-magazine-articles]').length) {
		return 'magazine_article_list';
	}

	const cardType = lavazzaEditorialCardAttr($root, $container, 'data-tealium-card-type');
	if (cardType && cardType !== 'card') {
		return cardType;
	}

	return cardType;
}

/**
 * @param {string} type
 * @returns {boolean}
 */
function isMagazineWidgetType(type) {
	return type === 'magazine_article_list' || type === 'magazine_category_list' || type === 'lavazza_stories_carousel';
}

/**
 * PDF: magazine `label` = CTA text or `none`;
 * @param {JQuery} $root
 * @param {JQuery} $link
 * @param {string} labelRaw
 * @param {string} type
 * @returns {string}
 */
function resolveMagazineEditorialCardLabel($root, $link, labelRaw, type) {
	if (!isMagazineWidgetType(type)) {
		return labelRaw;
	}

	if ((type === 'magazine_article_list' || type === 'lavazza_stories_carousel') && $link.length) {
		const ctaText = $link.find('.btn__label').first().text().trim();
		if (ctaText) {
			return ctaText;
		}
	}

	if (trackingMarkupToPlainText(labelRaw)) {
		return labelRaw;
	}

	return 'none';
}

/** True when the element lives inside the header megamenu (catalog or Page Designer). */
function isInsideNavbarMegamenu($el) {
	if (!$el || !$el.length || !$el.closest('.lavazza-navbar').length) {
		return false;
	}
	return (
		$el.closest('[data-lvz-header-menu]').length > 0 ||
		$el.closest('.editor-menu').length > 0 ||
		$el.closest('li.navbar-item-level-1 .dd__menu').length > 0 ||
		$el.closest('.navigation-menu-column__inner').length > 0
	);
}

/**
 * @param {JQuery} $scope
 * @param {JQuery} $target
 * @returns {JQuery}
 */
function resolveNestedHrefLink($scope, $target) {
	let $link = $scope.closest('a[href]');
	let $inner;

	if ($link.length) {
		return $link;
	}

	$inner = $target.closest('a[href]');
	if ($inner.length && $.contains($scope[0], $inner[0])) {
		return $inner;
	}

	return $();
}

/**
 * @param {JQuery} $scope
 * @param {JQuery} $target
 * @returns {boolean}
 */
function shouldSkipNestedInteractiveClick($scope, $target) {
	let $interactive = $target.closest('button, input:not([type="hidden"]), select, textarea, [role="button"]');
	return !!($interactive.length && !$interactive.is($scope));
}

/**
 * @param {JQuery} $scope
 * @param {JQuery} $link
 * @param {boolean} isCta
 * @param {JQuery} $gridCardItem
 * @returns {JQuery}
 */
/**
 * PDF "Editorial Component | Card": label = CTA text.
 * - CTA click: visible CTA text (aria-label is the accessibility sentence "Discover more about ...").
 * - Click elsewhere on a Full Card (image, body): its first CTA text, then the card label (title),
 *   then the image alt, so an image-only card never sends an empty label.
 * @param {JQuery} $scope clicked tracking element
 * @param {JQuery} $attrRoot element holding the data-tealium-card-* attributes
 * @param {boolean} isCta
 * @returns {string}
 */
function resolveEditorialCardLabel($scope, $attrRoot, isCta) {
	if (isCta) {
		return $scope.find('.btn__label').first().text() || $scope.attr('aria-label') || '';
	}

	if ($attrRoot.is('.lvz-full-card-item')) {
		return (
			$attrRoot.find('[data-lvz-cta-button="true"] .btn__label').first().text().trim() ||
			$attrRoot.attr('data-tealium-card-label') ||
			$attrRoot.find('img').first().attr('alt') ||
			''
		);
	}

	return $attrRoot.attr('data-tealium-card-label') || '';
}

function resolveEditorialCardAttrRoot($scope, $link, isCta, $gridCardItem) {
	if ($gridCardItem.length) {
		return $gridCardItem;
	}
	if (isCta) {
		return $scope;
	}
	if ($link.length && $scope.is('a[href]')) {
		return $link;
	}
	return $scope;
}

/**
 * @param {JQuery} $scope
 * @param {JQuery} $link
 * @returns {{ eventName: string, type: string, labelRaw: string, categoryRaw: string }}
 */
function readGenericEditorialTrackingAttrs($scope, $link) {
	return {
		eventName: normalizeWidgetToken(
			$scope.attr('data-tealium-event-name') || ($link.length ? $link.attr('data-tealium-event-name') : '') || ''
		),
		type: normalizeWidgetToken(
			$scope.attr('data-tealium-type') || ($link.length ? $link.attr('data-tealium-type') : '') || ''
		),
		labelRaw: $scope.attr('data-tealium-label') || ($link.length ? $link.attr('data-tealium-label') : '') || '',
		// Items rendered by a child component (e.g. Badge) cannot read the container title server-side:
		// fall back to the enclosing section title.
		categoryRaw:
			$scope.attr('data-tealium-category') ||
			($link.length ? $link.attr('data-tealium-category') : '') ||
			$scope.closest('[data-tealium-section-title]').attr('data-tealium-section-title') ||
			''
	};
}

/** Checkout tracking */

let PENDING_PROMO_CODE_KEY = 'checkoutTrackingPendingPromoCode';
let ORDER_CONFIRM_ACTION = 'Order-Confirm';
let CHECKOUT_TYP_EVENT = 'cart_typ';
let CHECKOUT_CART_VIEW_FIELDS = ['order_currency', 'standard_purchase_revenue', 'subscription_revenue'];
let CHECKOUT_STEP0_FIELDS = ['order_currency', 'order_promotion_code', 'standard_purchase_revenue', 'subscription_revenue'];
let CHECKOUT_STEP_FIELDS = [
	'order_currency',
	'order_promotion_code',
	'standard_purchase_revenue',
	'subscription_revenue',
	'user_type'
];
let CHECKOUT_GUEST_FIELDS = CHECKOUT_STEP_FIELDS;
let CHECKOUT_TYP_FIELDS = [
	'order_currency',
	'order_promotion_code',
	'user_type',
	'order_id',
	'order_shipping_type',
	'order_shipping_cost',
	'order_payment_type',
	'order_payment_cost',
	'order_product_tax',
	'order_shipping_tax',
	'order_type',
	'standard_purchase_revenue',
	'subscription_revenue',
	'subscription_revenue_forecast'
];

let CHECKOUT_STAGE_EVENTS = {
	login: 'cart_step0',
	shipping: 'cart_step1',
	payment: 'cart_step2',
	placeOrder: 'cart_step3'
};
let lastCheckoutStepEventName = '';
let pendingCheckoutStepStage = '';
let checkoutFirstStepTimer = null;

/**
 * @param {string} couponCode
 */
function setPendingApplyPromoCode(couponCode) {
	if (!couponCode || !String(couponCode).trim()) {
		$('body').removeData(PENDING_PROMO_CODE_KEY);
		return;
	}

	$('body').data(PENDING_PROMO_CODE_KEY, String(couponCode).trim());
}

function clearPendingApplyPromoCode() {
	$('body').removeData(PENDING_PROMO_CODE_KEY);
}

/**
 * @returns {string}
 */
function consumePendingApplyPromoCode() {
	let couponCode = $('body').data(PENDING_PROMO_CODE_KEY);

	clearPendingApplyPromoCode();

	return couponCode ? String(couponCode).trim() : '';
}

/**
 * @param {string} couponCode
 */
function trackApplyPromo(couponCode) {
	couponCode = String(couponCode || '').trim();

	if (!couponCode) {
		return;
	}

	let payload = {
		event_name: 'cta_apply-promo',
		order_promotion_code: couponCode
	};

	gtmUtils.sendUtagLinkEvent(payload);
}

/**
 * Call on promotion:success only when the user submitted the promo form (not coupon remove).
 */
function trackApplyPromoIfPending() {
	let couponCode = consumePendingApplyPromoCode();

	if (!couponCode) {
		return;
	}

	trackApplyPromo(couponCode);
}

function getCheckoutOrderTrackingData() {
	return (
		gtmUtils.parseProductTrackingJson($('[data-tealium-order-tracking]').first().attr('data-tealium-order-tracking')) || {}
	);
}

function getPageSubscriptionModuleRaw() {
	return gtmUtils.getPageSubscriptionModuleRaw() || getCheckoutOrderTrackingData();
}

function attachSubscriptionModule(payload, rawOverride) {
	return $.extend({}, payload || {}, gtmUtils.getSubscriptionModule(rawOverride || getPageSubscriptionModuleRaw()));
}

function getCheckoutProductsPayload() {
	let $el = $(
		'#checkout-main[data-tealium-product-modules], [data-tealium-cart-page="cart"][data-tealium-product-modules], .confirmation-top-section[data-tealium-product-modules], .checkout-login-page[data-tealium-product-modules]'
	).first();

	return gtmUtils.parseFormattedProductModules($el.attr('data-tealium-product-modules'));
}

function isLoggedInFromUtag() {
	return String((window.utag_data && window.utag_data.login_status) || '') === 'loggedin';
}

function resolveCheckoutTrackingFieldValue(data, key) {
	if (data[key] != null && data[key] !== '') {
		return data[key];
	}

	if (key === 'order_currency') {
		return String((window.utag_data && window.utag_data.store_currency) || '')
			.trim()
			.toUpperCase();
	}

	if (key === 'user_type') {
		return isLoggedInFromUtag() ? 'logged_user' : 'guest_user';
	}

	return data[key] != null ? String(data[key]) : '';
}

function pickCheckoutTrackingFields(fieldNames) {
	let data = getCheckoutOrderTrackingData();
	let payload = {};
	let i;
	let key;

	for (i = 0; i < fieldNames.length; i++) {
		key = fieldNames[i];
		payload[key] = resolveCheckoutTrackingFieldValue(data, key);

		if (key === 'order_currency') {
			payload[key] = String(payload[key] || '')
				.trim()
				.toUpperCase();
		}
	}

	return payload;
}

function getLineItemSubscriptionModuleRaw($lineItem) {
	let fromLine;

	if ($lineItem && $lineItem.length) {
		fromLine = gtmUtils.parseProductTrackingJson($lineItem.attr('data-tealium-subscription-module'));
		if (fromLine) {
			return fromLine;
		}
	}

	return getPageSubscriptionModuleRaw();
}

function clearPendingCartRemove() {
	pendingCartRemoveProduct = null;
	pendingCartRemoveSubscriptionModule = null;
}

/**
 * Dictionary page_* for in-page steps (checkout, subscription journey): utag_data keeps the landing
 * values, the step utag.view carries the current page_subsection / page_name (PDF "Pageview").
 * @param {string} subsection
 * @param {string} [section] defaults to the landing page_section
 * @returns {{ page_subsection: string, page_name_nocountry: string, page_name: string }}
 */
function buildStepPageFields(subsection, section) {
	let data = window.utag_data || {};
	let nocountry = [section || data.page_section, subsection].filter(Boolean).join(':');

	return {
		page_subsection: subsection,
		page_name_nocountry: nocountry,
		page_name: [data.store_country, nocountry].filter(Boolean).join(':')
	};
}

function buildCheckoutEventPayload(eventName, fieldNames) {
	let payload = attachSubscriptionModule($.extend({ event_name: eventName }, pickCheckoutTrackingFields(fieldNames)));
	let products = getCheckoutProductsPayload();

	if (products.length) {
		payload.products = products;
	}

	return payload;
}

function getCheckoutStepFieldNames(eventName) {
	return eventName === 'cart_step0' ? CHECKOUT_STEP0_FIELDS : CHECKOUT_STEP_FIELDS;
}

function trackProceedAsGuest() {
	gtmUtils.sendUtagLinkEvent({
		event_name: 'proceed_as_a_guest'
	});
}

function trackGuestContinue() {
	gtmUtils.sendUtagLinkEvent(buildCheckoutEventPayload('cart_guest_continue', CHECKOUT_GUEST_FIELDS));
}

function initCheckoutGuestTracking() {
	if (window.isCheckoutGuestTrackingInitialized) {
		return;
	}

	window.isCheckoutGuestTrackingInitialized = true;

	$(document).on('click', '[data-tealium-guest-checkout="true"]', function onGuestCheckoutClick() {
		trackProceedAsGuest();
	});

	$(document).on('click', '[data-tealium-guest-continue="true"], .submit-customer', function onGuestContinueClick() {
		trackGuestContinue();
	});
}

let lastExpressCheckoutEventKey = '';
let lastExpressCheckoutEventAt = 0;
const PAYPAL_EXPRESS_CART_DEDUPE_MS = 5000;

const EXPRESS_PAYMENT_METHOD_SLUGS = {
	paypal: 'paypal',
	googlepay: 'googlepay',
	paywithgoogle: 'googlepay',
	applepay: 'applepay',
	amazonpay: 'amazonpay'
};

/**
 * @param {string} type
 * @returns {string}
 */
function normalizeExpressPaymentMethodSlug(type) {
	const raw = String(type || '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

	return EXPRESS_PAYMENT_METHOD_SLUGS[raw] || sanitizeLinkText(raw);
}

/**
 * @returns {boolean}
 */
function hasPaypalExpressOnCartPage() {
	return (
		$('#paypal-container, .expressComponent.paypal, #express-container [data-method="paypal"], [class*="paypal-buttons"]')
			.length > 0
	);
}

/**
 * @param {*} [isExpressPdp]
 * @returns {boolean}
 */
function isCartExpressCheckoutContext(isExpressPdp) {
	if (isExpressPdp === true || String(isExpressPdp).toLowerCase() === 'true') {
		return false;
	}

	if ($('[data-tealium-paypal-express="cart"]').length) {
		return true;
	}

	if ($('[data-tealium-cart-page="cart"]').length) {
		return true;
	}

	if ($('.cart-page').length && $('#express-container').length) {
		return true;
	}

	const pageAction = String($('.page[data-action]').attr('data-action') || '').toLowerCase();

	return pageAction === 'cart-show' || pageAction.indexOf('cart-show') !== -1;
}

/**
 * @param {Object} settings
 * @returns {Object|null}
 */
function parseExpressPaymentsCallPayload(settings) {
	const data = settings && settings.data;

	if (!data) {
		return null;
	}

	if (typeof data === 'object' && !Array.isArray(data)) {
		if (data.data && typeof data.data === 'string') {
			try {
				return JSON.parse(data.data);
			} catch (e) {
				return null;
			}
		}

		return data;
	}

	if (typeof data === 'string') {
		const trimmed = data.trim();

		if (trimmed.startsWith('{')) {
			return gtmUtils.parseAjaxJsonPayload(trimmed);
		}

		let nested = '';

		try {
			nested = new URLSearchParams(data).get('data') || '';
		} catch (e) {
			nested = '';
		}

		if (nested) {
			try {
				return JSON.parse(nested);
			} catch (e2) {
				try {
					return JSON.parse(decodeURIComponent(nested));
				} catch (e3) {
					return null;
				}
			}
		}

		return gtmUtils.parseAjaxJsonPayload(data);
	}

	return null;
}

/**
 * @param {Object|null} payload
 * @returns {string}
 */
function resolveExpressPaymentMethodSlug(payload) {
	if (!payload) {
		return '';
	}

	const nested = payload.paymentMethod && payload.paymentMethod.type ? payload.paymentMethod.type : '';
	const methodType = payload.paymentMethodType || nested || payload.type || '';
	const slug = normalizeExpressPaymentMethodSlug(methodType);

	if (slug) {
		return slug;
	}

	const paymentMethod = payload.paymentMethod || payload.paymentMethodData;

	if (paymentMethod && typeof paymentMethod === 'object') {
		return normalizeExpressPaymentMethodSlug(
			paymentMethod.type || paymentMethod.paymentMethodType || paymentMethod.brand || paymentMethod.name || ''
		);
	}

	return '';
}

/**
 * @param {Object} settings
 * @returns {string}
 */
function resolveExpressPaymentMethodSlugFromSettings(settings) {
	const payload = parseExpressPaymentsCallPayload(settings);
	let slug = resolveExpressPaymentMethodSlug(payload);

	if (slug) {
		return slug;
	}

	const raw =
		typeof (settings && settings.data) === 'string' ? settings.data : JSON.stringify((settings && settings.data) || {});

	if (/paypal/i.test(raw)) {
		return 'paypal';
	}

	const $expressMethods = $('#express-container [data-method]');

	if ($expressMethods.length === 1 && String($expressMethods.first().attr('data-method') || '').toLowerCase() === 'paypal') {
		return 'paypal';
	}

	return '';
}

/**
 * @param {Object} settings
 * @returns {*}
 */
function resolveExpressPdpFlagFromSettings(settings) {
	const payload = parseExpressPaymentsCallPayload(settings);

	return payload && payload.isExpressPdp;
}

/**
 * @param {string} eventName
 */
function trackExpressCheckoutLinkEvent(eventName) {
	const dedupeKey = eventName;
	const now = Date.now();
	const dedupeMs = eventName === 'paypal_express_cart' ? PAYPAL_EXPRESS_CART_DEDUPE_MS : 400;

	if (dedupeKey === lastExpressCheckoutEventKey && now - lastExpressCheckoutEventAt < dedupeMs) {
		return;
	}

	lastExpressCheckoutEventKey = dedupeKey;
	lastExpressCheckoutEventAt = now;

	gtmUtils.sendUtagLinkEvent({
		event_name: eventName
	});
}

const PAYPAL_EXPRESS_ROOT_SELECTOR = '[data-tealium-paypal-express="cart"], #express-container';
const PAYPAL_EXPRESS_IFRAME_SELECTOR =
	PAYPAL_EXPRESS_ROOT_SELECTOR + ' iframe, #paypal-container iframe, [class*="paypal-buttons"] iframe';

/**
 * PayPal Smart Button renders inside a cross-origin iframe — parent cannot listen to click inside it.
 * @param {Element} [element]
 * @returns {boolean}
 */
function isPaypalExpressClickTarget(element) {
	if (!element) {
		return false;
	}

	const $target = $(element);

	if (
		$target.is('iframe') &&
		$target.closest(PAYPAL_EXPRESS_ROOT_SELECTOR + ', #paypal-container, [class*="paypal-buttons"]').length
	) {
		return true;
	}

	return $target.closest('[class*="paypal-buttons"], #paypal-container, .expressComponent.paypal').length > 0;
}

/**
 * PDF Batch 4 slide 121: paypal_express_cart on cart PayPal express checkout.
 */
function trackPaypalExpressCartFromUserIntent() {
	if (!isCartExpressCheckoutContext()) {
		return;
	}

	trackExpressCheckoutLinkEvent('paypal_express_cart');
}

/**
 * @param {Object} [settings]
 * @param {*} [isExpressPdp]
 */
function trackPaypalExpressCartIfApplicable(settings, isExpressPdp) {
	if (!isCartExpressCheckoutContext(isExpressPdp !== undefined ? isExpressPdp : resolveExpressPdpFlagFromSettings(settings))) {
		return;
	}

	if (settings) {
		const url = String(settings.url || '');

		if (url.indexOf('MakeExpressPaymentsCall') === -1) {
			return;
		}

		const methodSlug = resolveExpressPaymentMethodSlugFromSettings(settings);
		const hasPaypalExpressUi = $('#paypal-container, [class*="paypal-buttons"]').length > 0;

		if (methodSlug && methodSlug !== 'paypal') {
			return;
		}

		if (!methodSlug && !hasPaypalExpressUi) {
			return;
		}
	}

	trackExpressCheckoutLinkEvent('paypal_express_cart');
}

/**
 * cartExpressPayments.js bundles its own jQuery — $(document).ajaxSend in main.js never sees Adyen XHR.
 * @param {*} body
 * @returns {*}
 */
function resolveExpressPdpFlagFromRequestBody(body) {
	if (body === undefined || body === null) {
		return undefined;
	}

	const raw = typeof body === 'string' ? body : String(body);

	if (/isExpressPdp["':\s]+true/i.test(raw)) {
		return true;
	}

	if (/isExpressPdp["':\s]+false/i.test(raw)) {
		return false;
	}

	try {
		if (raw.trim().startsWith('{')) {
			const parsed = JSON.parse(raw);

			if (parsed && parsed.isExpressPdp !== undefined) {
				return parsed.isExpressPdp;
			}
		}
	} catch (e) {
		// ignore
	}

	try {
		const nested = new URLSearchParams(raw).get('data');

		if (nested) {
			const parsed = JSON.parse(decodeURIComponent(nested) || nested);

			if (parsed && parsed.isExpressPdp !== undefined) {
				return parsed.isExpressPdp;
			}
		}
	} catch (e2) {
		// ignore
	}

	return undefined;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
function isPaypalExpressXhrUrl(url) {
	const target = String(url || '');

	return (
		target.indexOf('MakeExpressPaymentsCall') !== -1 ||
		target.indexOf('SaveExpressPaymentData') !== -1 ||
		target.indexOf('SaveShopperData') !== -1
	);
}

/**
 * @param {string} url
 * @param {*} body
 */
function trackPaypalExpressCartFromXhr(url, body) {
	const urlStr = String(url || '');

	if (urlStr.indexOf('MakeExpressPaymentsCall') === -1) {
		return;
	}

	const isExpressPdp = resolveExpressPdpFlagFromRequestBody(body);

	if (!isCartExpressCheckoutContext(isExpressPdp)) {
		return;
	}

	const raw = String(body || '');
	const hasPaypalExpressUi = hasPaypalExpressOnCartPage();

	if (!/paypal/i.test(raw) && !hasPaypalExpressUi) {
		return;
	}

	trackExpressCheckoutLinkEvent('paypal_express_cart');
}

function flushPendingExpressPaymentXhr() {
	const pending = window.__aurPendingExpressPaymentXhr;

	if (!pending) {
		return;
	}

	window.__aurPendingExpressPaymentXhr = null;
	trackPaypalExpressCartFromXhr(pending.url, pending.body);
}

function initPaypalExpressXhrTracking() {
	window.__aurHandleExpressPaymentXhr = trackPaypalExpressCartFromXhr;
	flushPendingExpressPaymentXhr();

	if (window.isPaypalExpressXhrTrackingInitialized) {
		return;
	}

	window.isPaypalExpressXhrTrackingInitialized = true;

	if (window.__aurExpressPaymentXhrBootstrap) {
		return;
	}

	const originalOpen = XMLHttpRequest.prototype.open;
	const originalSend = XMLHttpRequest.prototype.send;

	XMLHttpRequest.prototype.open = function patchExpressXhrOpen(method, url) {
		this.__aurExpressUrl = url;
		return originalOpen.apply(this, arguments);
	};

	XMLHttpRequest.prototype.send = function patchExpressXhrSend(body) {
		try {
			trackPaypalExpressCartFromXhr(this.__aurExpressUrl, body);
		} catch (e) {
			// ignore express xhr patch errors
		}

		return originalSend.apply(this, arguments);
	};

	if (typeof window.fetch === 'function' && !window.isPaypalExpressFetchTrackingInitialized) {
		window.isPaypalExpressFetchTrackingInitialized = true;
		const originalFetch = window.fetch.bind(window);

		window.fetch = function patchExpressFetch(input, init) {
			try {
				const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
				const body = init && init.body !== undefined ? init.body : undefined;

				trackPaypalExpressCartFromXhr(url, body);
			} catch (e) {
				// ignore express fetch patch errors
			}

			return originalFetch(input, init);
		};
	}
}

/**
 * @param {Object} settings
 */
function trackAdyenExpressAjax(settings) {
	const url = settings && settings.url ? String(settings.url) : '';

	if (!isPaypalExpressXhrUrl(url)) {
		return;
	}

	trackPaypalExpressCartFromXhr(url, settings && settings.data);
}

function bindPaypalExpressContainerTracking(container) {
	if (!container || container.getAttribute('data-tealium-paypal-express-bound') === 'true') {
		return;
	}

	container.setAttribute('data-tealium-paypal-express-bound', 'true');

	container.addEventListener(
		'mousedown',
		function onPaypalExpressContainerMouseDown(event) {
			if (isPaypalExpressClickTarget(event.target)) {
				trackPaypalExpressCartFromUserIntent('container-mousedown');
			}
		},
		true
	);

	container.addEventListener(
		'pointerdown',
		function onPaypalExpressContainerPointerDown(event) {
			if (isPaypalExpressClickTarget(event.target)) {
				trackPaypalExpressCartFromUserIntent('container-pointerdown');
			}
		},
		true
	);
}

function initPaypalExpressCartClickTracking() {
	if (window.__aurExpressPaymentXhrBootstrap) {
		return;
	}

	document.addEventListener(
		'mousedown',
		function onPaypalExpressDocumentMouseDown(event) {
			if (!isCartExpressCheckoutContext()) {
				return;
			}

			if (isPaypalExpressClickTarget(event.target)) {
				trackPaypalExpressCartFromUserIntent('document-mousedown');
			}
		},
		true
	);

	document.addEventListener(
		'pointerdown',
		function onPaypalExpressDocumentPointerDown(event) {
			if (!isCartExpressCheckoutContext()) {
				return;
			}

			if (isPaypalExpressClickTarget(event.target)) {
				trackPaypalExpressCartFromUserIntent('document-pointerdown');
			}
		},
		true
	);

	window.addEventListener('blur', function onPaypalExpressIframeWindowBlur() {
		if (!isCartExpressCheckoutContext()) {
			return;
		}

		window.setTimeout(function onPaypalExpressIframeFocusCheck() {
			const activeEl = document.activeElement;

			if (activeEl && activeEl.tagName === 'IFRAME' && isPaypalExpressClickTarget(activeEl)) {
				trackPaypalExpressCartFromUserIntent('window-blur-iframe');
			}
		}, 0);
	});

	$(document).on('focus', PAYPAL_EXPRESS_IFRAME_SELECTOR, function onPaypalExpressIframeFocus() {
		trackPaypalExpressCartFromUserIntent('iframe-focus');
	});

	$(PAYPAL_EXPRESS_ROOT_SELECTOR).each(function onPaypalExpressRootBind() {
		bindPaypalExpressContainerTracking(this);
	});

	if (!window.isPaypalExpressContainerObserverInitialized) {
		window.isPaypalExpressContainerObserverInitialized = true;

		const observer = new MutationObserver(function onPaypalExpressContainerMutation() {
			$(PAYPAL_EXPRESS_ROOT_SELECTOR).each(function onPaypalExpressRootObserveBind() {
				bindPaypalExpressContainerTracking(this);
			});
		});

		observer.observe(document.body, { childList: true, subtree: true });
	}
}

function initCheckoutExpressTracking() {
	if (window.isCheckoutExpressTrackingInitialized) {
		return;
	}

	window.isCheckoutExpressTrackingInitialized = true;

	initPaypalExpressXhrTracking();
	initPaypalExpressCartClickTracking();

	if (!window.__aurExpressPaymentXhrBootstrap) {
		$.ajaxPrefilter(function onExpressCheckoutAjaxPrefilter(_options, _originalOptions) {
			trackAdyenExpressAjax(_originalOptions || _options);
		});

		$(document).ajaxSend(function onExpressPaymentsCallAjaxSend(_event, _jqXHR, settings) {
			trackAdyenExpressAjax(settings);
		});
	}

	// Shopper Agent / MIAW relay: express iframe posts to top; parent $.ajax may use JSON body.
	window.addEventListener('message', function onExpressRelayPaypalCartMessage(event) {
		const message = event && event.data;

		if (!message || message.type !== 'express.relay.request') {
			return;
		}

		const url = String(message.url || '');

		if (url.indexOf('MakeExpressPaymentsCall') === -1) {
			return;
		}

		const body = message.body && typeof message.body === 'object' ? message.body : {};
		const isExpressPdp = body.isExpressPdp;
		const relayData = body.data && typeof body.data === 'object' ? body.data : body;
		const methodType =
			relayData.paymentMethodType || (relayData.paymentMethod && relayData.paymentMethod.type) || relayData.type || '';

		if (
			normalizeExpressPaymentMethodSlug(methodType) !== 'paypal' &&
			!/paypal/i.test(JSON.stringify(body)) &&
			!$('#paypal-container').length
		) {
			return;
		}

		trackPaypalExpressCartIfApplicable(null, isExpressPdp);
	});
}

/**
 * @param {string} stage
 * @returns {string}
 */
function resolveCheckoutStepEventName(stage) {
	stage = String(stage || '').trim();

	return CHECKOUT_STAGE_EVENTS[stage] || '';
}

/**
 * @returns {boolean}
 */
function isCheckoutPage() {
	return document.getElementById('checkout-main') !== null;
}

/**
 * @returns {boolean}
 */
function isOrderConfirmPage() {
	let pageEl = document.querySelector('.page[data-action]');

	return !!(pageEl && pageEl.getAttribute('data-action') === ORDER_CONFIRM_ACTION);
}

function isCheckoutLoginPage() {
	return $('.checkout-login-page').length > 0;
}

function getCheckoutDomStage() {
	return String($('#checkout-main').attr('data-checkout-stage') || '').trim();
}

/**
 * @param {string} stage SFCC checkout stage (customer, shipping, payment, placeOrder)
 */
function flushCheckoutStepView(stage) {
	let eventName = resolveCheckoutStepEventName(stage);
	let payload;

	if (!eventName || eventName === lastCheckoutStepEventName) {
		return;
	}

	try {
		payload = buildCheckoutEventPayload(eventName, getCheckoutStepFieldNames(eventName));
	} catch (e) {
		payload = { event_name: eventName };
	}

	$.extend(payload, buildStepPageFields(eventName.replace(/^cart_/, '')));
	lastCheckoutStepEventName = eventName;
	gtmUtils.sendUtagViewEvent(payload);
}

/**
 * Contact information section (stage "customer") is not a PDF checkout step: no cart_step0 here
 * (that is the login/registration page), but the page still needs its unique utag.view with the
 * basic information module only.
 */
function trackCheckoutContactInformationView() {
	if (lastCheckoutStepEventName === 'contact_information') {
		return;
	}

	lastCheckoutStepEventName = 'contact_information';
	gtmUtils.sendUtagViewEvent();
}

function queueCheckoutStepView(stage) {
	let eventName = resolveCheckoutStepEventName(stage);
	let pendingEventName;

	if (String(stage || '').trim() === 'customer') {
		trackCheckoutContactInformationView();
		return;
	}

	if (!eventName || eventName === lastCheckoutStepEventName) {
		return;
	}

	if (checkoutFirstStepTimer) {
		pendingEventName = resolveCheckoutStepEventName(pendingCheckoutStepStage);

		if (pendingEventName === eventName) {
			return;
		}

		window.clearTimeout(checkoutFirstStepTimer);
		checkoutFirstStepTimer = null;
		flushCheckoutStepView(pendingCheckoutStepStage);
	}

	if (!lastCheckoutStepEventName) {
		pendingCheckoutStepStage = stage;
		checkoutFirstStepTimer = window.setTimeout(function onDelayedCheckoutStep() {
			checkoutFirstStepTimer = null;
			flushCheckoutStepView(pendingCheckoutStepStage);
		}, 900);
		return;
	}

	flushCheckoutStepView(stage);
}

function trackCheckoutTyp() {
	gtmUtils.sendUtagViewEvent(buildCheckoutEventPayload(CHECKOUT_TYP_EVENT, CHECKOUT_TYP_FIELDS));
}

function trackCheckoutLoginPageViewIfApplicable() {
	if (!isCheckoutLoginPage()) {
		return false;
	}

	flushCheckoutStepView('login');
	return true;
}

function initCheckoutStepTracking() {
	if (window.isCheckoutStepTrackingInitialized) {
		return;
	}

	window.isCheckoutStepTrackingInitialized = true;

	$('body').on('checkout:stageChange', function onCheckoutStageChange(e, data) {
		queueCheckoutStepView((data && data.stage) || getCheckoutDomStage());
	});

	queueCheckoutStepView(getCheckoutDomStage());
}

/**
 * Thank-you page: named utag.view instead of generic pageview.
 * @returns {boolean} true when cart_typ was sent (caller should skip generic pageview)
 */
function trackTypPageViewIfApplicable() {
	if (!isOrderConfirmPage()) {
		return false;
	}

	trackCheckoutTyp();
	return true;
}

let isEcommerceTrackingInitialized = false;
let pendingPlpLoadMore = false;
let pendingPlpImpressionContext = '';
let pendingCartRemoveProduct = null;
let pendingCartRemoveSubscriptionModule = null;
let pendingCartAdd = null;
let pendingEcommerceAddToCartShell = null;
let lastProductClickDedupeKey = '';
let lastProductClickDedupeAt = 0;

/**
 * @returns {boolean}
 */
function isInternalSearchResultsPage() {
	return !!gtmUtils.getUrlQueryParam('q');
}

/**
 * @returns {string}
 */
function getInternalSearchQueryFromUrl() {
	return gtmUtils.getUrlQueryParam('q');
}

function hasPlpFiltersInUrl() {
	return /[?&](prefn\d+|prefv\d+|pmin|pmax)=/.test(String(window.location.search || ''));
}

function hasPlpSortInUrl() {
	return !!gtmUtils.getUrlQueryParam('srule');
}

function resolvePlpSortImpressionType() {
	let $drawer = $('#filter-sort-drawer');
	let sortId = getCurrentSortRule($drawer);
	let sortLabel = getSortChoiceLabel($drawer, sortId);

	return sortLabel ? 'sort_' + sortLabel : 'sort_modify';
}

/**
 * PDF: listing | search | filters_applied | sort_{choice}
 * @param {string} [context] landing | filter | sort | loadmore
 * @returns {string}
 */
function resolvePlpListingImpressionType(context) {
	if (context === 'filter') {
		return 'filters_applied';
	}

	if (context === 'sort') {
		return resolvePlpSortImpressionType();
	}

	if (hasPlpFiltersInUrl()) {
		return 'filters_applied';
	}

	if (hasPlpSortInUrl()) {
		return resolvePlpSortImpressionType();
	}

	if (isInternalSearchResultsPage()) {
		return 'search';
	}

	return 'listing';
}

let CAROUSEL_IMPRESSION_ROOT =
	'[data-tealium-impression-type], [data-lvz-generic-product-carousel], [data-lvz-recommended-product-carousel], [data-lvz-product-highlight], [data-lvz-collection-layer-item], .experience-einstein-carousel, .einstein-carousel';

/**
 * Editorial / Einstein carousel title → PDF impression type (your_recently_viewed, tierra_blends, …).
 * Untitled carousels use "carousel" so they stay distinct from PLP "listing".
 * @param {jQuery} $el
 * @returns {string}
 */
function resolveCarouselImpressionType($el) {
	let $root;
	let fromAttr;
	let $title;
	let fromTitle;

	if (!$el || !$el.length) {
		return '';
	}

	$root = $el.closest(CAROUSEL_IMPRESSION_ROOT);
	if (!$root.length) {
		return '';
	}

	fromAttr = sanitizeLinkText($root.attr('data-tealium-impression-type') || '');
	if (fromAttr) {
		return fromAttr;
	}

	$title = $root
		.find(
			'.lvz-generic-product-carousel__title, .lvz-recommended-product-carousel__title, .lvz-product-highlight__title, .lvz-collection-layer__title, .carousel-title'
		)
		.first();

	if (!$title.length && $root.is('.experience-einstein-carousel, .einstein-carousel')) {
		$title = $root.parent().find('.carousel-title').first();
	}

	if (!$title.length) {
		$title = $root.closest('[data-lvz-collection-layer]').find('.lvz-collection-layer__title').first();
	}

	if ($title.length) {
		fromTitle = sanitizeLinkText($title.text());

		if (fromTitle) {
			return fromTitle;
		}
	}

	return 'carousel';
}

/**
 * PDF product_impression_type for the tile/context (listing, search, sort_*, filters_applied,
 * carousel title, pdp, cart, wishlist, subscription).
 * @param {jQuery} [$el]
 * @returns {string}
 */
function resolveProductImpressionType($el) {
	let carouselType;
	let $context = $el && $el.length ? $el : $();

	carouselType = resolveCarouselImpressionType($context);
	if (carouselType) {
		return carouselType;
	}

	if ($context.closest('[data-subscriptions-root]').length) {
		return 'subscription';
	}

	if (!$context.length && (isSubscriptionJourneyPage() || isSubscriptionHubPage())) {
		return 'subscription';
	}

	if ($context.closest('.minicart, .cart-page, .cart-line-item').length) {
		return 'cart';
	}

	if (!$context.length && gtmUtils.isCartShowPage()) {
		return 'cart';
	}

	if ($context.closest('.product-grid').length) {
		return resolvePlpListingImpressionType();
	}

	if ($context.closest('.product-detail, .product-wrapper').length) {
		return 'pdp';
	}

	if (gtmUtils.isProductShowPage() && !$context.length) {
		return 'pdp';
	}

	if (isPlpImpressionPage()) {
		return resolvePlpListingImpressionType();
	}

	if (isInternalSearchResultsPage()) {
		return 'search';
	}

	return 'listing';
}

/**
 * @returns {boolean}
 */
function isPlpImpressionPage() {
	let action = String($('.page').attr('data-action') || '');

	if (!action.startsWith('Search-')) {
		return false;
	}

	return $('.product-grid .product-tile-shell[data-tealium-product]').length > 0;
}

/**
 * @param {jQuery} $shell
 */
function trackProductClick($shell) {
	let product;
	let dedupeKey;
	let now;

	if (!$shell || !$shell.length || $shell.closest('[data-subscriptions-root]').length) {
		return;
	}

	product = gtmUtils.getProductFromTileShell($shell) || gtmUtils.getProductFromPdp($shell);

	if (!product) {
		return;
	}

	dedupeKey = String(product.id || '') + ':' + String($shell.attr('data-pid') || '');
	now = Date.now();

	if (dedupeKey === lastProductClickDedupeKey && now - lastProductClickDedupeAt < 500) {
		return;
	}

	lastProductClickDedupeKey = dedupeKey;
	lastProductClickDedupeAt = now;

	gtmUtils.sendEcommerceLinkEvent('view_product_listing', gtmUtils.buildEcommerceProductsPayload(product), {
		site_context: gtmUtils.resolveEcommerceSiteContext($shell),
		product_impression_type: resolveProductImpressionType($shell)
	});
}

/**
 * @param {boolean} onlyNew
 * @returns {Array<Object>}
 */
function collectPlpImpressionProducts(onlyNew) {
	let products = [];

	$('.product-grid .product-tile-shell[data-tealium-product]').each(function collectPlpTile() {
		let $shell = $(this);
		let product;

		if (onlyNew && $shell.attr('data-tealium-impressed') === 'true') {
			return;
		}

		product = gtmUtils.getProductFromTileShell($shell);

		if (!product || !product.id) {
			return;
		}

		products.push(gtmUtils.buildEcommerceProductPayload(product));
		$shell.attr('data-tealium-impressed', 'true');
	});

	return products.filter(Boolean);
}

/**
 * @param {string} method view | link
 * @param {string} context landing | filter | sort | loadmore
 */
function sendPlpProductImpression(method, context) {
	let products = collectPlpImpressionProducts(method === 'link');
	let payload;

	if (!products.length) {
		return;
	}

	payload = {
		event_name: 'product_impression',
		product_impression_type: resolvePlpListingImpressionType(context),
		products: products
	};

	if (method === 'link') {
		gtmUtils.sendUtagLinkEvent(payload);
		return;
	}

	gtmUtils.sendUtagViewEvent(payload);
}

/**
 * Landing PLP/search: product_impression lives in the unique utag.view.
 * @returns {boolean}
 */
function trackPlpProductImpressionViewIfApplicable() {
	if (!isPlpImpressionPage()) {
		return false;
	}

	sendPlpProductImpression('view', 'landing');
	return true;
}

/**
 * Collects not-yet-tracked editorial carousel tiles (any CAROUSEL_IMPRESSION_ROOT container,
 * PLP grid excluded), marking them as impressed.
 * @returns {{ types: string[], products: Object[] }} types: impression type (carousel title) of each product, same index
 */
function collectEditorialCarouselImpressions() {
	let types = [];
	let products = [];

	$('.product-tile-shell[data-tealium-product], .lvz-product-highlight__product[data-tealium-product]').each(
		function collectCarouselTile() {
			let $shell = $(this);
			let type;
			let product;

			if ($shell.closest('.product-grid').length || $shell.attr('data-tealium-impressed') === 'true') {
				return;
			}

			if (!$shell.closest(CAROUSEL_IMPRESSION_ROOT).length || !$shell.is(':visible')) {
				return;
			}

			type = resolveProductImpressionType($shell);
			product = gtmUtils.getProductFromTileShell($shell);

			if (!type || !product || !product.id) {
				return;
			}

			$shell.attr('data-tealium-impressed', 'true');

			types.push(type);
			products.push(gtmUtils.buildEcommerceProductPayload(product));
		}
	);

	return { types: types, products: products };
}

/**
 * Editorial carousels product_impression as utag.link: carousels on pages whose unique view is
 * already an ecommerce one (PDP, cart, PLP, subscription), or revealed after landing (tabs, lazy
 * Einstein). On other pages the landing impression is part of the page view (trackPageViewOnLoad).
 */
function trackEditorialCarouselImpressions() {
	let impression = collectEditorialCarouselImpressions();

	if (!impression.products.length) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'product_impression',
		product_impression_type: impression.types,
		products: impression.products
	});
}

/**
 * @param {string} ctaType
 * @param {jQuery} $context
 */
function trackListingCtaEvent(ctaType, $context) {
	let eventName;
	let product;
	let extra;
	let products;

	ctaType = String(ctaType || '').trim();

	if (!ctaType) {
		return;
	}

	$context = $context && $context.length ? $context : $();
	eventName = gtmUtils.resolveListingCtaEventName(ctaType);
	if (!eventName) {
		return;
	}
	product = gtmUtils.getProductFromTileShell($context) || gtmUtils.getProductFromPdp($context);

	extra = {
		site_context: gtmUtils.resolveEcommerceSiteContext($context),
		cta_type: sanitizeLinkText(ctaType),
		product_impression_type: resolveProductImpressionType($context)
	};
	products = product ? gtmUtils.buildEcommerceProductsPayload(product) : [];

	gtmUtils.sendEcommerceLinkEvent(eventName, products, extra);
}

/**
 * @param {jQuery} $btn
 */
function trackListingCtaClick($btn) {
	trackListingCtaEvent(String($btn.attr('data-tealium-cta') || '').trim(), gtmUtils.resolveListingCtaTileShell($btn));
}

/**
 * Unit price × qty. `quantity` is optional — falls back to `product.quantity`.
 * @param {Object} product
 * @param {number} [quantity]
 * @returns {string}
 */
function formatProductPotentialRevenue(product, quantity) {
	let unit = gtmUtils.getProductUnitPrice(product);
	let qty = parseInt(quantity, 10);

	if (isNaN(qty) || qty < 1) {
		qty = parseInt(product && product.quantity, 10);
	}

	if (isNaN(unit) || isNaN(qty) || qty < 1) {
		return '';
	}

	return String(Math.round(unit * qty * 100) / 100);
}

/**
 * Sum of formatProductPotentialRevenue() across products (each uses its own quantity).
 * @param {Array<Object>} products
 * @returns {string}
 */
function formatProductsPotentialRevenue(products) {
	let total = 0;
	let hasPrice = false;
	let i;
	let part;

	if (!products || !products.length) {
		return '';
	}

	for (i = 0; i < products.length; i++) {
		part = formatProductPotentialRevenue(products[i]);

		if (part !== '') {
			hasPrice = true;
			total += Number(part);
		}
	}

	if (!hasPrice) {
		return '';
	}

	return String(Math.round(total * 100) / 100);
}

/**
 * PDF: one-time_purchase | recurring_purchase | subscription_purchase.
 * Any subscription (coffee-only or machine+coffee) is subscription_purchase.
 * @param {Object} product
 * @param {jQuery} [$el]
 * @returns {string}
 */
function resolveProductPurchaseType(product, $el) {
	let isSub = product && (product.isSubscription || product.LVZ_IsSubscription);
	let isRecurring = !!(product && product.hasSmartOrderRefill);

	if ($el && $el.length && $el.closest('.cart-line-subscription-item').length) {
		isSub = true;
	}

	// PDP / quick view: SOR "recurring" or "limited" option checked (radio value "true").
	if (!isRecurring && $el && $el.length) {
		isRecurring =
			$el
				.closest('.product-detail, .product-wrapper')
				.find('.product-refill-options.lvz-pdp-refill input[type=radio]:checked')
				.val() === 'true';
	}

	if (isSub) {
		return 'subscription_purchase';
	}

	return isRecurring ? 'recurring_purchase' : 'one-time_purchase';
}

/**
 * @param {string} [source]
 * @param {jQuery} [$el]
 * @returns {string}
 */
function resolveCartAddImpressionType(source, $el) {
	let fromSource = sanitizeLinkText(source || '');

	if (fromSource === 'minicart') {
		return 'cart';
	}

	if (fromSource) {
		return fromSource;
	}

	return resolveProductImpressionType($el);
}

/**
 * @param {Object} product
 * @param {number} quantity
 * @param {string} [source]
 * @param {jQuery} [$el]
 */
function trackEcommerceCartAdd(product, quantity, source, $el) {
	if (!product) {
		return;
	}

	try {
		let qty = quantity || product.quantity || 1;
		let impressionType = resolveCartAddImpressionType(source, $el);
		let purchaseType = resolveProductPurchaseType(product, $el);
		let sorPrice;
		let basePrice;

		// Recurring purchase on PDP: the line is added at the SOR price, not the one-time one.
		if (purchaseType === 'recurring_purchase' && $el && $el.length) {
			let $refill = $el.closest('.product-detail, .product-wrapper').find('.product-refill-options');

			sorPrice = parseFloat($refill.attr('data-tealium-sor-price'));
			if (!isNaN(sorPrice)) {
				basePrice = product.price || {};
				// SOR price as sales (price / product_price), full price as list (product_sale_percentage).
				product = $.extend({}, product, {
					price: {
						sales: { value: sorPrice, currency: (basePrice.sales && basePrice.sales.currency) || '' },
						list: basePrice.list || basePrice.sales || null
					}
				});
			}
			// Selected delivery frequency ("2-week") -> product_recurring.
			product = $.extend({}, product, { sorRecurring: String($refill.find('select.deliveryEvery').val() || '') });
		}

		let extra = attachSubscriptionModule({
			source: impressionType,
			product_impression_type: impressionType,
			product_purchase_type: purchaseType,
			product_potential_revenue: formatProductPotentialRevenue(product, qty)
		});

		gtmUtils.sendEcommerceLinkEvent('cart_add', gtmUtils.buildEcommerceProductsPayload(product, qty), extra);
	} catch (e) {
		// never block cart/minicart UI
	}
}
function trackEcommerceCartRemove(product, quantity, subscriptionModuleRaw) {
	if (!product) {
		return;
	}

	try {
		gtmUtils.sendEcommerceLinkEvent(
			'cart_remove',
			gtmUtils.buildEcommerceProductsPayload(product, quantity),
			attachSubscriptionModule(
				{
					product_impression_type: 'cart',
					product_purchase_type: resolveProductPurchaseType(product, null, subscriptionModuleRaw)
				},
				subscriptionModuleRaw || pendingCartRemoveSubscriptionModule || getPageSubscriptionModuleRaw() || {}
			)
		);
	} catch (e) {
		// never block cart/minicart UI
	}
}

/**
 * @returns {boolean} true when a named cart pageview was sent
 */
function trackCartPageViewIfApplicable() {
	if (!gtmUtils.isCartShowPage()) {
		return false;
	}

	if ($('[data-tealium-cart-page="empty"]').length) {
		gtmUtils.sendUtagViewEvent({
			event_name: 'cart_view_empty'
		});
		return true;
	}

	let $cartPage = $('[data-tealium-cart-page="cart"]').first();
	let products = [];

	try {
		products = gtmUtils.parseFormattedProductModules($cartPage.attr('data-tealium-product-modules'));
	} catch (e) {
		products = [];
	}

	gtmUtils.sendUtagViewEvent(
		$.extend(
			{
				event_name: 'cart_view',
				product_impression_type: 'cart',
				products: products
			},
			pickCheckoutTrackingFields(CHECKOUT_CART_VIEW_FIELDS),
			attachSubscriptionModule({})
		)
	);
	return true;
}

/**
 * @param {Object} product
 * @param {jQuery} $wrapper
 */
function sendPdpProductView(product, $wrapper) {
	if (!product) {
		return;
	}

	gtmUtils.sendUtagViewEvent({
		event_name: 'prod_view',
		product_impression_type: 'pdp',
		products: gtmUtils.buildEcommerceProductsPayload(product, gtmUtils.getQuantityFromEcommerceContext($wrapper))
	});
}

/**
 * @returns {boolean} true when product_view was sent
 */
function trackProductViewOnPdpIfApplicable() {
	if (!gtmUtils.isProductShowPage()) {
		return false;
	}

	let $wrapper = gtmUtils.getPdpProductEl();
	let product = gtmUtils.getProductFromPdp($wrapper);

	if (!$wrapper.length || !product) {
		return false;
	}

	sendPdpProductView(product, $wrapper);
	return true;
}

/**
 * @param {jQuery|HTMLElement|Document} [root]
 */
function initEcommerceTracking() {
	if (isEcommerceTrackingInitialized) {
		return;
	}

	isEcommerceTrackingInitialized = true;

	$(document).on('search:showMore', function onPlpShowMoreImpression() {
		pendingPlpLoadMore = true;
	});

	$('body').on('search:updateDom', function onPlpDomUpdateImpression() {
		let context;

		if (!isPlpImpressionPage()) {
			pendingPlpLoadMore = false;
			pendingPlpImpressionContext = '';
			return;
		}

		if (pendingPlpLoadMore) {
			pendingPlpLoadMore = false;
			pendingPlpImpressionContext = '';
			sendPlpProductImpression('link', 'loadmore');
			return;
		}

		context = pendingPlpImpressionContext || 'filter';
		pendingPlpImpressionContext = '';
		sendPlpProductImpression('view', context);
	});

	$(document).on(
		'click',
		'[data-lvz-collection-layer-tab="true"], [data-lvz-recommended-product-carousel-tab="true"]',
		function onCarouselTabImpression() {
			window.setTimeout(trackEditorialCarouselImpressions, 0);
		}
	);

	// Carousels rendered after the landing view (Einstein recommendations load asynchronously).
	$(window).on('load', function onLateCarouselImpressions() {
		window.setTimeout(trackEditorialCarouselImpressions, 1500);
	});

	$(document).on(
		'click',
		'[data-lvz-product-highlight] .lvz-product-highlight__product a[href]',
		function onHighlightProductClick() {
			try {
				trackProductClick($(this).closest('.lvz-product-highlight__product'));
			} catch (e) {
				// never block PDP navigation
			}
		}
	);

	$(document).on(
		'click',
		'[data-tealium-cta="buy_online"], [data-tealium-cta="discover_more"], [data-tealium-cta="contact_us"], [data-tealium-cta="direct_link"]',
		function onListingCtaTracking() {
			let $btn = $(this);

			if ($btn.closest('[data-subscriptions-root]').length) {
				return;
			}

			trackListingCtaClick($btn);
		}
	);

	$(document).on('click', '.sa-buy-online-drawer .swal2-close', function onDirectLinkCloseTracking() {
		let $panel = $('.swal2-container .product-tile__buy-online-modal-panel').first();

		if (!$panel.length) {
			return;
		}

		trackListingCtaEvent('direct_link_close', gtmUtils.resolveListingCtaTileShell($panel));
	});

	$(document).on(
		'click',
		'.product-detail .color-attribute, .product-wrapper [data-attr="color"] button, .product-detail .product__pack-select-option, .product-wrapper .product__pack-select-option',
		function onPdpVariantProductClick() {
			let $btn = $(this);
			let $root;
			let product;

			if ($btn.attr('disabled') || $btn.hasClass('disabled-swatch') || $btn.hasClass('is-unavailable')) {
				return;
			}

			$root = $btn.closest('.product-detail, .product-wrapper');
			product = gtmUtils.getProductFromPdp($root);

			if (!product) {
				return;
			}

			gtmUtils.sendEcommerceLinkEvent('view_product_listing', gtmUtils.buildEcommerceProductsPayload(product), {
				site_context: gtmUtils.resolveEcommerceSiteContext($root),
				product_impression_type: resolveProductImpressionType($root)
			});
		}
	);

	$(document).on('click', '[data-tealium-cta="cta_subscription-promotion"]', function onSubscriptionPdpTracking(e) {
		let $link = $(this);
		let $root = $link.closest('.product-detail, .product-wrapper');

		if (!$root.length) {
			$root = $('.product-detail, .product-wrapper').first();
		}

		let product = gtmUtils.getProductFromPdp($root.length ? $root : undefined);

		gtmUtils.sendEcommerceLinkEvent(
			'cta_subscription-promotion',
			product ? gtmUtils.buildEcommerceProductsPayload(product) : [],
			{
				product_impression_type: resolveProductImpressionType($root),
				product_purchase_type: 'subscription_purchase'
			}
		);

		let anchorEl = $link[0];

		if (!anchorEl || anchorEl.tagName !== 'A' || !anchorEl.href) {
			return;
		}

		e.preventDefault();

		let openInNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
		let targetAttr = String(anchorEl.getAttribute('target') || '')
			.trim()
			.toLowerCase();
		let url = anchorEl.href;

		window.setTimeout(function onSubscriptionPdpNavigate() {
			if (openInNewTab || targetAttr === '_blank') {
				window.open(url, '_blank', 'noopener,noreferrer');
				return;
			}

			if (targetAttr === '_parent' || targetAttr === '_top') {
				window.open(url, targetAttr);
				return;
			}

			window.location.assign(url);
		}, 300);
	});

	$('body').on('product:beforeAddToCart', function onProductBeforeAddToCart(_e, triggerEl) {
		pendingEcommerceAddToCartShell = null;

		if (!triggerEl) {
			return;
		}

		let $shell = $(triggerEl).closest('.product-tile-shell');

		if ($shell.length) {
			pendingEcommerceAddToCartShell = $shell;
		}
	});

	// Free gift modal (SFRA addBonusProductsToCart -> Cart-AddBonusProducts) never triggers product:afterAddToCart:
	// snapshot the chosen gifts on click, send cart_add when the request succeeds.
	let pendingBonusCartAdd = null;

	$(document).on('click', '.choose-bonus-product-dialog .add-bonus-products', function onAddBonusProductsClick() {
		let $dialog = $(this).closest('.choose-bonus-product-dialog');

		pendingBonusCartAdd = $dialog
			.find('.selected-pid')
			.toArray()
			.map(function toBonusTrackingProduct(el) {
				let $selected = $(el);
				let pid = String($selected.data('pid') || '');
				let $item = $dialog.find('.bonus-product-item[data-pid="' + pid + '"]').first();

				return {
					id: pid,
					productName: $item.find('.bonus-gift-card__name').first().text() || $selected.find('.bonus-product-name').text(),
					imageSrc: $item.find('.bonus-gift-card__image').attr('src') || '',
					price: 0,
					available: true,
					isFreeGift: true,
					quantity: parseInt($selected.data('qty'), 10) || 1
				};
			})
			.filter(function hasPid(product) {
				return product.id;
			});
	});

	$(document).ajaxSuccess(function onBonusProductsAdded(_e, _xhr, settings, data) {
		let gifts = pendingBonusCartAdd;

		if (!gifts || !settings || String(settings.url || '').indexOf('AddBonusProducts') === -1) {
			return;
		}

		pendingBonusCartAdd = null;

		if (!gifts.length || (data && data.error)) {
			return;
		}

		gtmUtils.sendEcommerceLinkEvent(
			'cart_add',
			gifts.reduce(function collectGiftPayload(list, gift) {
				return list.concat(gtmUtils.buildEcommerceProductsPayload(gift, gift.quantity));
			}, []),
			attachSubscriptionModule({
				source: 'free_gift',
				product_impression_type: 'free_gift',
				product_purchase_type: 'one-time_purchase',
				product_potential_revenue: '0'
			})
		);
	});

	$('body').on('product:afterAddToCart', function onProductAfterAddToCart(_e, data, $tileShell) {
		if ($.isArray(data)) {
			$tileShell = data.length > 1 ? data[1] : $tileShell;
			data = data[0];
		}

		// A free gift choice (newBonusDiscountLineItem) only opens the bonus modal: the product is already in the cart.
		if (!data || data.error) {
			pendingEcommerceAddToCartShell = null;
			return;
		}

		let $shell = $tileShell && $tileShell.length ? $tileShell : pendingEcommerceAddToCartShell || $();

		pendingEcommerceAddToCartShell = null;

		let product = null;
		let $origin = $shell.length ? $shell : $('.product-detail');

		if ($shell.length) {
			product = gtmUtils.getProductFromTileShell($shell);
		}

		if (!product) {
			product = gtmUtils.getProductFromPdp();
		}

		if (!product) {
			return;
		}

		trackEcommerceCartAdd(
			product,
			gtmUtils.getQuantityFromEcommerceContext($origin),
			resolveProductImpressionType($origin),
			$origin
		);
	});

	let isQuantityOnlyAttributeSelect = false;
	let quantityOnlyAttributeSelectResetTimer = null;

	$(document).on('change', 'select.product-qty, input.product-qty', function onPdpQuantitySelectChange() {
		isQuantityOnlyAttributeSelect = true;
		clearTimeout(quantityOnlyAttributeSelectResetTimer);
		quantityOnlyAttributeSelectResetTimer = setTimeout(function resetQuantityOnlyFlag() {
			isQuantityOnlyAttributeSelect = false;
		}, 5000);
	});

	$('body').on('product:afterAttributeSelect', function onPdpVariationTracking(_e, response) {
		let skipProductView = isQuantityOnlyAttributeSelect;

		isQuantityOnlyAttributeSelect = false;
		clearTimeout(quantityOnlyAttributeSelectResetTimer);

		if (!response || !response.data || !response.data.product) {
			return;
		}

		gtmUtils.syncPdpTealiumProduct(response.data.product);

		if (!gtmUtils.isProductShowPage() || skipProductView) {
			return;
		}

		let $wrapper = gtmUtils.getPdpProductEl();
		sendPdpProductView(gtmUtils.getProductFromPdp($wrapper), $wrapper);
	});

	function getCartLineQtyField($lineItem) {
		return $lineItem.find('.quantity-form > .quantity, select.quantity, select.product-qty').first();
	}

	function parseCartQtyValue($field) {
		let qty = parseInt($field.val(), 10);

		if (!isNaN(qty)) {
			return qty;
		}

		qty = parseInt($field.attr('data-pre-select-qty'), 10);

		if (!isNaN(qty)) {
			return qty;
		}

		qty = parseInt($field.data('pre-select-qty'), 10);
		return isNaN(qty) ? 0 : qty;
	}

	function resolveCartLineTrackingProduct($lineItem, quantity) {
		let product = gtmUtils.getProductFromCartLineItem($lineItem);
		let $field;

		if (product) {
			product.quantity = quantity;
			return product;
		}

		$field = getCartLineQtyField($lineItem);

		if (!$field.length || !$field.data('pid')) {
			return null;
		}

		return {
			id: $field.data('pid'),
			productName: $lineItem.find('.cart-line-item__name').first().text() || '',
			quantity: quantity
		};
	}

	function queueCartLineQtyTracking($lineItem, delta) {
		let product;

		try {
			if (!$lineItem || !$lineItem.length || !delta) {
				return;
			}

			product = resolveCartLineTrackingProduct($lineItem, Math.abs(delta));

			if (!product) {
				return;
			}

			if (delta > 0) {
				pendingCartAdd = {
					product: product,
					quantity: delta,
					source: 'cart',
					$el: $lineItem
				};
				clearPendingCartRemove();
				return;
			}

			pendingCartAdd = null;
			pendingCartRemoveProduct = product;
			pendingCartRemoveSubscriptionModule = getLineItemSubscriptionModuleRaw($lineItem);
		} catch (e) {
			pendingCartAdd = null;
			clearPendingCartRemove();
		}
	}

	function flushPendingCartQtyTracking() {
		try {
			if (pendingCartAdd && pendingCartAdd.product) {
				trackEcommerceCartAdd(pendingCartAdd.product, pendingCartAdd.quantity, pendingCartAdd.source, pendingCartAdd.$el);
				pendingCartAdd = null;
			}

			if (pendingCartRemoveProduct) {
				trackEcommerceCartRemove(
					pendingCartRemoveProduct,
					pendingCartRemoveProduct.quantity || 1,
					pendingCartRemoveSubscriptionModule
				);
				clearPendingCartRemove();
			}
		} catch (e) {
			pendingCartAdd = null;
			clearPendingCartRemove();
		}
	}

	document.addEventListener(
		'click',
		function onCartQtyStepperCapture(event) {
			let btn;
			let $btn;
			let $lineItem;
			let $field;
			let isMinus;

			try {
				btn =
					event.target && event.target.closest
						? event.target.closest('.cart-line-item .product-qty-minus, .cart-line-item .product-qty-plus')
						: null;

				if (!btn || btn.disabled) {
					return;
				}

				$btn = $(btn);
				$lineItem = $btn.closest('.cart-line-item');
				$field = getCartLineQtyField($lineItem);
				isMinus = btn.classList.contains('product-qty-minus');

				if (!$lineItem.length || !$field.length) {
					return;
				}

				if (isMinus && parseCartQtyValue($field) <= 1) {
					return;
				}

				if (!isMinus && $field.is('select') && $field.prop('selectedIndex') >= $field.find('option').length - 1) {
					return;
				}

				queueCartLineQtyTracking($lineItem, isMinus ? -1 : 1);
			} catch (e) {
				// never block qty stepper
			}
		},
		true
	);

	$('body').on('change', '.quantity-form > .quantity', function onCartQtyChangeTracking() {
		try {
			let $field = $(this);
			let $lineItem = $field.closest('.cart-line-item');
			let preSelectQty;
			let quantity;

			if (!$lineItem.length) {
				return;
			}

			preSelectQty = parseInt($field.attr('data-pre-select-qty'), 10);

			if (isNaN(preSelectQty)) {
				preSelectQty = parseInt($field.data('pre-select-qty'), 10);
			}

			quantity = parseCartQtyValue($field);

			if (isNaN(preSelectQty) || isNaN(quantity) || quantity < 1 || quantity === preSelectQty) {
				return;
			}

			queueCartLineQtyTracking($lineItem, quantity - preSelectQty);
		} catch (e) {
			// never block qty update
		}
	});

	$(document).on('ajaxSuccess', function onCartQtyAjaxSuccess(_event, xhr, settings) {
		if (!settings || !settings.url || settings.url.indexOf('UpdateQuantity') === -1) {
			return;
		}

		flushPendingCartQtyTracking();
	});

	$(document).on('ajaxComplete', function onCartQtyAjaxComplete(_event, xhr, settings) {
		if (!settings || !settings.url || settings.url.indexOf('UpdateQuantity') === -1) {
			return;
		}

		if (xhr.status >= 400) {
			pendingCartAdd = null;
			clearPendingCartRemove();
		}
	});

	$('body').on(
		'click',
		'.remove-product, .cart-line-item__remove, .cart-delete-confirmation-btn, .minicart .cart-line-item__remove',
		function onCartRemoveIntent() {
			let $btn = $(this);
			let $lineItem = $btn.closest('.cart-line-item');

			pendingCartAdd = null;

			if (!$lineItem.length && $btn.data('uuid')) {
				$lineItem = $('.uuid-' + $btn.data('uuid') + ', .cart-line-item.uuid-' + $btn.data('uuid')).first();
			}

			pendingCartRemoveProduct = gtmUtils.getProductFromCartLineItem($lineItem);

			if (!pendingCartRemoveProduct && $btn.data('pid')) {
				pendingCartRemoveProduct = {
					id: $btn.data('pid'),
					productName: $btn.data('name') || '',
					quantity: 1,
					isSubscription: $lineItem.hasClass('cart-line-subscription-item')
				};
			}

			pendingCartRemoveSubscriptionModule = getLineItemSubscriptionModuleRaw($lineItem);
		}
	);

	$('body').on('cart:update', function onCartUpdateTracking(_e, data) {
		try {
			flushPendingCartQtyTracking();

			if (data && data.basket && data.basket.items && data.basket.items.length === 0 && gtmUtils.isCartShowPage()) {
				gtmUtils.sendUtagViewEvent({
					event_name: 'cart_view_empty'
				});
			}
		} catch (e) {
			pendingCartAdd = null;
			clearPendingCartRemove();
		}
	});
}

/** Video tracking */

let isVideoTrackingInitialized = false;

function sendVideoEvent(eventName, detail, includeTime) {
	let payload = {
		event_name: gtmUtils.sanitizeLinkText(eventName),
		video_title: gtmUtils.trackingEditorialAttrSerialized((detail && detail.title) || '')
	};

	if (includeTime && detail && detail.videoTime) {
		payload.video_time = detail.videoTime;
	}

	gtmUtils.sendUtagLinkEvent(payload);
}

function initVideoTracking() {
	if (isVideoTrackingInitialized) {
		return;
	}

	isVideoTrackingInitialized = true;
}

/** Authentication tracking */

let lastAuthEventKey = '';
let lastAuthEventAt = 0;
let REGISTRATION_PRIVACY_STORAGE_KEY = 'auth_registration_privacy_snapshot';
let REGISTRATION_OK_STORAGE_PREFIX = 'auth_registrazione_ok_sent:';

/**
 * @param {string} key
 * @returns {boolean}
 */
function isConsentMetaKey(key) {
	return /channel|source|method|type|url|page|origin|platform|device|place|timestamp|regsource|factory|loginchannel/i.test(
		String(key)
	);
}

/**
 * Gigya Screen Set stores registration consents in eventObj.data (e.g. privacyProfilazione).
 * privacyProfilingPlace / loginChannel are metadata, not yes/no consent values.
 *
 * @param {Object} formData
 * @returns {Object}
 */
function mapFormDataPrivacy(formData) {
	let mapped = {
		privacy_profilazione: '',
		privacy_invio_promo: '',
		loyalty_status: ''
	};

	if (!formData || typeof formData !== 'object') {
		return mapped;
	}

	mapped.privacy_profilazione = normalizeConsentValue(
		gtmUtils.pickExactFormValue(formData, [
			'privacyProfilazione',
			'privacy_profiling',
			'privacyProfiling',
			'privacyProfilingFlag',
			'flgProfilazione',
			'acceptPrivacyProfilazione'
		])
	);
	mapped.privacy_invio_promo = normalizeConsentValue(
		gtmUtils.pickExactFormValue(formData, [
			'privacyInvioPromo',
			'privacy_invio_promo',
			'privacyPromo',
			'privacyPromotional',
			'privacyMarketing',
			'flgPromo',
			'acceptPrivacyPromo',
			'privacyRecallConsent',
			'privacyRecall'
		])
	);
	mapped.loyalty_status = normalizeLoyaltyStatus(
		gtmUtils.pickExactFormValue(formData, [
			'isLoyalty',
			'loyaltyFlag',
			'loyalty',
			'loyaltyProgram',
			'flgLoyalty',
			'acceptLoyalty'
		])
	);

	Object.keys(formData).forEach(function (key) {
		if (isConsentMetaKey(key)) {
			return;
		}

		let lowerKey = String(key).toLowerCase();
		let value = formData[key];

		if (!mapped.privacy_profilazione && /profil/i.test(lowerKey)) {
			mapped.privacy_profilazione = normalizeConsentValue(value);
		}

		if (!mapped.privacy_invio_promo && /(promo|invio|marketing|commercial|newsletter|recall)/i.test(lowerKey)) {
			mapped.privacy_invio_promo = normalizeConsentValue(value);
		}

		if (!mapped.loyalty_status && /loyalty|club|reward/i.test(lowerKey)) {
			mapped.loyalty_status = normalizeLoyaltyStatus(value);
		}
	});

	return mapped;
}

/**
 * @param {Object} eventObj
 * @returns {Object}
 */
function getGigyaFormData(eventObj) {
	eventObj = eventObj || {};

	return eventObj.data || eventObj.formData || (eventObj.response && eventObj.response.data) || {};
}

/**
 * @param {string} normalizedKey
 * @param {Array<string>} patterns
 * @returns {boolean}
 */
function keyMatchesAnyPattern(normalizedKey, patterns) {
	return patterns.some(function (pattern) {
		return normalizedKey.indexOf(pattern) !== -1;
	});
}

function findNestedConsentValue(source, patterns) {
	if (!source || typeof source !== 'object') {
		return undefined;
	}

	let keys = Object.keys(source);

	for (let i = 0; i < keys.length; i++) {
		let key = keys[i];

		if (isConsentMetaKey(key)) {
			continue;
		}

		let normalizedKey = String(key).toLowerCase();
		let matched = keyMatchesAnyPattern(normalizedKey, patterns);

		if (matched && gtmUtils.isConsentLikeValue(source[key])) {
			return source[key];
		}

		let nested = source[key];
		if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
			let nestedValue = findNestedConsentValue(nested, patterns);
			if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
				return nestedValue;
			}
		}
	}

	return undefined;
}

/**
 * @returns {Object}
 */
function resolvePrivacyFieldsFromDom() {
	let fields = {
		privacy_profilazione: '',
		privacy_invio_promo: '',
		loyalty_status: ''
	};

	$('#gigya-handle')
		.find('input[type="checkbox"], input[type="radio"]')
		.each(function () {
			let $input = $(this);

			if ($input.attr('type') === 'radio' && !$input.prop('checked')) {
				return;
			}

			let descriptor = [
				$input.attr('name'),
				$input.attr('id'),
				$input.attr('data-schemaname'),
				$input.attr('data-name'),
				$input.closest('.gigya-composite-control').text(),
				$input.closest('.gigya-input-checkbox').text()
			]
				.join(' ')
				.toLowerCase();
			let consentValue = $input.attr('type') === 'radio' ? $input.val() : $input.prop('checked');

			if (/profil|profiling|personalization|personalisation/.test(descriptor) && !fields.privacy_profilazione) {
				fields.privacy_profilazione = normalizeConsentValue(consentValue);
			}

			if (/promo|marketing|newsletter|invio|commercial|offer|recall/.test(descriptor) && !fields.privacy_invio_promo) {
				fields.privacy_invio_promo = normalizeConsentValue(consentValue);
			}

			if (/loyalty|club|reward|member/.test(descriptor) && !fields.loyalty_status) {
				fields.loyalty_status = normalizeLoyaltyStatus(consentValue);
			}
		});

	return fields;
}

/**
 * @param {string} [eventType]
 * @returns {boolean}
 */
function isLoginEventType(eventType) {
	if (!eventType) {
		return true;
	}

	let normalized = String(eventType).toLowerCase();

	if (normalized.indexOf('register') !== -1) {
		return false;
	}

	if (normalized.indexOf('validate') !== -1) {
		return false;
	}

	return normalized.indexOf('login') !== -1;
}

/**
 * @param {Object} eventObj
 * @returns {string}
 */
function getScreenId(eventObj) {
	eventObj = eventObj || {};

	return String(eventObj.currentScreen || eventObj.screen || '').toLowerCase();
}

/**
 * Gigya onAfterSubmit often reports the destination screen in currentScreen.
 * @param {Object} eventObj
 * @param {Object} [options]
 * @param {string} [options.activeScreen]
 * @returns {string}
 */
function getSubmittedScreen(eventObj, options) {
	options = options || {};
	eventObj = eventObj || {};

	return String(
		options.activeScreen ||
			eventObj.prevScreen ||
			eventObj.previousScreen ||
			eventObj.sourceScreen ||
			eventObj.screen ||
			eventObj.currentScreen ||
			''
	).toLowerCase();
}

/**
 * @param {Object} eventObj
 * @returns {string}
 */
function getDestinationScreen(eventObj) {
	eventObj = eventObj || {};

	return String(eventObj.currentScreen || eventObj.nextScreen || eventObj.screen || '').toLowerCase();
}

let GIGYA_PENDING_REGISTRATION_CODES = [206001, 206002, 206003];

/**
 * @param {number} [errorCode]
 * @param {string} [errorMessage]
 * @returns {boolean}
 */
function isGigyaPendingRegistrationError(errorCode, errorMessage) {
	if (typeof errorCode === 'number' && GIGYA_PENDING_REGISTRATION_CODES.indexOf(errorCode) !== -1) {
		return true;
	}

	return /pending/i.test(String(errorMessage || ''));
}

/**
 * @param {Object} eventObj
 * @returns {boolean}
 */
function wasSubmitSuccessful(eventObj) {
	eventObj = eventObj || {};
	let response = eventObj.response || {};
	let errorCode = typeof response.errorCode === 'number' ? response.errorCode : eventObj.errorCode;
	let errorMessage = eventObj.errorMessage || response.errorMessage;

	if (isGigyaPendingRegistrationError(errorCode, errorMessage)) {
		return true;
	}

	if (typeof eventObj.errorCode === 'number' && eventObj.errorCode !== 0) {
		return false;
	}

	if (typeof response.errorCode === 'number' && response.errorCode !== 0) {
		return false;
	}

	if (eventObj.errorMessage || response.errorMessage) {
		return false;
	}

	let status = String(response.status || response.statusCode || '').toLowerCase();

	if (status === 'fail' || status === 'error') {
		return false;
	}

	return true;
}

/**
 * @param {Object} eventObj
 * @returns {string}
 */
function getOperationName(eventObj) {
	eventObj = eventObj || {};
	let response = eventObj.response || {};

	return String(response.operation || eventObj.eventName || '').toLowerCase();
}

/**
 * @param {string} screenId
 * @returns {boolean}
 */
function isRegisterScreen(screenId) {
	if (!screenId || screenId.indexOf('verification') !== -1) {
		return false;
	}

	return screenId.indexOf('register') !== -1;
}

/**
 * @param {string} screenId
 * @returns {boolean}
 */
function isVerificationSentScreen(screenId) {
	return screenId === 'gigya-verification-sent-screen';
}

/**
 * @param {string} screenId
 * @returns {boolean}
 */
function isPasswordResetScreen(screenId) {
	if (!screenId) {
		return false;
	}

	return screenId.indexOf('reset-password') !== -1 || screenId.indexOf('forgot-password') !== -1;
}

/**
 * @param {string} screenId
 * @returns {boolean}
 */
function isForgotPasswordEmailSentScreen(screenId) {
	return screenId === 'gigya-forgot-password-success-screen';
}

/**
 * @param {string} screenId
 * @returns {boolean}
 */
function isResetPasswordCompleteScreen(screenId) {
	return screenId === 'gigya-reset-password-success-screen';
}

/**
 * Only the account type (b2c/b2b) goes into this key, nothing tied to the specific user.
 * We used to also key on the profile's UID/email, but that data isn't always there: the
 * submit event has it, but the event fired when the verification-sent screen loads often
 * doesn't. So the same registration could end up checking two different keys depending on
 * which of the two moments ran first, and the dedupe check would miss the earlier one.
 * @param {string} accountType
 * @returns {string}
 */
function getRegistrationOkStorageKey(accountType) {
	return REGISTRATION_OK_STORAGE_PREFIX + accountType;
}

function wasRegistrationOkSent(accountType) {
	try {
		return window.sessionStorage.getItem(getRegistrationOkStorageKey(accountType)) === '1';
	} catch (e) {
		return false;
	}
}

function markRegistrationOkSent(accountType) {
	try {
		window.sessionStorage.setItem(getRegistrationOkStorageKey(accountType), '1');
	} catch (e) {
		// ignore storage failures
	}
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {boolean}
 */
function isRegisterOperation(eventObj) {
	return getOperationName(eventObj).indexOf('register') !== -1;
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {boolean}
 */
function shouldTrackRegistrationOk(eventObj, options) {
	if (isRegisterOperation(eventObj)) {
		return true;
	}

	if (isVerificationSentScreen(getDestinationScreen(eventObj)) || isVerificationSentScreen(getScreenId(eventObj))) {
		return true;
	}

	return isRegisterScreen(getSubmittedScreen(eventObj, options)) && wasSubmitSuccessful(eventObj);
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {'b2c'|'b2b'}
 */
function resolveRegistrationAccountType(eventObj, options) {
	options = options || {};

	if (options.registrationAccountType === 'b2b' || options.registrationAccountType === 'b2c') {
		return options.registrationAccountType;
	}

	let screenId =
		getSubmittedScreen(eventObj, options) ||
		getDestinationScreen(eventObj) ||
		String(options.activeScreen || '').toLowerCase();
	let flowType = String(options.registrationFlowType || '').toLowerCase();
	let profile = (eventObj.response && eventObj.response.profile) || eventObj.profile || {};
	let profileData = profile.data || {};

	if (
		screenId.indexOf('register-company') !== -1 ||
		flowType === 'register-company' ||
		flowType === 'org' ||
		profileData.companyName ||
		profileData.vatNumber ||
		profileData.organizationName
	) {
		return 'b2b';
	}

	return 'b2c';
}

/**
 * Get the login provider type from the event object.
 *
 * @param {Object} eventObj
 * @returns {string}
 */
let SOCIAL_PROVIDER_STORAGE_KEY = 'lvzTealiumSocialProvider';

/**
 * Social registration: the screenset onAfterSubmit (pending registration / complete-registration screen)
 * carries no provider, so remember the social button the user clicked. A standard form submit (it has a
 * password field) clears it.
 */
function initSocialProviderCapture() {
	document.addEventListener(
		'click',
		function onGigyaAuthClick(e) {
			let target = e.target && e.target.closest ? e.target : null;
			let providerEl = target && target.closest('[data-gigya-provider]');
			let submitEl = target && target.closest('.gigya-input-submit, input[type="submit"]');

			try {
				if (providerEl) {
					window.sessionStorage.setItem(SOCIAL_PROVIDER_STORAGE_KEY, providerEl.getAttribute('data-gigya-provider') || '');
				} else if (submitEl && submitEl.form && submitEl.form.querySelector('input[type="password"]')) {
					window.sessionStorage.removeItem(SOCIAL_PROVIDER_STORAGE_KEY);
				}
			} catch (err) {
				// sessionStorage blocked: registration_type falls back to standard
			}
		},
		true
	);
}

function getStoredSocialProvider() {
	try {
		return window.sessionStorage.getItem(SOCIAL_PROVIDER_STORAGE_KEY) || '';
	} catch (e) {
		return '';
	}
}

function resolveLoginProviderType(eventObj) {
	let response = (eventObj && eventObj.response) || {};
	let provider = String(
		(eventObj && (eventObj.provider || eventObj.loginProvider)) || response.loginProvider || getStoredSocialProvider()
	).toLowerCase();

	// Same mapping as tealiumData.persistLoginProvider: e-mail/password ("site" or no provider) -> standard.
	if (!provider || provider === 'site') {
		return 'standard';
	}

	return provider.startsWith('google') ? 'google' : provider;
}

/**
 * Aurora doc example: private:standard
 * @param {Object} [eventObj]
 * @param {Object} [options]
 * @param {Object} [options.profile]
 * @param {boolean} [options.isBusiness]
 * @returns {string}
 */
function resolveRegistrationType(eventObj, options) {
	options = options || {};
	eventObj = eventObj || {};

	let utagData = window.utag_data || {};

	if (utagData.registration_type) {
		return gtmUtils.sanitizeLinkText(utagData.registration_type);
	}

	let profile = options.profile || (eventObj.response && eventObj.response.profile) || eventObj.profile || eventObj.user || {};

	let fromProfile =
		profile.registrationType ||
		profile.registration_type ||
		(profile.data && (profile.data.registrationType || profile.data.registration_type));

	if (fromProfile) {
		return gtmUtils.sanitizeLinkText(fromProfile);
	}

	let isBusiness = options.isBusiness;

	if (isBusiness === undefined) {
		isBusiness = resolveRegistrationAccountType(eventObj, options) === 'b2b';
	}

	let loginType = resolveLoginProviderType(eventObj);

	return isBusiness ? 'company:' + loginType : 'private:' + loginType;
}

/**
 * privacy_profilazione / privacy_invio_promo vocabulary per the tracking spec: "true"/"false".
 * @param {*} value
 * @returns {string}
 */
function normalizeConsentValue(value) {
	if (value === true || value === 1 || value === '1' || value === 'true') {
		return 'true';
	}

	if (value === false || value === 0 || value === '0' || value === 'false') {
		return 'false';
	}

	if (value === undefined || value === null || value === '') {
		return '';
	}

	return gtmUtils.sanitizeLinkText(value);
}

/**
 * loyalty_status vocabulary per the tracking spec: "attivo"/"nonattivo" (not true/false or yes/no).
 * @param {*} value
 * @returns {string}
 */
function normalizeLoyaltyStatus(value) {
	if (value === true || value === 1 || value === '1' || value === 'true' || value === 'yes') {
		return 'attivo';
	}

	if (value === false || value === 0 || value === '0' || value === 'false' || value === 'no') {
		return 'nonattivo';
	}

	if (value === undefined || value === null || value === '') {
		return '';
	}

	return gtmUtils.sanitizeLinkText(value);
}

/**
 * @param {Object} payload
 */
// localStorage, not sessionStorage: the activation link in the e-mail opens a new tab.
function persistRegistrationPrivacySnapshot(payload) {
	try {
		window.localStorage.setItem(
			REGISTRATION_PRIVACY_STORAGE_KEY,
			JSON.stringify({
				privacy_profilazione: payload.privacy_profilazione || '',
				privacy_invio_promo: payload.privacy_invio_promo || '',
				loyalty_status: payload.loyalty_status || '',
				type: payload.type || '',
				registration_type: payload.registration_type || ''
			})
		);
	} catch (e) {
		// ignore storage failures
	}
}

/**
 * @returns {Object|null}
 */
function loadRegistrationPrivacySnapshot() {
	try {
		return JSON.parse(window.localStorage.getItem(REGISTRATION_PRIVACY_STORAGE_KEY) || 'null');
	} catch (e) {
		return null;
	}
}

/**
 * @param {Object} payload
 * @param {Object} source
 */
function mergeRegistrationPrivacyFields(payload, source) {
	if (!source) {
		return;
	}

	['privacy_profilazione', 'privacy_invio_promo', 'loyalty_status'].forEach(function (key) {
		if (!payload[key] && source[key]) {
			payload[key] = source[key];
		}
	});
}

/**
 * TODO: Confirm Gigya Screen Set consent keys per locale if mapFormDataPrivacy misses fields.
 * US formData example: privacyProfilingPlace (metadata), loyalty* (consent). Not profile.data.
 *
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {Object}
 */
function resolvePrivacyFields(eventObj, options) {
	eventObj = eventObj || {};
	options = options || {};
	let profile = (eventObj.response && eventObj.response.profile) || eventObj.profile || {};
	let data = profile.data || {};
	let preferences = profile.preferences || {};
	let subscriptions = profile.subscriptions || eventObj.subscriptions || {};
	let formData = getGigyaFormData(eventObj);
	let formMapped = mapFormDataPrivacy(formData);
	let sources = [data, preferences, subscriptions, formData];
	let domFields = options.registrationPrivacySnapshot || resolvePrivacyFieldsFromDom();

	function pickValue(patterns) {
		for (let i = 0; i < sources.length; i++) {
			let value = findNestedConsentValue(sources[i], patterns);
			if (value !== undefined && value !== null && value !== '') {
				return value;
			}
		}
		return undefined;
	}

	let resolved = {
		privacy_profilazione:
			formMapped.privacy_profilazione ||
			normalizeConsentValue(pickValue(['privacy_profilazione', 'privacyprofilazione', 'profilazione'])) ||
			domFields.privacy_profilazione ||
			'',
		privacy_invio_promo:
			formMapped.privacy_invio_promo ||
			normalizeConsentValue(
				pickValue(['privacy_invio_promo', 'privacyinvio', 'invio_promo', 'privacyrecall', 'recallconsent'])
			) ||
			domFields.privacy_invio_promo ||
			'',
		loyalty_status:
			formMapped.loyalty_status ||
			normalizeLoyaltyStatus(pickValue(['loyalty_status', 'loyaltyflag', 'loyalty'])) ||
			domFields.loyalty_status ||
			'nonattivo'
	};

	return resolved;
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {Object}
 */
function buildRegistrationDetailPayload(eventObj, options) {
	let accountType = resolveRegistrationAccountType(eventObj, options);
	let payload = {
		type: accountType,
		registration_type: resolveRegistrationType(eventObj, {
			isBusiness: accountType === 'b2b'
		})
	};

	$.extend(payload, resolvePrivacyFields(eventObj, options));
	mergeRegistrationPrivacyFields(payload, loadRegistrationPrivacySnapshot());

	return payload;
}

/**
 * @param {string} dedupeKey
 * @param {Object} payload
 */
function sendAuthEventOnce(dedupeKey, payload) {
	let now = Date.now();

	if (lastAuthEventKey === dedupeKey && now - lastAuthEventAt < 400) {
		return;
	}

	lastAuthEventKey = dedupeKey;
	lastAuthEventAt = now;
	gtmUtils.sendUtagLinkEvent(payload);
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @returns {string}
 */
function resolveDigitalIdFromAuth(eventObj, options) {
	let data = (eventObj && (eventObj.data || (eventObj.response && eventObj.response.data))) || {};
	let fromOptions = options && options.digitalId;
	let value = fromOptions || data.digitalId || data.digitalID || '';

	return value ? String(value) : '';
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @param {string} [options.eventType]
 * @param {Object} [options.profile]
 * @param {boolean} [options.isBusiness]
 * @param {string} [options.digitalId]
 */
function trackLoginOk(eventObj, options) {
	options = options || {};

	if (!isLoginEventType(options.eventType)) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'login_ok',
		login_status: 'loggedin',
		digital_id: resolveDigitalIdFromAuth(eventObj, options),
		customer_email: options.customer_email || '',
		customer_type: options.customer_type || '',
		customer_gender: options.customer_gender || '',
		customer_birth: options.customer_birth || '',
		privacy_profilazione: options.privacy_profilazione === 'true' ? 'true' : 'false',
		privacy_invio_promo: options.privacy_invio_promo === 'true' ? 'true' : 'false',
		privacy_informativa: options.privacy_informativa === 'false' ? 'false' : 'true',
		customer_sub_type: options.customer_sub_type || '',
		registered_machine_code: options.registered_machine_code || '',
		registered_machine_name: options.registered_machine_name || '',
		// At login the Gigya screen is never "register-company": the account kind comes from the SFCC
		// customer_type (same rule as utag_data: enterprise -> company).
		registration_type: resolveRegistrationType(
			eventObj,
			$.extend({}, options, {
				isBusiness: options.customer_type ? options.customer_type === 'enterprise' : options.isBusiness
			})
		)
	});
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 */
function trackRegistrationStep1(eventObj, options) {
	try {
		options = options || {};
		options.registrationPrivacySnapshot = resolvePrivacyFieldsFromDom();

		let accountType = resolveRegistrationAccountType(eventObj, options);
		let registrationType = resolveRegistrationType(eventObj, {
			isBusiness: accountType === 'b2b'
		});

		persistRegistrationPrivacySnapshot(
			$.extend({ type: accountType, registration_type: registrationType }, resolvePrivacyFields(eventObj, options))
		);

		sendAuthEventOnce('registrazione_step1:' + accountType, {
			event_name: 'registrazione_step1',
			type: accountType,
			registration_type: registrationType
		});
	} catch (e) {}
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 */
function trackRegistrationOk(eventObj, options) {
	options = options || {};

	if (isRegisterScreen(getSubmittedScreen(eventObj, options) || String(options.activeScreen || '').toLowerCase())) {
		options.registrationPrivacySnapshot = resolvePrivacyFieldsFromDom();
	}

	let payload = buildRegistrationDetailPayload(eventObj, options);
	payload.event_name = 'registrazione_ok';

	persistRegistrationPrivacySnapshot(payload);
	markRegistrationOkSent(payload.type);
	sendAuthEventOnce('registrazione_ok:' + payload.type, payload);
}

/**
 * Dictionary "registration pages" | ok: the e-mail confirmation is a Gigya screen (no SFCC page),
 * so it gets its own utag.view like any SPA content (PDF "Pageview").
 */
function trackRegistrationOkPageView() {
	if (trackRegistrationOkPageView.sent) {
		return;
	}
	trackRegistrationOkPageView.sent = true;

	gtmUtils.sendUtagViewEvent(
		$.extend({ page_type: 'account', page_section: 'registration' }, buildStepPageFields('ok', 'registration'))
	);
}

function ensureRegistrationOk(eventObj, options) {
	let accountType = resolveRegistrationAccountType(eventObj, options);

	if (wasRegistrationOkSent(accountType)) {
		return;
	}

	trackRegistrationOk(eventObj, options);
}

/**
 * Account Activated page (GigyaHandle-mailredirect / GigyaHandle-mailValidation, same
 * handler): a plain page load with no Gigya event data, so the payload comes entirely
 * from the snapshot captured during registrazione_step1 / registrazione_ok.
 */
function trackRegistrationComplete() {
	let snapshot = loadRegistrationPrivacySnapshot() || {};
	let accountType = snapshot.type || 'b2c';
	let payload = {
		event_name: 'registrazione_completata',
		type: accountType,
		// Snapshots saved before the provider fix hold "private:" / "company:" (standard login had no provider).
		// No snapshot (other browser): the logged-in session value (Gigya login provider) before the default.
		registration_type: String(
			snapshot.registration_type ||
				(window.utag_data && window.utag_data.registration_type) ||
				(accountType === 'b2b' ? 'company:standard' : 'private:standard')
		).replace(/:$/, ':standard'),
		privacy_profilazione: snapshot.privacy_profilazione || '',
		privacy_invio_promo: snapshot.privacy_invio_promo || '',
		loyalty_status: snapshot.loyalty_status || ''
	};

	let dedupeStorageKey = 'auth_registrazione_completata:' + accountType;

	try {
		if (window.sessionStorage.getItem(dedupeStorageKey)) {
			return;
		}
		window.sessionStorage.setItem(dedupeStorageKey, '1');
	} catch (e) {
		// ignore storage failures
	}

	sendAuthEventOnce('registrazione_completata:' + accountType, payload);
}

/**
 * @returns {boolean}
 */
function isRegistrationCompletePage() {
	// Account-Show?registrationComplete=1: activation link opened while already logged in (GigyaHandle-mailredirect).
	return (
		document.querySelector('[data-tealium-registration-complete="true"]') !== null ||
		/[?&]registrationComplete=1(&|$)/.test(window.location.search)
	);
}

function trackRegistrationCompletePageViewIfApplicable() {
	if (!isRegistrationCompletePage()) {
		return;
	}

	trackRegistrationComplete();
}

function trackResetPasswordSendEmail() {
	sendAuthEventOnce('reset_password_send_email', {
		event_name: 'reset_password_send_email'
	});
}

function trackResetPasswordComplete() {
	sendAuthEventOnce('reset_password_complete', {
		event_name: 'reset_password_complete'
	});
}

/**
 * @param {Object} eventObj
 * @param {Object} [options]
 * @param {string} [options.registrationFlowType]
 */
function isRegistrationFlow(eventObj, options) {
	let submittedScreen = getSubmittedScreen(eventObj, options);
	let destinationScreen = getDestinationScreen(eventObj);
	let flowType = String((options && options.registrationFlowType) || '').toLowerCase();

	if (isRegisterScreen(submittedScreen) || isVerificationSentScreen(destinationScreen)) {
		return true;
	}

	return flowType === 'register' || flowType === 'register-company' || flowType === 'org';
}

function handleRegistrationAfterSubmit(eventObj, options) {
	if (!wasSubmitSuccessful(eventObj)) {
		return;
	}

	if (!isRegistrationFlow(eventObj, options)) {
		return;
	}

	trackRegistrationStep1(eventObj, options);

	if (shouldTrackRegistrationOk(eventObj, options)) {
		ensureRegistrationOk(eventObj, options);
	}
}

/**
 * Social sign-up: Gigya logs the new user in straight away (onLogin newUser) with no form submit and no
 * e-mail confirmation screen, so the screenset handlers above never run.
 * @param {Object} eventObj Gigya onLogin event
 */
function handleSocialRegistrationLogin(eventObj) {
	// Social only: e-mail/password sign-up is tracked on the screenset submit.
	if (!eventObj || !eventObj.newUser || resolveLoginProviderType(eventObj) === 'standard') {
		return;
	}

	// Already tracked by the complete-registration screen submit.
	if (wasRegistrationOkSent(resolveRegistrationAccountType(eventObj, {}))) {
		return;
	}

	trackRegistrationStep1(eventObj, {});
	ensureRegistrationOk(eventObj, {});
}

function handleRegistrationScreenLoad(eventObj, options) {
	if (getScreenId(eventObj) === 'gigya-verification-sent-screen') {
		ensureRegistrationOk(eventObj, options);
		trackRegistrationOkPageView();
	}
}

/**
 * @param {Object} eventObj
 */
function isPasswordResetFlow(eventObj, options) {
	options = options || {};

	if (options.forcePasswordResetFlow) {
		return true;
	}

	return isPasswordResetScreen(getSubmittedScreen(eventObj, options)) || isPasswordResetScreen(getDestinationScreen(eventObj));
}

function handleForgotPasswordAfterSubmit(eventObj, options) {
	if (!wasSubmitSuccessful(eventObj)) {
		return;
	}

	if (!isPasswordResetFlow(eventObj, options)) {
		return;
	}

	let submittedScreen = getSubmittedScreen(eventObj, options);

	if (submittedScreen.indexOf('reset-password') !== -1) {
		trackResetPasswordComplete();
		return;
	}

	if (submittedScreen.indexOf('forgot-password') !== -1) {
		trackResetPasswordSendEmail();
	}
}

/**
 * @param {Object} eventObj
 */
function handleForgotPasswordScreenLoad(eventObj) {
	let screenId = getScreenId(eventObj);

	if (isResetPasswordCompleteScreen(screenId)) {
		trackResetPasswordComplete();
		return;
	}

	if (isForgotPasswordEmailSentScreen(screenId)) {
		trackResetPasswordSendEmail();
	}
}

/** Subscription tracking */

let SUBSCRIPTION_HUB_PAGE_NAME = 'subscription:hub';
let SUBSCRIPTION_HUB_EVENT = 'subscription_hub';
let isCartSubscriptionTrackingInitialized = false;

let STEP_PHASES = {
	machine: 'machine_selection_phase',
	format: 'coffee_selection_phase',
	tiers: 'customize_phase',
	coffee: 'assortment_phase',
	addon: 'addon_phase',
	summary: 'summary_phase'
};

let DOC_STEP_NUMBERS = {
	machine: 1,
	format: 1,
	tiers: 2,
	coffee: 3,
	addon: 4,
	summary: 5
};

let EDIT_TARGET_DOC_STEPS = {
	machine: 'step-1',
	format: 'step-1',
	tiers: 'step-2',
	coffee: 'step-3',
	addon: 'step-4'
};

/**
 * PDF "View and edit summary #2": action is "edit_" + the clicked row label (British English,
 * independent of site locale).
 */
let VIEW_EDIT_SUMMARY_EDIT_ACTIONS = {
	machine: 'edit_select_your_machine',
	format: 'edit_select_your_format',
	tiers: 'edit_select_your_plan',
	coffee: 'edit_select_your_blend'
};

/**
 * Subscription Hub is a Page Designer landing (not Subscription-Show journey).
 * Subscription Hub PD landing → utag_data.page_name_nocountry "subscription:hub".
 * @returns {boolean}
 */
function isSubscriptionHubPage() {
	let pageEl = document.querySelector('.page[data-tealium-page-view]');
	let hubAttr = pageEl && pageEl.getAttribute('data-tealium-page-view');

	if (hubAttr === SUBSCRIPTION_HUB_PAGE_NAME || hubAttr === SUBSCRIPTION_HUB_EVENT) {
		return true;
	}

	let utagData = window.utag_data || {};
	let pageName = String(utagData.page_name || '').toLowerCase();
	let pageNameNoCountry = String(utagData.page_name_nocountry || '').toLowerCase();
	let isSubscriptionHubContext =
		String(utagData.page_type || '').toLowerCase() === 'subscription' &&
		String(utagData.page_section || '').toLowerCase() === 'subscription' &&
		String(utagData.page_subsection || '').toLowerCase() === 'hub';

	return (
		pageNameNoCountry === SUBSCRIPTION_HUB_PAGE_NAME ||
		pageName.endsWith(':' + SUBSCRIPTION_HUB_PAGE_NAME) ||
		pageNameNoCountry === SUBSCRIPTION_HUB_EVENT ||
		pageName === SUBSCRIPTION_HUB_EVENT ||
		isSubscriptionHubContext
	);
}

/**
 * @returns {boolean}
 */
function isSubscriptionJourneyPage() {
	return document.querySelector('[data-subscriptions-root]') !== null;
}

/**
 * @param {string} step
 * @returns {string}
 */
function normalizeInternalStep(step) {
	return String(step || '').toLowerCase();
}

/**
 * @param {string} subscriptionType
 * @returns {string}
 */
function getEventNameSubType(subscriptionType) {
	return subscriptionType === 'onlyCoffee' ? 'coffee' : 'machine_and_coffee';
}

/**
 * @param {string} internalStep
 * @returns {number|null}
 */
function getDocStepNumber(internalStep) {
	return DOC_STEP_NUMBERS[normalizeInternalStep(internalStep)] || null;
}

/**
 * @param {string} internalStep
 * @returns {string}
 */
function getStepPhase(internalStep) {
	return STEP_PHASES[normalizeInternalStep(internalStep)] || '';
}

/**
 * @param {HTMLElement} root
 * @param {Object} [state]
 * @returns {string}
 */
function getModelType(root, state) {
	if (root && root.getAttribute('data-coffee-selling-model-tiered') === 'true') {
		return 'tier_model';
	}

	if (state && state.sellingModel && state.sellingModel.isTiered) {
		return 'tier_model';
	}

	if (root && root.getAttribute('data-subscription-uses-tiered-models') === 'true') {
		return 'tier_model';
	}

	return 'untier_model';
}

/**
 * @param {string} subType
 * @param {number} docStepNumber
 * @returns {string}
 */
function buildStepViewEventName(subType, docStepNumber) {
	return 'subscription_' + subType + '_step-' + docStepNumber;
}

/**
 * @param {*} value
 * @returns {string}
 */
function trackText(value) {
	return gtmUtils.sanitizeLinkText(value);
}

/**
 * sanitizeLinkText already lowercases and turns whitespace into underscores — this only
 * adds the final a-z0-9_- whitelist clamp on top.
 * @param {*} value
 * @returns {string}
 */
function slugifyEventPart(value) {
	return trackText(value).replace(/[^a-z0-9_-]/g, '');
}

/**
 * PDF tier values are the bare plan (lover, expert, hero, super): SF plan names come as "Lavazza Expert".
 * @param {*} value
 * @returns {string}
 */
function slugifyTier(value) {
	return slugifyEventPart(value).replace(/^lavazza_/, '');
}

/**
 * @param {string} internalStep
 * @returns {string}
 */
function getDocStepSuffix(internalStep) {
	let docStepNumber = getDocStepNumber(normalizeInternalStep(internalStep));

	return docStepNumber ? 'step-' + docStepNumber : '';
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} payload
 * @returns {Object}
 */
function withBaseLinkContext(root, state, internalStep, payload) {
	return $.extend(buildBaseLinkContext(root, state, internalStep), payload);
}

/**
 * @param {Object} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} payload
 */
function sendSubscriptionLink(root, state, internalStep, payload) {
	gtmUtils.sendUtagLinkEvent(withBaseLinkContext(root, state, internalStep, payload));
}

/**
 * @param {Object} [sellingModel]
 * @returns {string}
 */
function getTierSlug(sellingModel) {
	if (!sellingModel) {
		return '';
	}

	// TierName__c / EditorialName__c ("Lover"); never the Salesforce id, which is alphanumeric.
	return slugifyTier(sellingModel.tierName || sellingModel.title || sellingModel.name || '');
}

/**
 * @param {Object} root
 * @param {Object} state
 * @param {string} internalStep
 * @returns {Object}
 */
function buildBaseLinkContext(root, state, internalStep) {
	let subscriptionType = (root && root.getAttribute('data-subscription-type')) || '';

	return {
		category: getEventNameSubType(subscriptionType),
		type: getModelType(root, state),
		step: getStepPhase(internalStep)
	};
}

/**
 * @param {Array|Object} collection
 * @returns {Array<{item: Object, quantity: number}>}
 */
function getSelectionRowsFromState(collection) {
	let list = Array.isArray(collection)
		? collection
		: collection && typeof collection === 'object'
		? Object.keys(collection).map(function (key) {
				return collection[key];
		  })
		: [];

	return list
		.map(function (item) {
			let quantity = Number((item && item.quantity) || 0);

			if (!item || quantity <= 0) {
				return null;
			}

			return {
				item: item,
				quantity: quantity
			};
		})
		.filter(Boolean);
}

/**
 * @param {Object} state
 * @returns {boolean}
 */
function isCoffeePresetMode(state) {
	state = state || {};

	if (state.coffeeMode === 'custom') {
		return false;
	}

	if (state.coffeeMode === 'preset' || state.preset) {
		return true;
	}

	return !getSelectionRowsFromState(state.products).length;
}

/**
 * @param {Object} payload
 * @param {Object} state
 * @returns {string}
 */
function applyCoffeeContinueLinkFields(payload, state) {
	if (isCoffeePresetMode(state)) {
		// PDF "subscription_package_selected": only tier + selected_package on top of category/type/step
		// ("none" on the untier model, like the edit step-3 view)
		payload.tier = resolveSubscriptionSelectedTier(state) || 'none';
		payload.selected_package = buildCoffeeStepExtras(state, { coffeeMode: 'preset' }).package_type;
		return 'subscription_package_selected';
	}

	// PDF "subscription_assortment_selected": tier + label (quantity_productname joined with "|") only.
	payload.tier = resolveSubscriptionSelectedTier(state) || 'none';
	payload.label = getSelectionRowsFromState(state.products)
		.map(function (row) {
			return row.quantity + '_' + trackText(row.item.productName || row.item.name || row.item.id);
		})
		.join('|');
	return 'subscription_assortment_selected';
}

/**
 * @param {Object} payload
 * @param {Object} state
 * @returns {string}
 */
function applyAddonContinueLinkFields(payload, state) {
	let types = [];
	let labels = [];

	function collect(type, item, quantity) {
		let name = item && trackText(item.productName || item.name || item.id);

		if (!name) {
			return;
		}
		if (types.indexOf(type) === -1) {
			types.push(type);
		}
		labels.push((parseInt(quantity, 10) || 1) + '_' + name);
	}

	// PDF: addon_type free_gift / add_on (both -> "free_gift|add_on"), label quantity_name joined with "|".
	(state.freegifts || []).forEach(function (gift) {
		collect('free_gift', gift, gift && gift.quantity);
	});
	getSelectionRowsFromState(state.addons).forEach(function (row) {
		collect('add_on', row.item, row.quantity);
	});

	$.extend(payload, buildSummaryPackageFields(state));
	payload.label = labels.join('|');

	return 'subscription_' + (types.join('|') || 'add_on') + '_selected';
}

/**
 * @param {Object} state
 * @returns {string}
 */
function resolveSubscriptionSelectedFormat(state) {
	let format = state && state.format;
	let category;
	let system;

	if (!format) {
		return '';
	}

	// PDF values (cups-amm, cups-ncc, beans, ground, pods, tabs) from the selling model
	// LavazzaCategory__c / LavazzaSystem__c, never the Salesforce record id.
	category = String(format.category || format.LavazzaCategory__c || '').toLowerCase();
	system = String((format.system || format.LavazzaSystem__c || '') + ' ' + (format.formatShortCode || '')).toLowerCase();

	if (category.indexOf('bean') !== -1) {
		return 'beans';
	}
	if (category.indexOf('ground') !== -1) {
		return 'ground';
	}
	if (category.indexOf('tab') !== -1) {
		return 'tabs';
	}
	if (category.indexOf('pod') !== -1 || category.indexOf('k-cup') !== -1) {
		return 'pods';
	}
	if (category.indexOf('capsule') !== -1) {
		if (system.indexOf('modo') !== -1 || system.indexOf('amm') !== -1) {
			return 'cups-amm';
		}
		if (system.indexOf('nespresso') !== -1 || system.indexOf('ncc') !== -1) {
			return 'cups-ncc';
		}
		return 'cups-' + slugifyEventPart(format.system || format.LavazzaSystem__c || '');
	}

	return slugifyEventPart(format.category || format.LavazzaCategory__c || '');
}

/**
 * @param {Object} state
 * @returns {string}
 */
function resolveSubscriptionSelectedTier(state) {
	// Untier selling models have no plan: callers fall back to "none".
	if (!state || !state.sellingModel || state.sellingModel.isTiered === false) {
		return '';
	}

	return getTierSlug(state.sellingModel);
}

/**
 * @param {Object} payload
 * @param {Object} state
 * @param {string} internalStep
 * @param {HTMLElement} root
 * @returns {string|null} eventName
 */
function applyContinueLinkStepFields(payload, state, internalStep, root) {
	switch (internalStep) {
		case 'machine':
			// PDF label: selected machine name, same source as the summary machine_name.
			payload.label = state.machine ? trackText(state.machine.productName || state.machine.name) : '';
			return 'subscription_machine_selected';
		case 'format': {
			const selectedFormat = resolveSubscriptionSelectedFormat(state);

			if (selectedFormat) {
				payload.selected_format = selectedFormat;
			}

			return 'subscription_format_selected';
		}
		case 'tiers': {
			payload.selected_tier = resolveSubscriptionSelectedTier(state) || 'none';

			return 'subscription_tier_selected';
		}
		case 'coffee':
			return applyCoffeeContinueLinkFields(payload, state);
		case 'addon':
			return applyAddonContinueLinkFields(payload, state);
		default:
			return null;
	}
}

/**
 * @param {Object} root
 * @param {Object} state
 * @param {string} internalStep
 * @returns {Object|null}
 */
function buildContinueLinkPayload(root, state, internalStep) {
	internalStep = normalizeInternalStep(internalStep);
	state = state || {};

	if (!root || !internalStep) {
		return null;
	}

	let payload = buildBaseLinkContext(root, state, internalStep);
	let eventName = applyContinueLinkStepFields(payload, state, internalStep, root);

	if (!eventName) {
		return null;
	}

	payload.event_name = eventName;
	return payload;
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionContinueLink(root, state, internalStep) {
	let payload = buildContinueLinkPayload(root, state, internalStep);

	if (!payload) {
		return;
	}

	gtmUtils.sendUtagLinkEvent(payload);
}

/**
 * @param {Object} [state]
 * @param {Object} [options]
 * @returns {Object}
 */
function buildCoffeeStepExtras(state, options) {
	options = options || {};
	let coffeeMode = options.coffeeMode;

	if (!coffeeMode && state) {
		if (state.coffeeMode) {
			coffeeMode = state.coffeeMode;
		} else if (state.preset) {
			coffeeMode = 'preset';
		} else if (state.products && state.products.length) {
			coffeeMode = 'custom';
		}
	}

	if (options.forceCustomized || coffeeMode === 'custom') {
		return {
			package_selection_screen: 'custom',
			package_type: 'customized'
		};
	}

	let presetLabel = '';

	if (state && state.preset) {
		presetLabel = gtmUtils.sanitizeLinkText(state.preset.name || state.preset.id || '');
	}

	return {
		package_selection_screen: 'preset',
		package_type: presetLabel
	};
}

/**
 * @param {HTMLElement} root
 * @param {Object} [state]
 * @param {Object} [options]
 * @returns {Object|null}
 */
function buildStepViewPayload(root, state, options) {
	options = options || {};
	state = state || {};

	if (!root) {
		return null;
	}

	let subscriptionType = root.getAttribute('data-subscription-type') || '';
	let internalStep = normalizeInternalStep(options.internalStep || root.getAttribute('data-current-step'));
	let docStepNumber = getDocStepNumber(internalStep);

	if (!docStepNumber) {
		return null;
	}

	let payload = buildBaseLinkContext(root, state, internalStep);
	payload.event_name = buildStepViewEventName(getEventNameSubType(subscriptionType), docStepNumber);
	$.extend(
		payload,
		buildStepPageFields(
			getEventNameSubType(subscriptionType) +
				':' +
				['first', 'second', 'third', 'fourth', 'fifth'][docStepNumber - 1] +
				'-step'
		)
	);

	if (internalStep === 'coffee') {
		$.extend(payload, buildCoffeeStepViewExtras(root, state, options));
	} else if (internalStep === 'addon' || internalStep === 'summary') {
		$.extend(payload, buildSummaryPackageFields(state));
	}

	return payload;
}

/**
 * Which step-3 packages the server rendered: preset cards and/or the custom assortment view.
 * @param {HTMLElement} root
 * @returns {{preset: boolean, custom: boolean}}
 */
function getCoffeePackageAvailability(root) {
	return {
		preset: root.querySelectorAll('[data-preset-card]:not([data-coffee-mode])').length > 0,
		custom: !!root.querySelector('[data-coffee-custom-view]')
	};
}

/**
 * PDF "Subscription step 3" scenarios: custom_only / preconfigured_only / custom_and_preconfigured,
 * second pageview after the switch to custom → custom_only + switch true.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {Object} options
 * @returns {Object}
 */
function buildCoffeeStepViewExtras(root, state, options) {
	let available = getCoffeePackageAvailability(root);
	let extras = {
		tier: resolveSubscriptionSelectedTier(state) || 'none',
		package_type: 'preconfigured',
		package_selection_screen: 'custom_and_preconfigured',
		package_selection_switch: 'false'
	};

	if (!available.preset) {
		extras.package_type = 'customized';
		extras.package_selection_screen = 'custom_only';
		extras.package_selection_switch = '';
	} else if (!available.custom) {
		extras.package_selection_screen = 'preconfigured_only';
		extras.package_selection_switch = '';
	} else if (options.forceCustomized || !isCoffeePresetMode(state)) {
		extras.package_type = 'customized';
		extras.package_selection_screen = 'custom_only';
		extras.package_selection_switch = 'true';
	}

	return extras;
}

/**
 * @param {HTMLElement} root
 * @param {Object} [state]
 * @param {Object} [options]
 */
function trackSubscriptionStepView(root, state, options) {
	let payload = buildStepViewPayload(root, state, options);

	if (!payload) {
		return;
	}

	gtmUtils.sendUtagViewEvent(payload);
}

/**
 * Marks customized step-3 view as sent when landing already in custom mode.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function markCoffeeCustomStepViewSent(root, state, internalStep) {
	if (normalizeInternalStep(internalStep) !== 'coffee') {
		return;
	}

	let coffeeMode = state.coffeeMode;

	if (!coffeeMode && state.products && state.products.length) {
		coffeeMode = 'custom';
	}

	if (coffeeMode === 'custom') {
		root._subscriptionTrackingCoffeeCustomViewSent = true;
	}
}

/**
 * Second step-3 pageview when the user switches preset → custom.
 * @param {HTMLElement} root
 * @param {Object} state
 */
function trackSubscriptionCoffeeCustomStepView(root, state) {
	if (!root || root._subscriptionTrackingCoffeeCustomViewSent) {
		return;
	}

	root._subscriptionTrackingCoffeeCustomViewSent = true;

	let available = getCoffeePackageAvailability(root);

	// Only scenario 3b (presets + switch to custom) has a second pageview.
	if (!available.preset || !available.custom) {
		return;
	}

	trackSubscriptionStepView(root, state, {
		internalStep: 'coffee',
		coffeeMode: 'custom',
		forceCustomized: true
	});
}

/**
 * Sticky discover CTA — switch subscription type.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionDiscover(root, state, internalStep) {
	let currentType = getEventNameSubType((root && root.getAttribute('data-subscription-type')) || '');

	// The banner proposes the other subscription type.
	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_discover', {
		action: currentType === 'coffee' ? 'change_to_machine-and-coffee-sub' : 'change_to_coffee-sub'
	});
}

/**
 * Reset custom assortment from sticky minimum block.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionResetAssortment(root, state, internalStep) {
	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_reset_assortment');
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} [sellingModel]
 */
function trackSubscriptionChangingTier(root, state, internalStep, sellingModel) {
	let tierSlug = getTierSlug(sellingModel);
	let stepSuffix = getDocStepSuffix(internalStep);

	if (!stepSuffix) {
		return;
	}

	sendSubscriptionLink(root, state, internalStep, {
		event_name: 'subscription_changing_tier_' + (tierSlug || 'none') + '_' + stepSuffix
	});
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionLeavingTierChange(root, state, internalStep) {
	sendSubscriptionLink(root, state, internalStep, {
		event_name: 'subscription_leaving_tier_change'
	});
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} sellingModel
 */
function trackSubscriptionTierChange(root, state, internalStep, sellingModel) {
	let tierSlug = getTierSlug(sellingModel);

	if (!tierSlug) {
		return;
	}

	sendSubscriptionLink(root, state, internalStep, {
		event_name: 'subscription_tier_change_' + tierSlug
	});
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionAbandonModal(root, state, internalStep) {
	let stepSuffix = getDocStepSuffix(internalStep);

	if (!stepSuffix) {
		return;
	}

	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_abandon_modal_' + stepSuffix);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {'yes'|'no'} choice
 */
function trackSubscriptionLeavingFlowChoice(root, state, internalStep, choice) {
	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_leaving_flow_choice_' + choice);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionReplaceModal(root, state, internalStep) {
	let stepSuffix = getDocStepSuffix(internalStep);

	if (!stepSuffix) {
		return;
	}

	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_replace_modal_' + stepSuffix);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {'yes'|'no'} choice
 */
function trackSubscriptionReplaceFlowChoice(root, state, internalStep, choice) {
	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_replace_flow_choice_' + choice);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} options
 * @param {string} [options.action] literal action (e.g. "open")
 * @param {string} [options.editStep] internal step of the clicked edit icon, mapped to the PDF action
 */
function trackSubscriptionViewEditSummary(root, state, internalStep, options) {
	let stepSuffix = getDocStepSuffix(internalStep);
	let action;
	let dedupeKey;
	let now;

	if (!stepSuffix || !options) {
		return;
	}

	action = options.editStep ? VIEW_EDIT_SUMMARY_EDIT_ACTIONS[normalizeInternalStep(options.editStep)] : options.action;

	if (!action) {
		return;
	}

	dedupeKey = 'subscription_view-edit-summary_' + stepSuffix + ':' + action;
	now = Date.now();

	if (
		root._subscriptionViewEditSummaryDedupe &&
		root._subscriptionViewEditSummaryDedupe.key === dedupeKey &&
		now - root._subscriptionViewEditSummaryDedupe.at < 400
	) {
		return;
	}

	root._subscriptionViewEditSummaryDedupe = {
		key: dedupeKey,
		at: now
	};

	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_view-edit-summary_' + stepSuffix, {
		action: action
	});
}

/**
 * Edit from summary step page (not drawer).
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} editStep
 */
function trackSubscriptionEditFromSummary(root, state, editStep) {
	let docStep = EDIT_TARGET_DOC_STEPS[normalizeInternalStep(editStep)];

	if (!docStep) {
		return;
	}

	sendSubscriptionLink(
		root,
		state,
		'summary',
		attachSubscriptionModule(
			$.extend(buildSummaryPackageFields(state), {
				// Journey summary (PDF "Subscription step 5 - edit"); cta_manage_subscription_edit_* is the personal area one.
				event_name: 'subscription_edit_' + docStep
			}),
			buildEcommerceSubscriptionModuleFromJourney(root, state)
		)
	);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} [frequency]
 */
function trackSubscriptionChangingFrequency(root, state, internalStep, frequency) {
	let freqSlug = slugifyEventPart(formatEcommerceSubscriptionFrequency(frequency || state.frequency));
	let stepSuffix = getDocStepSuffix(internalStep);

	if (!stepSuffix) {
		return;
	}

	sendSubscriptionLinkWithoutStep(
		root,
		state,
		internalStep,
		'subscription_changing_frequency_' + (freqSlug || 'none') + '_' + stepSuffix
	);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 */
function trackSubscriptionLeavingFrequencyChange(root, state, internalStep) {
	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_leaving_frequency_change');
}

/**
 * Slug is the PDF frequency token of the new frequency (15days / 2months).
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {Object} frequency
 */
function trackSubscriptionFrequencyChange(root, state, internalStep, frequency) {
	let freqSlug = slugifyEventPart(formatEcommerceSubscriptionFrequency(frequency));

	if (!freqSlug) {
		return;
	}

	sendSubscriptionLinkWithoutStep(root, state, internalStep, 'subscription_frequency_change_' + freqSlug);
}

/**
 * Events whose tracking plan carries only category + type (no step): frequency edits, view-edit summary, leave and replace modals.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} internalStep
 * @param {string} eventName
 * @param {Object} [extraFields]
 */
function sendSubscriptionLinkWithoutStep(root, state, internalStep, eventName, extraFields) {
	let payload = withBaseLinkContext(
		root,
		state,
		internalStep,
		$.extend(
			{
				event_name: eventName
			},
			extraFields
		)
	);

	delete payload.step;
	gtmUtils.sendUtagLinkEvent(payload);
}

/**
 * PDF tier + package_type of the journey summary (step 4/5 views and add-and-go-to-cart).
 * @param {Object} state
 * @returns {{ tier: string, package_type: string }}
 */
function buildSummaryPackageFields(state) {
	return {
		tier: resolveSubscriptionSelectedTier(state) || 'none',
		package_type: isCoffeePresetMode(state) ? 'preconfigured' : 'customized'
	};
}

/**
 * @param {Object} item
 * @param {number} quantity
 * @returns {Object|null}
 */
function formatSubscriptionProductModule(item, quantity) {
	var formatted;

	if (!item || !item.id) {
		return null;
	}

	formatted = gtmUtils.buildEcommerceProductPayload(item, quantity);

	if (formatted) {
		formatted.product_type = 'subscription';
	}

	return formatted;
}

/**
 * Line items for the subscription module (machine, coffees, addons, gifts).
 * @param {Object} state
 * @returns {Array<Object>}
 */
function buildSubscriptionProducts(state) {
	let products = [];

	state = state || {};

	if (state.machine) {
		products.push(formatSubscriptionProductModule(state.machine, 1));
	}

	getSelectionRowsFromState(state.products).forEach(function (row) {
		products.push(formatSubscriptionProductModule(row.item, row.quantity));
	});

	getSelectionRowsFromState(state.addons).forEach(function (row) {
		products.push(formatSubscriptionProductModule(row.item, row.quantity));
	});

	if (state.freegifts && state.freegifts.length) {
		state.freegifts.forEach(function (gift) {
			products.push(formatSubscriptionProductModule(gift, 1));
		});
	}

	return products.filter(Boolean);
}

/**
 * PDF ecommerce frequency token: 2months / 15days.
 * @param {Object} [frequency]
 * @returns {string}
 */
function formatEcommerceSubscriptionFrequency(frequency) {
	let amount;
	let unit;
	let plural;

	if (!frequency) {
		return '';
	}

	amount = String(frequency.id || frequency.frequency || '').replace(/[^0-9]/g, '');
	unit = String(frequency.frequencyUOM || frequency.uom || '').toLowerCase();

	if (!amount) {
		return sanitizeLinkText(frequency.id || '');
	}

	if (unit.startsWith('month')) {
		plural = amount === '1' ? 'month' : 'months';
		return amount + plural;
	}

	if (unit.startsWith('week')) {
		plural = amount === '1' ? 'week' : 'weeks';
		return amount + plural;
	}

	if (unit.startsWith('day')) {
		plural = amount === '1' ? 'day' : 'days';
		return amount + plural;
	}

	return amount + sanitizeLinkText(unit);
}

/**
 * PDF subscription_type (beans, machine_capsule, roast_and_ground, k-cups, ...) from the selling model
 * LavazzaCategory__c ("Coffee Beans", "Capsules", "Ground Coffee", "K-Cup Pods", "Tabs", ...).
 * @param {string} lavazzaCategory
 * @returns {string}
 */
function mapSubscriptionCategoryToTrackingType(lavazzaCategory) {
	let normalized = String(lavazzaCategory || '')
		.toLowerCase()
		.replace(/[^a-z]/g, '');

	if (!normalized) {
		return '';
	}
	if (normalized.indexOf('kcup') !== -1) {
		return 'k-cups';
	}
	if (normalized.indexOf('bean') !== -1) {
		return 'beans';
	}
	if (normalized.indexOf('ground') !== -1) {
		return 'roast_and_ground';
	}
	if (normalized.indexOf('capsule') !== -1) {
		return 'machine_capsule';
	}

	return sanitizeLinkText(lavazzaCategory);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @returns {string}
 */
function resolveJourneySubscriptionType(root, state) {
	let sellingModel = (state && state.sellingModel) || {};
	let format = (state && state.format) || {};

	return mapSubscriptionCategoryToTrackingType(sellingModel.category || format.LavazzaCategory__c || '');
}

/**
 * Title already rendered on the cart subscription card.
 * @returns {string}
 */
function getCartCardSubscriptionProductName() {
	return String($('.cart-subscription-card__title').first().text() || '').trim();
}

/**
 * Ecommerce &lt;subscription module&gt; for cart_add from the journey.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} [subscriptionProductName] cart card title
 * @returns {Object}
 */
function buildEcommerceSubscriptionModuleFromJourney(root, state, subscriptionProductName) {
	let module = {};
	let sellingModel;
	let coffeeIds = [];

	state = state || {};
	sellingModel = state.sellingModel || {};

	module.subscription_category = getEventNameSubType((root && root.getAttribute('data-subscription-type')) || '');
	module.subscription_type = resolveJourneySubscriptionType(root, state) || '';
	module.subscription_frequency = formatEcommerceSubscriptionFrequency(state.frequency);
	module.subscription_product_name = sanitizeLinkText(subscriptionProductName || getCartCardSubscriptionProductName());
	module.subscription_product_sku = sanitizeLinkText(sellingModel.subscriptionMaterialCode || '') || null;
	module.product_sku_group = '';
	module.subscription_product_quantity = '1';
	module.subscription_product_price = formatProductsPotentialRevenue(buildSubscriptionProducts(state));

	function collectCoffeeRow(row) {
		let id = row.item && (row.item.id || row.item.productId || row.item.pid);

		if (id && coffeeIds.indexOf(String(id)) === -1) {
			coffeeIds.push(String(id));
		}
	}

	// PDF product_sku_group: every product of the subscription (machine, coffees, add-ons, gifts), same as the server.
	if (state.machine) {
		collectCoffeeRow({ item: state.machine });
	}
	getSelectionRowsFromState(state.products).forEach(collectCoffeeRow);
	getSelectionRowsFromState(state.addons).forEach(collectCoffeeRow);
	(state.freegifts || []).forEach(function (gift) {
		collectCoffeeRow({ item: gift });
	});

	module.product_sku_group = coffeeIds.join(',');

	return gtmUtils.formatSubscriptionModule(module) || module;
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @returns {Object}
 */
function buildAddAndGoToCartPayload(root, state, serverModule) {
	return attachSubscriptionModule(
		$.extend({}, buildBaseLinkContext(root, state, 'summary'), buildSummaryPackageFields(state), {
			event_name: 'subscription_add-and-go-to-cart'
		}),
		serverModule || buildEcommerceSubscriptionModuleFromJourney(root, state)
	);
}

/**
 * @param {HTMLElement} root
 * @param {Object} state
 * @returns {Object}
 */
/**
 * @param {Array<Object>} journeyProducts
 * @returns {Object}
 */
function indexJourneyProductsById(journeyProducts) {
	let journeyById = {};
	let i;
	let journeyProduct;
	let productId;

	if (!journeyProducts || !journeyProducts.length) {
		return journeyById;
	}

	for (i = 0; i < journeyProducts.length; i++) {
		journeyProduct = journeyProducts[i];
		productId = journeyProduct && (journeyProduct.product_id || journeyProduct.product);

		if (productId) {
			journeyById[String(productId)] = journeyProduct;
		}
	}

	return journeyById;
}

/**
 * @param {Object} formatted
 * @param {Object} journeyById
 */
function mergeJourneyImageOntoFormatted(formatted, journeyById) {
	let journeyProduct;

	if (formatted.product_img_src) {
		return;
	}

	journeyProduct = journeyById[String(formatted.product_id || formatted.product)];
	if (journeyProduct && journeyProduct.product_img_src) {
		formatted.product_img_src = journeyProduct.product_img_src;
	}
}

/**
 * @param {Array<Object>} rawModules
 * @param {Array<Object>} journeyProducts
 * @returns {Object}
 */
function formatServerProductModules(rawModules, journeyProducts) {
	let products = [];
	let journeyById = indexJourneyProductsById(journeyProducts);
	let i;
	let formatted;

	if (!rawModules || !rawModules.length) {
		return products;
	}

	for (i = 0; i < rawModules.length; i++) {
		formatted = gtmUtils.formatProductModule(rawModules[i]);

		if (!formatted || !(formatted.product_id || formatted.product)) {
			continue;
		}

		mergeJourneyImageOntoFormatted(formatted, journeyById);
		products.push(formatted);
	}

	return products;
}

function buildCartAddPayload(root, state, subscriptionProductName, serverModule, serverProductModules) {
	let journeyProducts = buildSubscriptionProducts(state);
	let ecommerceModule =
		gtmUtils.formatSubscriptionModule(serverModule) ||
		buildEcommerceSubscriptionModuleFromJourney(root, state, subscriptionProductName);
	let products = formatServerProductModules(serverProductModules, journeyProducts);

	if (!products.length) {
		products = journeyProducts;
	}

	return $.extend({}, ecommerceModule, {
		event_name: 'cart_add',
		products: products,
		product_impression_type: 'subscription',
		product_purchase_type: resolveProductPurchaseType(
			$.extend({}, products[0] || {}, { isSubscription: true }),
			root ? $(root) : $(),
			ecommerceModule
		),
		product_potential_revenue: formatProductsPotentialRevenue(products)
	});
}

/**
 * Step 5 add to cart: subscription journey event + transversal cart_add.
 * Product module and subscription module stay as sibling fields on cart_add.
 * @param {HTMLElement} root
 * @param {Object} state
 * @param {string} [subscriptionProductName]
 * @param {Object} [serverModule]
 * @param {Array<Object>} [serverProductModules]
 */
function trackSubscriptionAddToCart(root, state, subscriptionProductName, serverModule, serverProductModules) {
	if (!root) {
		return;
	}

	state = state || {};

	gtmUtils.sendUtagLinkEvent(buildAddAndGoToCartPayload(root, state, serverModule));
	gtmUtils.sendUtagLinkEvent(buildCartAddPayload(root, state, subscriptionProductName, serverModule, serverProductModules));
}

/**
 * @param {HTMLElement|null} lineItemEl
 * @param {string} eventName
 */
function trackCartSubscriptionAction(lineItemEl, eventName) {
	if (!eventName) {
		return;
	}

	gtmUtils.sendUtagLinkEvent(
		attachSubscriptionModule(
			{
				event_name: eventName
			},
			gtmUtils.parseProductTrackingJson(lineItemEl && lineItemEl.getAttribute('data-tealium-subscription-module'))
		)
	);
}

/** Search layer tracking (AUR-363 / AUR-364) */

let isSearchLayerTrackedOpen = false;

function trackSearchLayerOpen() {
	isSearchLayerTrackedOpen = true;
}

let lastSearchLayerCloseAt = 0;

function trackSearchLayerClose() {
	let now = Date.now();

	// One event per user action, even if several close paths run for the same click.
	if (!isSearchLayerTrackedOpen || now - lastSearchLayerCloseAt < 1000) {
		return;
	}

	isSearchLayerTrackedOpen = false;
	lastSearchLayerCloseAt = now;
	gtmUtils.sendUtagLinkEvent({
		event_name: 'searchresult_layer_close'
	});
}

/**
 * @param {jQuery} $item
 * @returns {string}
 */
function resolveSearchSuggestionType($item) {
	let itemId = String($item.attr('id') || '');

	if (itemId.startsWith('phrase-')) {
		return 'suggestion';
	}
	if (itemId.startsWith('category-')) {
		return 'category';
	}
	if (itemId.startsWith('content-')) {
		return 'related_content';
	}
	if (itemId.startsWith('product-')) {
		return 'product';
	}

	return '';
}

/**
 * @param {jQuery} $link
 * @returns {string}
 */
function getSearchSuggestionLabel($link) {
	let $item = $link.closest('.item');

	if ($item.find('.suggestions__category-name').length) {
		return $item.find('.suggestions__category-name').first().text().trim();
	}

	if ($link.hasClass('suggestions__product-link')) {
		return $link.find('.suggestions__product-name').first().text().trim() || $link.attr('aria-label') || $link.text();
	}

	return $link.text().trim();
}

/**
 * @param {string} prefix
 * @param {string} value
 * @returns {string}
 */
function formatPrefixedSearchTerm(prefix, value) {
	const term = sanitizeLinkText(value);

	if (!prefix || !term) {
		return '';
	}

	return prefix + ':' + term;
}

/**
 * PDF Search-query — both direct and assisted.
 * @param {string} searchType
 * @param {string} term
 * @param {number} resultCount
 */
function trackSearchQuery(searchType, term, resultCount) {
	const searchTerm = formatPrefixedSearchTerm(searchType, term);
	let count = parseInt(resultCount, 10);

	if (!searchTerm) {
		return;
	}

	if (isNaN(count) || count < 0) {
		count = 0;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'search_result_query',
		search_term: searchTerm,
		search_type: searchType,
		search_result: String(count)
	});
}

/**
 * PDF Search-direct: search_term: "keyword: $$$search_term$$$"
 * @param {string} query
 * @param {number} [resultCount]
 */
function trackSearchDirect(query, resultCount) {
	const searchTerm = formatPrefixedSearchTerm('keyword', query);

	if (!searchTerm) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'search_result_keyword',
		search_term: searchTerm
	});
	trackSearchQuery('direct', query, resultCount);
}

let PENDING_ASSISTED_SEARCH_KEY = 'gtmPendingAssistedSearch';

/**
 * @param {string} type
 * @param {string} term
 */
function storePendingAssistedSearch(type, term) {
	try {
		window.sessionStorage.setItem(PENDING_ASSISTED_SEARCH_KEY, JSON.stringify({ type: type, term: term }));
	} catch (e) {
		// ignore storage failures
	}
}

/**
 * @returns {{type: string, term: string}|null}
 */
function consumePendingAssistedSearch() {
	let raw;

	try {
		raw = window.sessionStorage.getItem(PENDING_ASSISTED_SEARCH_KEY);
		if (!raw) {
			return null;
		}
		window.sessionStorage.removeItem(PENDING_ASSISTED_SEARCH_KEY);
		return JSON.parse(raw);
	} catch (e) {
		return null;
	}
}

/**
 * Fires on page load: a direct internal search (?q= in the URL), or a deferred assisted search
 */
function trackSearchResultsPageViewIfApplicable() {
	let pending = consumePendingAssistedSearch();
	let $resultCountEl = $('[data-tealium-search-result-count]').first();
	let resultCount;

	if (pending) {
		if (!$resultCountEl.length) {
			return;
		}

		resultCount = $resultCountEl.attr('data-tealium-search-result-count');
		trackSearchQuery('assisted', pending.term, resultCount);
		return;
	}

	if (!isInternalSearchResultsPage()) {
		return;
	}

	let query = getInternalSearchQueryFromUrl();

	if (!query) {
		return;
	}

	resultCount = $resultCountEl.attr('data-tealium-search-result-count');

	trackSearchDirect(query, resultCount);
}

/**
 * PDF Search-assisted: search_term: "suggestion: $$$search_term$$$"
 * @param {string} type
 * @param {string} term
 */
function trackSearchAssisted(type, term) {
	const searchTerm = formatPrefixedSearchTerm('suggestion', term);

	if (!type || !searchTerm) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'search_result_' + type,
		search_term: searchTerm
	});

	if (type === 'product' || type === 'related_content') {
		trackSearchQuery('assisted', term, 1);
		return;
	}

	storePendingAssistedSearch(type, term);
}

function initSearchLayerTracking() {
	if (window.isSearchLayerTrackingInitialized) {
		return;
	}

	window.isSearchLayerTrackingInitialized = true;

	$(document).on('click focusin', '.search-field', function onSearchLayerOpen() {
		trackSearchLayerOpen();
	});

	// searchresult_layer_close is sent by desktopSearch / mobileSearch close() (trackLayerClose) when the
	// layer actually closes: no extra click / Escape listeners here, they made the event fire twice.

	$(document).on(
		'click',
		'.suggestions .suggestions__link, .suggestions .suggestions__product-link',
		function onSearchSuggestionClick() {
			const $link = $(this);
			const $item = $link.closest('.item');
			const type = resolveSearchSuggestionType($item);
			const term = getSearchSuggestionLabel($link);

			if (!type || !term) {
				return;
			}

			trackSearchAssisted(type, term);
		}
	);

	$(document).on('click', '.search-desktop-layer__link, .search-mobile-layer__link', function onSearchPopularCategoryClick() {
		const $link = $(this);
		const term = $link.text().trim();

		if (!term) {
			return;
		}

		trackSearchAssisted('most_popular', term);
	});
}

/** Subscription management tracking (AUR-363) — account area */

/**
 * Personal area frequency drawer: fired by the save handler once the update succeeded,
 * before it navigates away (a global ajaxSuccess listener ran after the reload had started).
 * @param {HTMLElement|jQuery} selectedOption the active [data-frequency-select] option
 */
function trackSubscriptionEditFrequencySelected(selectedOption) {
	const $selected = $(selectedOption).first();
	let sourceJson;

	try {
		sourceJson = JSON.parse(String($selected.attr('data-source-json') || '{}')) || {};
	} catch (e) {
		sourceJson = {};
	}

	const frequency = {
		frequency: $selected.attr('data-frequency-days') || sourceJson.frequency,
		frequencyUOM: sourceJson.frequencyUOM || sourceJson.uom || 'day'
	};
	const label =
		formatEcommerceSubscriptionFrequency(frequency) || sanitizeLinkText($selected.attr('data-frequency-label') || '');

	if (!label) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'subscription_edit_frequency_selected',
		label: label
	});
}

const SUBSCRIPTION_MANAGEMENT_EDIT_DRAWERS = {
	'subscription-shipping-address-drawer': 'shipping',
	'subscription-payment-method-drawer': 'payment_method'
};

/**
 * PDF Subscription-edit: event_name: "cta_manage_subscription_edit_$$$section$$$"
 * @param {string} section
 */
function trackSubscriptionEditSection(section) {
	const slug = sanitizeLinkText(section);

	if (!slug) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'cta_manage_subscription_edit_' + slug
	});
}

/**
 * @param {string} eventName
 * @param {string} [label]
 */
function trackSubscriptionManagementEvent(eventName, label) {
	let payload = {
		event_name: eventName
	};

	if (label) {
		payload.label = gtmUtils.sanitizeLinkText(label);
	}

	gtmUtils.sendUtagLinkEvent(payload);
}

let lastSubscriptionCancellationSuccessKey = '';
let lastSubscriptionCancellationSuccessAt = 0;

/**
 * @param {JQuery} [$drawer]
 * @returns {{ hasPenalty: boolean, category: string }}
 */
function resolveSubscriptionCancellationTrackingDetailFromDom($drawer, via) {
	$drawer = $drawer && $drawer.length ? $drawer : $('#subscription-cancellation-drawer');
	const $root = $drawer.find('[data-subscription-cancellation-drawer-content-root]').first();
	const hasFee = String($root.attr('data-subscription-cancellation-has-fee') || '') === 'true';
	const isAsm = String($root.attr('data-subscription-cancellation-is-asm') || '') === 'true';
	const skipFee = $drawer.find('[data-subscription-cancellation-skip-fee]').is(':checked');
	const $selected = $drawer.find('[data-subscription-cancellation-reasons] option:selected').first();
	const resolvedVia = via || 'cancel';
	let hasPenalty = false;

	if (resolvedVia === 'paybylink' || resolvedVia === 'authorize_fee') {
		hasPenalty = true;
	} else {
		hasPenalty = !!(hasFee && !(isAsm && skipFee));
	}

	return {
		hasPenalty: hasPenalty,
		category: sanitizeLinkText(String($selected.text() || $selected.val() || '').trim())
	};
}

/**
 * @param {Object} [detail]
 */
function trackSubscriptionCancellationSucceeded(detail) {
	detail = detail || {};
	const eventName = detail.hasPenalty ? 'cta_unsub_subscription_contact_customer_service' : 'cta_unsub_subscription_nopenal';
	const category = sanitizeLinkText(String(detail.category || ''));
	const dedupeKey = eventName + '|' + category;
	const now = Date.now();
	const payload = {
		event_name: eventName
	};

	if (lastSubscriptionCancellationSuccessKey === dedupeKey && now - lastSubscriptionCancellationSuccessAt < 3000) {
		return;
	}

	lastSubscriptionCancellationSuccessKey = dedupeKey;
	lastSubscriptionCancellationSuccessAt = now;

	if (category) {
		payload.category = category;
	}

	gtmUtils.sendUtagLinkEvent(payload);
}

function trackSubscriptionCancellationOpened() {
	trackSubscriptionManagementEvent('cta_manage_subscription_unsubscribe');
}

/**
 * @param {Object} xhr
 * @param {Object} settings
 * @returns {boolean}
 */
function isSubscriptionCancellationAjaxSuccess(xhr, settings) {
	const url = settings && settings.url ? String(settings.url) : '';
	const body = xhr && xhr.responseJSON;

	if (isSubscriptionSaveErrorResponse(xhr)) {
		return false;
	}

	if (url.indexOf('SubMgmt-CancelSubscription') !== -1) {
		return true;
	}

	if (url.indexOf('SubMgmt-CreateCancellationPayByLink') !== -1) {
		return true;
	}

	if (url.indexOf('SubMgmt-AuthorizeCancellationFee') !== -1) {
		return !!(body && body.ok && body.subId);
	}

	return false;
}

/**
 * @param {Object} xhr
 * @param {Object} settings
 */
function trackSubscriptionCancellationAjaxSuccess(xhr, settings) {
	const url = settings && settings.url ? String(settings.url) : '';
	let via = 'cancel';

	if (url.indexOf('SubMgmt-CreateCancellationPayByLink') !== -1) {
		via = 'paybylink';
	} else if (url.indexOf('SubMgmt-AuthorizeCancellationFee') !== -1) {
		via = 'authorize_fee';
	}

	trackSubscriptionCancellationSucceeded(
		resolveSubscriptionCancellationTrackingDetailFromDom($('#subscription-cancellation-drawer'), via)
	);
}

/** Download doc tracking (AUR-364) */

/**
 * File title from the document URL: last path segment without extension (e.g. tablì_istruzioni).
 * @param {string} href
 * @returns {string}
 */
function resolveDownloadFileTitle(href) {
	let name = String(href || '').split(/[?#]/)[0].split('/').pop() || '';

	try {
		name = decodeURIComponent(name);
	} catch (e) {
		// keep the raw segment
	}

	return name.replace(/\.[a-z0-9]+$/i, '');
}

/**
 * PDF "Download Manual and Guide": label = downloaded file title, type = clicked CTA (user_manual, quick_guide).
 * @param {string} fileTitle
 * @param {string} downloadId technicalData download id (userManual, quickGuide)
 */
function trackDownloadDoc(fileTitle, downloadId) {
	let label = sanitizeLinkText(String(fileTitle || ''));
	let type = sanitizeLinkText(String(downloadId || '').replace(/([a-z])([A-Z])/g, '$1_$2'));

	if (!label && !type) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'download_doc',
		label: label,
		type: type
	});
}

/**
 * @param {string} label
 * @param {string} category
 */
function trackDownloadApp(label, category) {
	label = sanitizeLinkText(String(label || ''));

	if (!label) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'download_app',
		label: label,
		category: sanitizeLinkText(String(category || ''))
	});
}

function initDownloadTracking() {
	if (window.isDownloadTrackingInitialized) {
		return;
	}

	window.isDownloadTrackingInitialized = true;

	$(document).on('click', '[data-tealium-download-doc="true"]', function onDownloadDocClick() {
		let $link = $(this);

		trackDownloadDoc(resolveDownloadFileTitle($link.attr('href')), $link.attr('data-download-id') || '');
	});

	$(document).on('click', '[data-tealium-download-app="true"]', function onDownloadAppClick() {
		trackDownloadApp($(this).attr('data-tealium-label'), $(this).attr('data-tealium-category'));
	});
}

/**
 * @param {string} eventName
 */
function trackPdp3dArExperience(eventName, $cta) {
	let $detail = $cta && $cta.length ? $cta.closest('.product-detail[data-tealium-product]') : $();
	let product = gtmUtils.getProductFromTileShell($detail) || {};

	eventName = sanitizeLinkText(String(eventName || ''));

	if (!eventName) {
		return;
	}

	// PDF "3D/AR component": label = product name, category = product SKU.
	gtmUtils.sendUtagLinkEvent({
		event_name: eventName,
		label: sanitizeLinkText(product.productName || product.name || ''),
		category: sanitizeLinkText(product.id || $detail.attr('data-pid') || '')
	});
}

function initPdp3dArTracking() {
	if (window.isPdp3dArTrackingInitialized) {
		return;
	}

	window.isPdp3dArTrackingInitialized = true;

	$(document).on('click', '[data-tealium-pdp-3d-ar="true"]', function onPdp3dArClick() {
		trackPdp3dArExperience($(this).attr('data-tealium-event-name'), $(this));
	});
}

/** Bundle PDP more info tracking */

function initBundleMoreInfoTracking() {
	if (window.isBundleMoreInfoTrackingInitialized) {
		return;
	}

	window.isBundleMoreInfoTrackingInitialized = true;

	$(document).on('click', '[data-tealium-bundle-more-info="true"]', function onBundleMoreInfoClick() {
		let $btn = $(this);
		let $card = $btn.closest('[data-bundle-product-card]');
		let label = $btn.attr('data-tealium-label') || $card.find('.bundle-product-card__name').first().text().trim();
		let category = $btn.attr('data-tealium-category') || String($card.attr('data-pid') || '').trim();
		let type = $card.closest('.bundle-products').find('.bundle-products__title').first().text().trim();

		if (!label && !category) {
			return;
		}

		gtmUtils.sendUtagLinkEvent({
			event_name: 'view_more_info',
			label: sanitizeLinkText(label),
			category: sanitizeLinkText(category),
			type: sanitizeLinkText(type)
		});
	});
}

function initMagazineTracking() {
	if (window.isMagazineTrackingInitialized) {
		return;
	}

	window.isMagazineTrackingInitialized = true;

	$(document).on('click', '[data-lvz-related-article-list-load-more="true"]', function onMagazineRelatedArticleLoadMoreClick() {
		gtmUtils.sendUtagLinkEvent({
			event_name: 'load_more'
		});
	});
}

/**
 * @param {string} category
 * @param {string} label
 */
function trackFaqNavigation(category, label) {
	label = trackingEditorialAttrSerialized(label);

	if (!label) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'faq_navigation',
		category: trackingEditorialAttrSerialized(category),
		label: label
	});
}

let lastStipContactSuccessKey = '';
let lastStipContactSuccessAt = 0;

/**
 * @param {string} origin
 * @returns {boolean}
 */
function isStipFormTrackingOrigin(origin) {
	let host;

	try {
		host = new URL(String(origin || '')).hostname.toLowerCase();
	} catch (e) {
		return false;
	}

	return host === 'stip.io' || host.includes('.stip.io') || host === 'stipworld.com' || host.includes('.stipworld.com');
}

/**
 * @param {Object} detail
 * @returns {Object}
 */
function readStipFormDetailData(detail) {
	if (!detail || typeof detail !== 'object') {
		return {};
	}

	if (detail.data && typeof detail.data === 'object') {
		return detail.data;
	}

	return detail;
}

/**
 * @param {Object} data
 * @returns {string}
 */
function resolveStipContactFormType(data) {
	let formType = String((data && data.form_type) || '')
		.trim()
		.toLowerCase();

	if (formType === 'b2b' || formType === 'b2c') {
		return formType;
	}

	return String($('[data-contact-us-form]').first().attr('data-contact-us-form') || '')
		.trim()
		.toLowerCase() === 'b2b'
		? 'b2b'
		: 'b2c';
}

/**
 * @param {Object} data
 * @returns {string}
 */
function resolveStipContactReason(data) {
	if (!data) {
		return '';
	}

	let keys = ['reason_of_contact', 'reason_of_contact_1', 'reason_of_contact_2', 'reason_of_contact_3', 'reason_flat'];
	let i;
	let value;

	for (i = 0; i < keys.length; i += 1) {
		value = data[keys[i]];

		if (value !== null && value !== undefined && String(value).trim()) {
			return String(value).trim();
		}
	}

	return '';
}

/**
 * STIP cancel-subscription PD
 * @returns {boolean}
 */
function isSubscriptionUnsubscribeStipFormPage() {
	if (document.querySelector('[data-tealium-subscription-unsubscribe-form="true"]')) {
		return true;
	}

	return !!document.querySelector('iframe[src*="form-unsubscribe"]');
}

/**
 * @param {*} rawPayload
 * @returns {Object|null}
 */
function parseStipFormPostMessagePayload(rawPayload) {
	let payload = rawPayload;

	if (typeof payload === 'string') {
		payload = payload.trim();

		if (!payload || !payload.startsWith('{')) {
			return null;
		}

		try {
			payload = JSON.parse(payload);
		} catch (e) {
			return null;
		}
	}

	if (!payload || typeof payload !== 'object') {
		return null;
	}

	return payload;
}

/**
 * @param {Object} payload
 * @returns {boolean}
 */
function isStipFormSubmitSuccessPayload(payload) {
	return payload && payload.type === 'stip-form-event' && String(payload.event || '').trim() === 'stip:form:submit:success';
}

/**
 * @param {string} formId
 * @returns {boolean}
 */
function isAllowedStipFormTrackingFormId(formId) {
	let normalizedFormId = String(formId || '').trim();

	if (!normalizedFormId || normalizedFormId === 'stip_lavazza-form') {
		return true;
	}

	if (isSubscriptionUnsubscribeStipFormPage()) {
		return normalizedFormId.indexOf('unsubscribe') !== -1 || normalizedFormId.indexOf('lavazza') !== -1;
	}

	return false;
}

/**
 * @param {Object} detail
 */
function trackStipContactFormSuccess(detail) {
	let data = readStipFormDetailData(detail);
	let category = trackingEditorialAttrSerialized(resolveStipContactReason(data));
	let isUnsubscribeForm = isSubscriptionUnsubscribeStipFormPage();
	let eventName = isUnsubscribeForm
		? 'cta_unsub_subscription_form'
		: resolveStipContactFormType(data) === 'b2b'
		? 'form_customer_support-b2b'
		: 'form_customer_support';
	let signature = eventName + '|' + category;
	let now = Date.now();
	let payload;

	if (lastStipContactSuccessKey === signature && now - lastStipContactSuccessAt < 2000) {
		return;
	}

	lastStipContactSuccessKey = signature;
	lastStipContactSuccessAt = now;

	payload = {
		event_name: eventName
	};

	if (category) {
		payload.category = category;
	}

	if (!isUnsubscribeForm) {
		payload.label = 'lead_ok';
	}

	gtmUtils.sendUtagLinkEvent(payload);
}

function initContactUsStipTracking() {
	if (window.isContactUsStipTrackingInitialized) {
		return;
	}

	window.isContactUsStipTrackingInitialized = true;

	$(window).on('message', function onStipFormMessage(jqEvent) {
		let nativeEvent = jqEvent.originalEvent || jqEvent;
		let payload = parseStipFormPostMessagePayload(nativeEvent && nativeEvent.data);

		if (!isStipFormTrackingOrigin(nativeEvent && nativeEvent.origin)) {
			return;
		}

		if (!isStipFormSubmitSuccessPayload(payload)) {
			return;
		}

		if (!isAllowedStipFormTrackingFormId(payload.formId)) {
			return;
		}

		trackStipContactFormSuccess(payload.detail);
	});
}

let lastLeadFormSuccessKey = '';
let lastLeadFormSuccessAt = 0;

/**
 * @param {string} origin
 * @returns {boolean}
 */
function isSfmcLeadFormTrackingOrigin(origin) {
	let host;

	try {
		host = new URL(String(origin || '')).hostname.toLowerCase();
	} catch (e) {
		return false;
	}

	return (
		host === 'crm.lavazza.com' ||
		host.includes('.crm.lavazza.com') ||
		host === 'exacttarget.com' ||
		host.includes('.exacttarget.com') ||
		host === 'marketingcloudapps.com' ||
		host.includes('.marketingcloudapps.com') ||
		host === 'sfmc-content.com' ||
		host.includes('.sfmc-content.com')
	);
}

/**
 * @param {*} payload
 * @returns {boolean}
 */
function isLeadFormSuccessMessage(payload) {
	let parsed = payload;

	if (typeof parsed === 'string') {
		parsed = parsed.trim();

		if (!parsed.startsWith('{')) {
			return false;
		}

		try {
			parsed = JSON.parse(parsed);
		} catch (e) {
			return false;
		}
	}

	if (!parsed || typeof parsed !== 'object') {
		return false;
	}

	if (parsed.type === 'stip-form-event') {
		return false;
	}

	if (
		Object.prototype.hasOwnProperty.call(parsed, 'height') &&
		parsed.event_name == null &&
		parsed.label == null &&
		parsed.event == null
	) {
		return false;
	}

	if (
		String(parsed.event_name || '')
			.trim()
			.toLowerCase()
			.startsWith('form_component')
	) {
		return true;
	}

	return (
		String(parsed.label || '')
			.trim()
			.toLowerCase() === 'lead_ok'
	);
}

/**
 * @returns {string}
 */
function resolveLeadFormType() {
	let fromDom = String($('[data-tealium-lead-form]').first().attr('data-tealium-lead-form') || '')
		.trim()
		.toLowerCase();
	let fromIframe = '';

	if (fromDom === 'b2b' || fromDom === 'b2c') {
		return fromDom;
	}

	$('iframe').each(function readLeadFormTypeFromIframe() {
		let src;

		if (fromIframe) {
			return;
		}

		src = String(this.src || this.getAttribute('src') || '').toLowerCase();

		if (src.includes('form_b2b')) {
			fromIframe = 'b2b';
			return;
		}

		if (src.includes('form_b2c')) {
			fromIframe = 'b2c';
		}
	});

	if (fromIframe) {
		return fromIframe;
	}

	return gtmUtils.isBusinessProductPageContext() ? 'b2b' : 'b2c';
}

/**
 * @param {string} formType
 */
function trackLeadFormSuccess(formType) {
	let eventName = formType === 'b2b' ? 'form_component-b2b' : 'form_component';
	let now = Date.now();

	if (lastLeadFormSuccessKey === eventName && now - lastLeadFormSuccessAt < 2000) {
		return;
	}

	lastLeadFormSuccessKey = eventName;
	lastLeadFormSuccessAt = now;

	gtmUtils.sendUtagLinkEvent({
		event_name: eventName,
		label: 'lead_ok'
	});
}

function initLeadFormTracking() {
	if (window.isLeadFormTrackingInitialized) {
		return;
	}

	window.isLeadFormTrackingInitialized = true;

	// Only the success message posted by the SFMC form counts as "lead_ok". Iframe reloads and page
	// unloads with focus in the iframe used to fire it without any submit (landing, button clicks).
	$(window).on('message', function onSfmcLeadFormMessage(jqEvent) {
		let nativeEvent = jqEvent.originalEvent || jqEvent;

		if (!isSfmcLeadFormTrackingOrigin((nativeEvent && nativeEvent.origin) || jqEvent.origin)) {
			return;
		}

		if (!isLeadFormSuccessMessage(nativeEvent && nativeEvent.data)) {
			return;
		}

		trackLeadFormSuccess(resolveLeadFormType());
	});
}

/**
 * MyLavazza (loyalty SDK) widgets: homepage AccountInfoBanner, profile / account menu components.
 * The SDK renders its own markup inside [data-loyalty-component], so clicks are caught on the host.
 * category = widget type (e.g. account_info_banner), label = text of the clicked link / button.
 */
/**
 * Event path crossing open shadow roots (falls back to the parent chain).
 * @param {Event} e
 * @returns {Array<Node>}
 */
function getComposedEventPath(e) {
	let path = typeof e.composedPath === 'function' ? e.composedPath() : [];
	let el;

	if (!path.length) {
		for (el = e.target; el; el = el.parentNode) {
			path.push(el);
		}
	}

	return path;
}

/**
 * Innermost clickable control and the [data-loyalty-component] host containing it.
 * @param {Array<Node>} path
 * @returns {{ target: Element|null, host: Element|null }}
 */
function findLoyaltyClickTarget(path) {
	let hit = { target: null, host: null };
	let i;
	let el;

	for (i = 0; i < path.length && !hit.host; i++) {
		el = path[i];
		if (!el || el.nodeType !== 1) {
			continue;
		}
		if (!hit.target && el.matches('a, button, [role="button"]')) {
			hit.target = el;
		}
		if (el.hasAttribute('data-loyalty-component')) {
			hit.host = el;
		}
	}

	return hit;
}

function initMyLavazzaTracking() {
	if (window.isMyLavazzaTrackingInitialized) {
		return;
	}

	window.isMyLavazzaTrackingInitialized = true;

	// The loyalty SDK renders its own markup (possibly in a shadow root) and may stop click propagation:
	// listen in capture phase and walk the composed path to find the clicked control and its host.
	document.addEventListener(
		'click',
		function onMyLavazzaClick(e) {
			let hit = findLoyaltyClickTarget(getComposedEventPath(e));
			let target = hit.target;
			let host = hit.host;

			if (!host || !target || target === host) {
				return;
			}

			gtmUtils.sendUtagLinkEvent({
				event_name: 'widget',
				type: 'mylavazza',
				label: trackingEditorialAttrSerialized(target.getAttribute('aria-label') || target.textContent || ''),
				category: String(host.getAttribute('data-loyalty-component') || '')
					.replace(/([a-z])([A-Z])/g, '$1_$2')
					.toLowerCase()
			});
		},
		true
	);
}

function initFaqTracking() {
	if (window.isFaqTrackingInitialized) {
		return;
	}

	window.isFaqTrackingInitialized = true;

	$(document).on('click', 'a[data-tealium-faq-nav="by_category"]', function onFaqCategoryNavClick(e) {
		trackFaqNavigation(
			'by_category',
			$(this).attr('data-tealium-label') || $(this).find('.faq-hub__topic-name').first().text()
		);

		if (e.metaKey || e.ctrlKey || e.shiftKey || e.which === 2 || !this.href) {
			return;
		}

		e.preventDefault();
		window.location.assign(this.href);
	});

	$(document).on('submit', 'form[data-tealium-faq-nav="by_search"]', function onFaqSearchNavSubmit(e) {
		let query = String($(this).find('[name="q"]').val() || '').trim();

		if (!query) {
			return;
		}

		e.preventDefault();
		trackFaqNavigation('by_search', query);
		this.submit();
	});

	$(document).on('click', '[data-tealium-faq-item="true"]', function onFaqItemClick() {
		let $toggle = $(this);

		if ($toggle.attr('aria-expanded') === 'true') {
			return;
		}

		let $scope = $toggle.closest('[data-tealium-faq]');
		let surface = String($scope.attr('data-tealium-faq') || '').trim();
		let label = $toggle.attr('data-tealium-label') || $toggle.find('.faq-question__title').first().text();

		if (!label) {
			return;
		}

		if (surface === 'widget') {
			gtmUtils.sendUtagLinkEvent({
				event_name: 'widget',
				type: 'faq',
				label: trackingEditorialAttrSerialized(label)
			});
			return;
		}

		if (surface !== 'navigation') {
			return;
		}

		trackFaqNavigation(
			$toggle.closest('[data-tealium-category]').attr('data-tealium-category') ||
				$scope.attr('data-tealium-category') ||
				'',
			label
		);
	});
}

function initChatMokyOpenTracking() {
	if (window.isChatMokyOpenTrackingInitialized) {
		return;
	}

	window.isChatMokyOpenTrackingInitialized = true;

	let lastChatMokyOpenAt = 0;
	const CHAT_MOKY_OPEN_DEDUPE_MS = 500;

	function trackChatMokyOpen() {
		const now = Date.now();

		if (now - lastChatMokyOpenAt < CHAT_MOKY_OPEN_DEDUPE_MS) {
			return;
		}

		lastChatMokyOpenAt = now;
		gtmUtils.sendUtagLinkEvent({
			event_name: 'chat_moky_open'
		});
	}

	function wrapLaunchChatApi() {
		const bootstrap = window.embeddedservice_bootstrap;
		const utilAPI = bootstrap && bootstrap.utilAPI;

		if (!utilAPI || typeof utilAPI.launchChat !== 'function' || utilAPI.launchChat.__gtmChatMokyWrapped) {
			return;
		}

		const originalLaunchChat = utilAPI.launchChat.bind(utilAPI);

		utilAPI.launchChat = function wrappedLaunchChat() {
			trackChatMokyOpen();
			return originalLaunchChat.apply(utilAPI, arguments);
		};
		utilAPI.launchChat.__gtmChatMokyWrapped = true;
	}

	function installOpenShopperAgentHook() {
		if (window.__gtmOpenShopperAgentHookInstalled) {
			return;
		}

		window.__gtmOpenShopperAgentHookInstalled = true;

		let currentOpen = window.openShopperAgent;

		function wrapOpenFn(fn) {
			if (typeof fn !== 'function' || fn.__gtmChatMokyWrapped) {
				return fn;
			}

			const originalOpen = fn;
			const wrappedOpen = function wrappedOpenShopperAgent() {
				trackChatMokyOpen();
				return originalOpen.apply(this, arguments);
			};

			wrappedOpen.__gtmChatMokyWrapped = true;
			return wrappedOpen;
		}

		try {
			Object.defineProperty(window, 'openShopperAgent', {
				configurable: true,
				enumerable: true,
				get: function getOpenShopperAgent() {
					return currentOpen;
				},
				set: function setOpenShopperAgent(fn) {
					currentOpen = wrapOpenFn(fn);
				}
			});
		} catch (e) {
			if (typeof currentOpen === 'function') {
				window.openShopperAgent = wrapOpenFn(currentOpen);
			}
			return;
		}

		if (typeof currentOpen === 'function') {
			window.openShopperAgent = currentOpen;
		}
	}

	installOpenShopperAgentHook();

	document.addEventListener(
		'click',
		function onChatMokyEmbeddedMessagingClick(event) {
			const embeddedContainer = document.querySelector('div.embedded-messaging');

			if (!embeddedContainer || !embeddedContainer.contains(event.target)) {
				return;
			}

			trackChatMokyOpen();
		},
		true
	);

	window.addEventListener('onEmbeddedMessagingReady', wrapLaunchChatApi);
	window.addEventListener('onEmbeddedMessagingButtonClicked', trackChatMokyOpen);
	wrapLaunchChatApi();

	let bootstrapPollAttempts = 0;
	const bootstrapPollInterval = window.setInterval(function pollEmbeddedBootstrapForLaunchChatWrap() {
		wrapLaunchChatApi();
		bootstrapPollAttempts += 1;

		const launchChat =
			window.embeddedservice_bootstrap &&
			window.embeddedservice_bootstrap.utilAPI &&
			window.embeddedservice_bootstrap.utilAPI.launchChat;

		if ((launchChat && launchChat.__gtmChatMokyWrapped) || bootstrapPollAttempts >= 120) {
			window.clearInterval(bootstrapPollInterval);
		}
	}, 500);

	$(document).on('click', '.shopper-agent-button', function onChatMokyHeaderButtonClick() {
		trackChatMokyOpen();
	});

	$(document).on('click', '.shopper-agent-from-search-banner', function onChatMokySearchEntryClick() {
		trackChatMokyOpen();
	});
}

/** PLP filter / sort + notify-me tracking (AUR-364) */

/**
 * PDF $$$filter-type$$$ is the category shown to the user (accordion / switch title), not the attribute id.
 * @param {string} attr
 * @returns {string}
 */
function resolvePlpFilterTypeLabel(attr) {
	let $box = $('#filter-sort-drawer .lvz-filter__checkbox, [data-lvz-rapid-toggle]')
		.filter(function () {
			return String($(this).data('attr') || '') === attr;
		})
		.first();
	let label =
		$box.closest('.lvz-filter__group-item').find('.lvz-filter__group-name').first().text() ||
		$box.closest('.switch').find('.switch__title').first().text();

	// Some group headers render the title twice ("RecipesRecipes"): keep a single occurrence.
	label = String(label || '')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^(.+?)\s*\1$/i, '$1');

	return label || attr;
}

/**
 * Every filter applied in the drawer after the commit (PDF: all applied filters, "|"-joined).
 * @param {jQuery} $drawer
 * @returns {Array<string>}
 */
function collectAppliedFilterTokens($drawer) {
	let tokens = [];

	$drawer.find('.lvz-filter__checkbox:checked').each(function () {
		let token = serializePlpFilterToken(String($(this).data('attr') || ''), String($(this).data('value') || ''));

		if (tokens.indexOf(token) === -1) {
			tokens.push(token);
		}
	});

	return tokens;
}

function serializePlpFilterToken(attr, value) {
	return (
		sanitizeLinkText(resolvePlpFilterTypeLabel(String(attr || ''))) + ':' + sanitizeLinkText(String(value || ''))
	);
}

function getCommittedSortRule($drawer) {
	let committed = '';
	$drawer.find('input[name="plp-sort"]').each(function () {
		if ($(this).data('committed')) {
			committed = String($(this).val() || '');
		}
	});
	return committed;
}

function getCurrentSortRule($drawer) {
	let $checked = $drawer.find('input[name="plp-sort"]:checked');
	return $checked.length ? String($checked.val() || '') : '';
}

function getSortChoiceLabel($drawer, ruleId) {
	let $input = $drawer
		.find('input[name="plp-sort"]')
		.filter(function () {
			return String($(this).val() || '') === String(ruleId || '');
		})
		.first();

	if (!$input.length) {
		return sanitizeLinkText(String(ruleId || ''));
	}

	let label = $input.closest('label').find('.radio__label').text().trim();
	return sanitizeLinkText(label || String(ruleId || ''));
}

function collectDrawerFilterDiff($drawer) {
	let added = [];
	let removed = [];

	$drawer.find('.lvz-filter__checkbox').each(function () {
		let $box = $(this);
		let wasCommitted = !!$box.data('committed');
		let isChecked = $box.prop('checked');
		let filter = serializePlpFilterToken(String($box.data('attr') || ''), String($box.data('value') || ''));

		if (!wasCommitted && isChecked) {
			added.push(filter);
		}
		if (wasCommitted && !isChecked) {
			removed.push(filter);
		}
	});

	let committedSort = getCommittedSortRule($drawer);
	let currentSort = getCurrentSortRule($drawer);

	return {
		added: added,
		removed: removed,
		sortChanged: committedSort !== currentSort && currentSort !== '',
		currentSort: currentSort
	};
}

function trackPlpFilterEvents(diff, trackingHint, $drawer) {
	if (trackingHint && trackingHint.attr) {
		let hintFilter = serializePlpFilterToken(trackingHint.attr, trackingHint.value);
		if (trackingHint.action === 'remove') {
			let hintRemovePayload = {
				event_name: 'filter_remove',
				filter: hintFilter
			};
			gtmUtils.sendUtagLinkEvent(hintRemovePayload);
			return;
		}
		let appliedAfterHint = collectAppliedFilterTokens($drawer);
		let hintUsagePayload = {
			event_name: 'filter_usage',
			filter: (appliedAfterHint.length ? appliedAfterHint : [hintFilter]).join('|')
		};
		gtmUtils.sendUtagLinkEvent(hintUsagePayload);
		return;
	}

	if (!diff) {
		return;
	}

	if (diff.sortChanged) {
		let sortPayload = {
			event_name: 'sort_modify',
			filter: 'sorting:' + getSortChoiceLabel($drawer, diff.currentSort)
		};
		gtmUtils.sendUtagLinkEvent(sortPayload);
	}

	if (diff.removed.length) {
		let removePayload = {
			event_name: 'filter_remove',
			filter: diff.removed.join('|')
		};
		gtmUtils.sendUtagLinkEvent(removePayload);
	}

	if (diff.added.length) {
		// Adding a filter to the ones already applied: send all applied filters, not only the new one.
		let applied = collectAppliedFilterTokens($drawer);
		let usagePayload = {
			event_name: 'filter_usage',
			filter: (applied.length ? applied : diff.added).join('|')
		};
		gtmUtils.sendUtagLinkEvent(usagePayload);
	}
}

/**
 * @param {Object} data
 * @param {jQuery|HTMLElement} [data.drawer]
 * @param {Object} [data.trackingHint]
 */
function trackPlpFilterCommit(data) {
	let $drawer = data && data.drawer ? $(data.drawer) : $('#filter-sort-drawer');
	if (!$drawer.length) {
		return;
	}
	let diff = collectDrawerFilterDiff($drawer);
	pendingPlpImpressionContext = diff && diff.sortChanged ? 'sort' : 'filter';
	trackPlpFilterEvents(diff, data && data.trackingHint, $drawer);
}

/**
 * Called by productTileCart.js once the Product-Variation response for a PLP variant
 * click (pack size / color) has resolved. label/category must come from that response,
 * not from the tile's DOM at click time: the tile still shows the *previous* variant's
 * name and pid until this response lands.
 *
 * @param {Object} product - resolved product data from the Product-Variation response
 * @param {string} variantName
 * @param {string} variantValue
 */
function trackPlpVariantSelected(product, variantName, variantValue) {
	if (!product || !variantName || !variantValue) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: String('attribute-plp_' + variantName + '-' + variantValue).toLocaleLowerCase(),
		label: sanitizeLinkText(product.productName || ''),
		category: product.id || ''
	});
}

function resolveNotifyMeContext(trigger) {
	let $trigger = trigger ? $(trigger) : $();
	let pid = String($trigger.attr('data-notify-me-pid') || '').trim();
	let $productRoot = $trigger.closest('.product-tile-shell, .product-detail, .product-wrapper');
	let name = $productRoot.find('.product-tile__name-link, h1.product-name').first().text().trim();
	let product = gtmUtils.getProductFromTileShell($productRoot) || gtmUtils.getProductFromPdp($productRoot);
	return {
		pid: pid,
		label: sanitizeLinkText(name || pid),
		type: gtmUtils.formatNotifyMeVariantType(product)
	};
}

/**
 * @param {Object} data
 * @param {HTMLElement} [data.trigger]
 */
function trackNotifyMeOpen(data) {
	let ctx = resolveNotifyMeContext(data && data.trigger);
	let payload = {
		event_name: 'cta_notify-me',
		type: ctx.type,
		label: ctx.label,
		category: ctx.pid
	};
	gtmUtils.sendUtagLinkEvent(payload);
}

/**
 * @param {string} label
 */
function trackSubscriptionEditFlowEntry(label) {
	if (!label) {
		return;
	}

	try {
		sessionStorage.setItem('gtm_subscription_edit_entry', label);
	} catch (storageError) {
		// sessionStorage unavailable — still track click
	}

	trackSubscriptionEditSection(label);
}

/**
 * Page entry for SubMgmt-EditPlan / SubMgmt-EditAssortment (navigation may skip click handler).
 */
function trackSubscriptionEditFlowPageEntry() {
	const $root = $('[data-account-subscription-edit-root]');

	if (!$root.length || window.isSubscriptionEditFlowPageTracked) {
		return;
	}

	window.isSubscriptionEditFlowPageTracked = true;

	const mode = String($root.attr('data-mode') || '').toLowerCase();
	const step = String($root.attr('data-step') || '').toLowerCase();
	let label = mode === 'plan' || step === 'plan' ? 'plan' : '';

	if (!label && (mode === 'assortment' || step === 'coffee')) {
		label = 'assortment';
	}

	if (!label) {
		return;
	}

	try {
		if (sessionStorage.getItem('gtm_subscription_edit_entry') === label) {
			sessionStorage.removeItem('gtm_subscription_edit_entry');
			return;
		}
	} catch (storageError) {
		// ignore
	}

	trackSubscriptionEditSection(label);
}

/**
 * @param {Object} [xhr]
 * @returns {boolean}
 */
function isSubscriptionSaveErrorResponse(xhr) {
	const response = xhr && xhr.responseJSON;

	if (!response) {
		return false;
	}

	return !!(response.error || response.ok === false || response.success === false);
}

/**
 * Selected coffee blends on account assortment save (PDF label, same role as frequency 15days).
 * @param {Object} [settings]
 * @returns {string}
 */
function getAccountEditAssortmentSelectedLabel(settings) {
	let payload = {};

	try {
		if (settings && typeof settings.data === 'string') {
			payload = JSON.parse(settings.data) || {};
		} else if (settings && settings.data && typeof settings.data === 'object') {
			payload = settings.data;
		}
	} catch (e) {
		payload = {};
	}

	const products = Array.isArray(payload.products) ? payload.products : [];
	const names = products
		.filter(function (item) {
			return item && (parseInt(item.quantity, 10) || 0) > 0;
		})
		.map(function (item) {
			const name = sanitizeLinkText(item.productName || item.name || item.id || '');

			return name ? parseInt(item.quantity, 10) + '_' + name : '';
		})
		.filter(Boolean);

	if (!names.length) {
		$('[data-account-subscription-edit-root] [data-blend-tile]').each(function onAssortmentTileLabel() {
			const $tile = $(this);
			const quantity = parseInt($tile.attr('data-selected-quantity'), 10) || 0;
			let sourceJson;

			if (quantity < 1) {
				return;
			}

			try {
				sourceJson = JSON.parse(String($tile.attr('data-source-json') || '{}')) || {};
			} catch (parseError) {
				sourceJson = {};
			}

			const name = sanitizeLinkText(
				sourceJson.productName ||
					sourceJson.name ||
					$tile.find('.product-tile__name-link').first().text() ||
					$tile.attr('data-item-id') ||
					''
			);

			if (name) {
				names.push(quantity + '_' + name);
			}
		});
	}

	return names.join('|');
}

/**
 * @param {JQuery} [$root]
 * @returns {string}
 */
function resolveSubscriptionEditSelectedTier($root) {
	$root = $root && $root.length ? $root : $('[data-account-subscription-edit-root]').first();

	if (String($root.attr('data-tealium-model-type') || '') !== 'tier_model') {
		return '';
	}

	const planId = String($root.attr('data-selected-plan-id') || '').trim();

	if (planId) {
		const $plan = $root.find('[data-plan-select="' + planId + '"]').first();
		// TierName__c first (same rule as the journey getTierSlug): the editorial title may hold placeholder copy.
		const planTitle =
			$plan.attr('data-plan-tier-name') || $plan.attr('data-plan-title') || $plan.attr('data-tealium-plan-slug') || '';

		if (planTitle) {
			return slugifyTier(planTitle);
		}
	}

	return slugifyTier(String($root.attr('data-tealium-tier') || ''));
}

/**
 * @param {JQuery} [$root]
 * @returns {Object}
 */
function buildSubscriptionEditTierSelectedPayload($root) {
	$root = $root && $root.length ? $root : $('[data-account-subscription-edit-root]').first();
	const payload = {
		event_name: 'subscription_edit_tier_selected',
		step: 'customize_phase'
	};
	const category = sanitizeLinkText(String($root.attr('data-tealium-sub-type') || ''));
	const type = sanitizeLinkText(String($root.attr('data-tealium-model-type') || ''));
	const selectedTier = resolveSubscriptionEditSelectedTier($root);

	if (category) {
		payload.category = category;
	}

	if (type) {
		payload.type = type;
	}

	payload.selected_tier = selectedTier || 'none';

	return payload;
}

/**
 * @param {Object} [settings]
 * @returns {Object}
 */
function buildSubscriptionEditAssortmentSelectedPayload(settings) {
	const $root = $('[data-account-subscription-edit-root]').first();
	const payload = {
		event_name: 'subscription_edit_assortment_selected',
		step: 'assortment_phase',
		label: getAccountEditAssortmentSelectedLabel(settings)
	};
	const category = sanitizeLinkText(String($root.attr('data-tealium-sub-type') || ''));
	const type = sanitizeLinkText(String($root.attr('data-tealium-model-type') || ''));
	const selectedTier = resolveSubscriptionEditSelectedTier($root);

	if (category) {
		payload.category = category;
	}

	if (type) {
		payload.type = type;
	}

	payload.selected_tier = selectedTier || 'none';

	return payload;
}

/**
 * City/state/country only — no street, name or phone (PII).
 * @param {Object} [settings]
 * @returns {string}
 */
function getAccountEditShippingSelectedLabel(settings) {
	const data = settings && settings.data;
	const city = sanitizeLinkText(gtmUtils.getSerializedFormValue(data, 'city'));
	const state = sanitizeLinkText(gtmUtils.getSerializedFormValue(data, 'state'));
	const country = sanitizeLinkText(gtmUtils.getSerializedFormValue(data, 'country'));

	if (city || state || country) {
		return city || state || country;
	}

	return sanitizeLinkText(
		$('#subscription-shipping-address-city').val() ||
			$('#subscription-shipping-address-state').val() ||
			$('#subscription-shipping-address-country').val() ||
			''
	);
}

/**
 * True only when the payment method was actually saved (not tokenization / 3DS pending).
 * @param {Object} [xhr]
 * @param {Object} [settings]
 * @returns {boolean}
 */
function isSubscriptionPaymentMethodSaveSuccess(xhr, settings, response) {
	const url = settings && settings.url ? String(settings.url) : '';
	const body = response || (xhr && xhr.responseJSON);

	if (!$('#subscription-payment-form').length) {
		return false;
	}

	if (isSubscriptionSaveErrorResponse({ responseJSON: body })) {
		return false;
	}

	if (url.indexOf('SubMgmt-UpdatePaymentMethod') !== -1) {
		return true;
	}

	if (url.indexOf('SubMgmt-CompleteChangePaymentMethod') !== -1) {
		return !!(body && body.ok && !body.action);
	}

	if (url.indexOf('SubMgmt-ChangePaymentMethod') !== -1) {
		return !!(body && body.ok && body.subId && !body.redirectAction && !body.action);
	}

	return false;
}

/**
 * Payment type only — no PAN / token (PII).
 * @param {Object} [settings]
 * @param {Object} [response]
 * @returns {string}
 */
function getAccountEditPaymentSelectedLabel(settings, response) {
	const requestPayload = gtmUtils.parseAjaxJsonPayload(settings && settings.data);
	let type = sanitizeLinkText((response && response.paymentMethodType) || '');

	if (!type && requestPayload) {
		type = sanitizeLinkText(
			(requestPayload.paymentMethod && requestPayload.paymentMethod.paymentMethodType) ||
				requestPayload.paymentMethodType ||
				''
		);
	}

	if (!type) {
		type = sanitizeLinkText(
			gtmUtils.getSerializedFormValue(settings && settings.data, 'subscriptionPaymentMethod') ||
				$('#subscription-cardType').val() ||
				$('#subscription-payment-form [name="subscriptionPaymentMethod"]:checked').val() ||
				''
		);
	}

	if (type === 'scheme') {
		type = sanitizeLinkText($('#subscription-cardType').val() || '') || 'card';
	}

	if (type === 'paywithgoogle') {
		type = 'googlepay';
	}

	if (type === 'mc') {
		type = 'mastercard';
	}

	return type || 'card';
}

/**
 * Fire before the feature module reloads the page on success.
 * @param {Object} options
 * @param {Object} jqXHR
 */
function bindSubscriptionPaymentSaveTracking(options, _originalOptions, jqXHR) {
	const url = String((options && options.url) || '');

	if (
		url.indexOf('SubMgmt-UpdatePaymentMethod') === -1 &&
		url.indexOf('SubMgmt-CompleteChangePaymentMethod') === -1 &&
		url.indexOf('SubMgmt-ChangePaymentMethod') === -1
	) {
		return;
	}

	jqXHR.done(function onSubscriptionPaymentSaveDone(response, _status, xhr) {
		const settings = {
			url: url,
			data: options.data
		};

		if (!isSubscriptionPaymentMethodSaveSuccess(xhr, settings, response)) {
			return;
		}

		gtmUtils.sendUtagLinkEvent({
			event_name: 'subscription_edit_payment_method_selected',
			label: getAccountEditPaymentSelectedLabel(settings, response)
		});
	});
}

/**
 * SubMgmt-SaveAssortment / SavePlan — fire only on successful save, not on click.
 */
function initSubscriptionEditSaveTracking() {
	if (window.isSubscriptionEditSaveTrackingInitialized) {
		return;
	}

	window.isSubscriptionEditSaveTrackingInitialized = true;

	$(document).on('click', '[data-account-edit-continue]', function onSubscriptionPlanContinueClick() {
		const $root = $('[data-account-subscription-edit-root]').first();
		const step = String($root.attr('data-step') || '').toLowerCase();

		if (step !== 'plan') {
			return;
		}

		gtmUtils.sendUtagLinkEvent(buildSubscriptionEditTierSelectedPayload($root));
	});

	$(document).on('ajaxSuccess', function onSubscriptionEditSaveAjaxSuccess(_event, xhr, settings) {
		const url = settings && settings.url ? String(settings.url) : '';
		const isAssortmentSave = url.indexOf('SubMgmt-SaveAssortment') !== -1;
		const isPlanSave = url.indexOf('SubMgmt-SavePlan') !== -1;

		if (!isAssortmentSave && !isPlanSave) {
			return;
		}

		if (!$('[data-account-subscription-edit-root]').length) {
			return;
		}

		if (isSubscriptionSaveErrorResponse(xhr)) {
			return;
		}

		if (isPlanSave) {
			gtmUtils.sendUtagLinkEvent(buildSubscriptionEditTierSelectedPayload());
			return;
		}

		if (!isAccountSubscriptionEditAssortmentPage()) {
			return;
		}

		const payload = buildSubscriptionEditAssortmentSelectedPayload(settings);

		if (!payload.label) {
			return;
		}

		gtmUtils.sendUtagLinkEvent(payload);
	});
}

function initSubscriptionManagementTracking() {
	if (window.isSubscriptionManagementTrackingInitialized) {
		return;
	}

	window.isSubscriptionManagementTrackingInitialized = true;

	$(document).on('click', '.subscription-summary-card__cta', function onSubscriptionManageClick() {
		if ($(this).closest('.recurring-summary-card').length) {
			return;
		}

		trackSubscriptionManagementEvent('cta_manage_subscription');
	});

	$(document).on('click', '[data-subscription-frequency-drawer-open]', function onSubscriptionEditFrequencyOpen() {
		if ($(this).attr('data-tealium-subscription-edit')) {
			return;
		}

		trackSubscriptionEditSection('frequency');
	});

	$(document).on('ajaxSuccess', function onSubscriptionShippingSaveAjaxSuccess(_event, xhr, settings) {
		if (!settings || !settings.url || settings.url.indexOf('SubMgmt-UpdateContact') === -1) {
			return;
		}

		if (isSubscriptionSaveErrorResponse(xhr)) {
			return;
		}

		const label = getAccountEditShippingSelectedLabel(settings);

		if (!label) {
			return;
		}

		gtmUtils.sendUtagLinkEvent({
			event_name: 'subscription_edit_shipping_selected',
			label: label
		});
	});

	if (!window.isSubscriptionPaymentSaveTrackingInitialized) {
		window.isSubscriptionPaymentSaveTrackingInitialized = true;
		$.ajaxPrefilter(bindSubscriptionPaymentSaveTracking);
	}

	$(document).on(
		'click',
		'.subscription-detail-page [data-drawer-open], .lvz-myaccount-container--subscriptions-subpage [data-drawer-open]',
		function onSubscriptionManagementEditDrawerOpen() {
			const $trigger = $(this);

			if ($trigger.attr('data-tealium-subscription-edit')) {
				return;
			}

			const drawerId = String($trigger.attr('data-drawer-open') || '');
			const section = SUBSCRIPTION_MANAGEMENT_EDIT_DRAWERS[drawerId];

			if (!section) {
				return;
			}

			trackSubscriptionEditSection(section);
		}
	);

	$(document).on('click', '[data-tealium-subscription-edit]', function onSubscriptionEditFlowAttrClick() {
		trackSubscriptionEditFlowEntry(String($(this).attr('data-tealium-subscription-edit') || '').trim());
	});

	$(document).on('click', '[data-drawer-open="subscription-next-order-drawer"]', function onSubscriptionNextOrderDrawerOpen() {
		trackSubscriptionEditSection('next_order');
	});

	$(document).on('ajaxSuccess', function onSubscriptionUnsubscribeAjaxSuccess(_event, xhr, settings) {
		if (!settings || !settings.url) {
			return;
		}

		if (!isSubscriptionCancellationAjaxSuccess(xhr, settings)) {
			return;
		}

		trackSubscriptionCancellationAjaxSuccess(xhr, settings);
	});

	$(document).on(
		'click',
		'[data-tealium-subscription-unsubscribe="via_form"]',
		function onSubscriptionUnsubscribeViaFormClick() {
			trackSubscriptionManagementEvent('cta_manage_subscription_unsubscribe');
		}
	);

	$(document).on('subscription:cancellation-opened', function onSubscriptionCancellationModalOpened() {
		trackSubscriptionCancellationOpened();
	});

	$(document).on(
		'click',
		'[data-tealium-subscription-unsubscribe="via_customer_service"] a',
		function onSubscriptionUnsubscribeViaCustomerServiceClick() {
			trackSubscriptionManagementEvent('cta_unsub_subscription_contact_customer_service');
		}
	);

	trackSubscriptionEditFlowPageEntry();
}

/** Recurring purchase tracking

/**
 * event_name "cta_manage_recurring_purchase_edit_$$$section$$$"
 * @param {string} section
 */
function trackRecurringPurchaseEditSection(section) {
	const slug = sanitizeLinkText(section);

	if (!slug) {
		return;
	}

	gtmUtils.sendUtagLinkEvent({
		event_name: 'cta_manage_recurring_purchase_edit_' + slug
	});
}

/**
 * @param {string} url
 * @returns {string}
 */
function getRecurringManageRefillAction(url) {
	const match = String(url || '').match(/[?&]action(?:=|%3[Dd])([^&]+)/i);

	return match ? decodeURIComponent(match[1]).toLowerCase() : '';
}

/**
 * @param {Object} xhr
 * @returns {boolean}
 */
function isRecurringSaveSuccessResponse(xhr) {
	const response = xhr && xhr.responseJSON;

	if (!response || response.success !== true) {
		return false;
	}

	return !isSubscriptionSaveErrorResponse(xhr);
}

function initRecurringPurchaseTracking() {
	if (window.isRecurringPurchaseTrackingInitialized) {
		return;
	}

	window.isRecurringPurchaseTrackingInitialized = true;

	$(document).on('click', '[data-tealium-recurring-edit]', function onRecurringPurchaseEditClick() {
		trackRecurringPurchaseEditSection(String($(this).attr('data-tealium-recurring-edit') || '').trim());
	});

	$(document).on('ajaxSuccess', function onRecurringPurchaseAjaxSuccess(_event, xhr, settings) {
		if (!settings || !settings.url || settings.url.indexOf('ManageRefillList') === -1) {
			return;
		}

		if (!isRecurringSaveSuccessResponse(xhr)) {
			return;
		}

		const action = getRecurringManageRefillAction(settings.url);

		if (action === 'updaterefill') {
			const $selected = $(
				'.js-recurring-frequency-drawer:not([aria-hidden="true"]) [data-recurring-frequency-option].is-active'
			).first();
			const label = formatEcommerceSubscriptionFrequency({
				frequency: $selected.attr('data-interval'),
				frequencyUOM: $selected.attr('data-periodicity')
			});

			if (!label) {
				return;
			}

			gtmUtils.sendUtagLinkEvent({
				event_name: 'recurring_purchase_edit_frequency_selected',
				label: label
			});
			return;
		}

		if (action === 'updatequantity') {
			const quantities = [];

			$('.js-recurring-product-drawer:not([aria-hidden="true"]) [data-recurring-quantity-input]').each(function () {
				const quantity = parseInt($(this).val(), 10);
				if (quantity > 0) {
					quantities.push(String(quantity));
				}
			});

			if (!quantities.length) {
				return;
			}

			gtmUtils.sendUtagLinkEvent({
				event_name: 'recurring_purchase_edit_quantity_selected',
				label: quantities.join(',')
			});
			return;
		}

		if (action === 'canceled') {
			gtmUtils.sendUtagLinkEvent({
				event_name: 'cta_manage_recurring_purchase_cancel'
			});
		}
	});
}

/**
 * @param {Object} data
 * @param {HTMLElement} [data.trigger]
 * @param {string} [data.pid]
 */
function trackNotifyMeSubmit(data) {
	let ctx = resolveNotifyMeContext(data && data.trigger);
	if (data && data.pid) {
		ctx.pid = String(data.pid);
	}
	if (!ctx.label) {
		ctx.label = sanitizeLinkText(ctx.pid);
	}
	let payload = {
		event_name: 'cta_notify-me-submit',
		type: ctx.type,
		label: ctx.label,
		category: ctx.pid
	};
	gtmUtils.sendUtagLinkEvent(payload);
}

function initPlpFilterTracking() {
	if (window.isPlpFilterTrackingInitialized) {
		return;
	}
	window.isPlpFilterTrackingInitialized = true;

	$(document).on('click', '[data-lvz-remove-filters]', function (e) {
		let anchorEl = this;
		let openInNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;

		e.preventDefault();

		let removed = [];
		$('#filter-sort-drawer .lvz-filter__checkbox:checked').each(function () {
			removed.push(serializePlpFilterToken(String($(this).data('attr') || ''), String($(this).data('value') || '')));
		});
		let payload = { event_name: 'filter_remove' };
		if (removed.length) {
			payload.filter = removed.join('|');
		}
		gtmUtils.sendUtagLinkEvent(payload);

		if (openInNewTab) {
			window.open(anchorEl.href, '_blank', 'noopener,noreferrer');
			return;
		}
		if (anchorEl.href) {
			window.location.assign(anchorEl.href);
		}
	});

	$(document).on('click', '[data-lvz-filter-clear]', function () {
		let clearPayload = {
			event_name: 'filter_clear'
		};
		gtmUtils.sendUtagLinkEvent(clearPayload);
	});
}

/**
 * Cart page: view details drawer + edit subscription icon.
 */
function initCartSubscriptionTracking() {
	if (window.isCartSubscriptionTrackingInitialized || isCartSubscriptionTrackingInitialized) {
		return;
	}

	window.isCartSubscriptionTrackingInitialized = true;
	isCartSubscriptionTrackingInitialized = true;

	$(document).on('click', '.cart-subscription-card__details-button', function onCartSubscriptionDetailsClick() {
		trackCartSubscriptionAction($(this).closest('.cart-line-subscription-item').get(0), 'cta_checkout_subscription_detail');
	});

	$(document).on('click', '.cart-subscription-card__icon-button', function onCartSubscriptionEditClick() {
		trackCartSubscriptionAction($(this).closest('.cart-line-subscription-item').get(0), 'cta_checkout_subscription_edit');
	});
}

/** Check order status tracking (AUR-363) */

let CHECK_ORDER_STATUS_EVENT = 'check_order_status';
let CHECK_ORDER_NUMBER_PATTERN = /^[A-Za-z0-9-]+$/;
let CHECK_ORDER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let isCheckOrderStatusTrackingInitialized = false;

/**
 * @param {jQuery} $form
 * @returns {boolean}
 */
function isCheckOrderStatusFormValid($form) {
	let orderNumber = String($form.find('[name="trackOrderNumber"]').val() || '').trim();
	let orderEmail = String($form.find('[name="trackOrderEmail"]').val() || '').trim();

	return CHECK_ORDER_NUMBER_PATTERN.test(orderNumber) && CHECK_ORDER_EMAIL_PATTERN.test(orderEmail);
}

function initCheckOrderStatusTracking() {
	if (window.isCheckOrderStatusTrackingInitialized || isCheckOrderStatusTrackingInitialized) {
		return;
	}

	window.isCheckOrderStatusTrackingInitialized = true;
	isCheckOrderStatusTrackingInitialized = true;

	$(document).on('submit', '.lvz-order-status__form', function onCheckOrderStatusSubmit() {
		let $form = $(this);

		if (!isCheckOrderStatusFormValid($form)) {
			return;
		}

		gtmUtils.sendUtagLinkEvent({
			event_name: CHECK_ORDER_STATUS_EVENT
		});
	});
}

/** Machine registration tracking (AUR-3086) */

let MACHINE_REGISTRATION_EVENT = 'registrazione_macchina_thank-you';
let REGISTER_NEW_MACHINE_EVENT = 'cta_register-new-machine';
let isMachineRegistrationTrackingInitialized = false;
let lastMachineRegistrationSuccessKey = '';
let lastMachineRegistrationSuccessAt = 0;

/**
 * @param {Object} xhr
 * @param {Object} settings
 * @returns {boolean}
 */
function isMachineRegistrationSubmitAjaxSuccess(xhr, settings) {
	let url = settings && settings.url ? String(settings.url) : '';

	if (url.indexOf('MachineRegistration-Submit') === -1) {
		return false;
	}

	if (!xhr || xhr.status < 200 || xhr.status >= 300) {
		return false;
	}

	let parsed = gtmUtils.parseAjaxJsonPayload(xhr.responseJSON || xhr.responseText);

	return !!(parsed && parsed.success === true);
}

/**
 * @param {Object} xhr
 */
function trackMachineRegistrationSuccess(xhr) {
	let parsed = gtmUtils.parseAjaxJsonPayload(xhr && (xhr.responseJSON || xhr.responseText));
	let machine = parsed && parsed.machine ? parsed.machine : {};
	let registration = parsed && parsed.registration ? parsed.registration : {};
	let machineCode = sanitizeLinkText(String(machine.sku || registration.productCode || ''));
	// PDF registered_machine_name: machine model (a_modo_mio, lavazza_expert, tabli), else the product name.
	let machineName = slugifyEventPart(String(machine.model || machine.name || ''));
	let dedupeKey = MACHINE_REGISTRATION_EVENT + ':' + machineCode + ':' + machineName;
	let now = Date.now();

	if (dedupeKey === lastMachineRegistrationSuccessKey && now - lastMachineRegistrationSuccessAt < 2000) {
		return;
	}

	lastMachineRegistrationSuccessKey = dedupeKey;
	lastMachineRegistrationSuccessAt = now;

	gtmUtils.sendUtagLinkEvent({
		event_name: MACHINE_REGISTRATION_EVENT,
		registered_machine_code: machineCode,
		registered_machine_name: machineName
	});
}

function initMachineRegistrationTracking() {
	if (window.isMachineRegistrationTrackingInitialized || isMachineRegistrationTrackingInitialized) {
		return;
	}

	window.isMachineRegistrationTrackingInitialized = true;
	isMachineRegistrationTrackingInitialized = true;

	$(document).on('click', '[data-tealium-cta="cta_register-new-machine"]', function onRegisterNewMachineClick() {
		gtmUtils.sendUtagLinkEvent({
			event_name: REGISTER_NEW_MACHINE_EVENT
		});
	});

	$(document).ajaxSuccess(function onMachineRegistrationSubmitAjaxSuccess(_event, xhr, settings) {
		if (!isMachineRegistrationSubmitAjaxSuccess(xhr, settings)) {
			return;
		}

		trackMachineRegistrationSuccess(xhr);
	});
}

/**
 * Account edit assortment landing (SubMgmt-EditAssortment or EditPlan?step=coffee).
 * @returns {boolean}
 */
function isAccountSubscriptionEditAssortmentPage() {
	const root = document.querySelector('[data-account-subscription-edit-root]');

	if (!root) {
		return false;
	}

	const step = String(root.getAttribute('data-step') || '').toLowerCase();
	const mode = String(root.getAttribute('data-mode') || '').toLowerCase();

	return step === 'coffee' || mode === 'assortment';
}

/**
 * PDF Subscription - edit step 3 (assortment): unique utag.view on landing.
 * @returns {boolean} true when this named pageview was sent
 */
function trackAccountSubscriptionEditAssortmentViewIfApplicable() {
	if (!isAccountSubscriptionEditAssortmentPage()) {
		return false;
	}

	const root = document.querySelector('[data-account-subscription-edit-root]');
	const subType = String(root.getAttribute('data-tealium-sub-type') || '').trim();
	const category = subType;
	const modelType = String(root.getAttribute('data-tealium-model-type') || '').trim() || 'untier_model';
	// Untier plans have no tier (PDF: "none if not available"); the server value may be placeholder copy.
	const tier = modelType === 'tier_model' ? slugifyTier(root.getAttribute('data-tealium-tier') || '') || 'none' : 'none';

	if (!subType) {
		return false;
	}

	gtmUtils.sendUtagViewEvent({
		event_name: 'subscription_edit_' + subType + '_step-3',
		category: category,
		type: modelType,
		step: 'assortment_phase',
		tier: tier,
		package_type: 'customized'
	});

	return true;
}

/**
 * One utag.view per landing: hub, journey step, or generic pageview elsewhere.
 */
function trackPageViewOnLoad() {
	if (isSubscriptionHubPage()) {
		gtmUtils.sendUtagViewEvent({
			event_name: SUBSCRIPTION_HUB_EVENT
		});
		return;
	}

	if (isSubscriptionJourneyPage()) {
		return;
	}

	if (trackAccountSubscriptionEditAssortmentViewIfApplicable()) {
		return;
	}

	// PDF "Product impression": the unique landing utag.view carries the editorial carousels
	// (product_impression_type = carousel title of each product, aligned with the products module).
	let impression = collectEditorialCarouselImpressions();

	if (!impression.products.length) {
		gtmUtils.sendUtagViewEvent();
		return;
	}

	gtmUtils.sendUtagViewEvent({
		event_name: 'product_impression',
		product_impression_type: impression.types,
		products: impression.products
	});
}

let ANALYTICS_PAGE_VIEW_DELAY_MS = 500;
let isAnalyticsPageViewScheduled = false;

/**
 * Runs once per page: TYP, cart, PDP product_view, hub, or generic utag.view.
 */
function runAnalyticsPageViewPipeline() {
	trackRegistrationCompletePageViewIfApplicable();
	trackSearchResultsPageViewIfApplicable();

	if (trackTypPageViewIfApplicable()) {
		return;
	}

	if (trackCheckoutLoginPageViewIfApplicable()) {
		return;
	}

	if (isCheckoutPage()) {
		return;
	}

	if (trackCartPageViewIfApplicable()) {
		trackEditorialCarouselImpressions();
		return;
	}

	if (trackProductViewOnPdpIfApplicable()) {
		trackEditorialCarouselImpressions();
		return;
	}

	if (trackPlpProductImpressionViewIfApplicable()) {
		trackEditorialCarouselImpressions();
		return;
	}

	trackPageViewOnLoad();
	trackEditorialCarouselImpressions();
}

function trackVideoStart(detail) {
	sendVideoEvent('video_start', detail, false);
}

function trackVideoPlay(detail) {
	sendVideoEvent('video_play', detail, true);
}

function trackVideoPause(detail) {
	sendVideoEvent('video_pause', detail, true);
}

function trackVideoEnded(detail) {
	sendVideoEvent('video_complete', detail, false);
}

function trackPdpGalleryView(detail) {
	if (!detail) {
		return;
	}

	let galleryPayload = {
		event_name: 'product_image_gallery',
		label: gtmUtils.sanitizeLinkText(detail.label),
		category: sanitizeLinkText(detail.category),
		position: String(detail.position || '')
	};

	gtmUtils.sendUtagLinkEvent(galleryPayload);
}

/**
 * window.load may fire before gtm init (defer scripts). Schedule immediately when the page is already loaded.
 */
function scheduleAnalyticsPageView() {
	if (isAnalyticsPageViewScheduled) {
		return;
	}

	isAnalyticsPageViewScheduled = true;

	function queuePipeline() {
		window.setTimeout(runAnalyticsPageViewPipeline, ANALYTICS_PAGE_VIEW_DELAY_MS);
	}

	$(window).off('load.lvzAnalyticsPageView');

	if (document.readyState === 'complete') {
		queuePipeline();
		return;
	}

	$(window).on('load.lvzAnalyticsPageView', queuePipeline);
}

/** Global UI tracking */

$(() => {
	function navigateAfterTrackedLink(mouseEvent, anchorEl) {
		if (!anchorEl || !anchorEl.href) {
			return;
		}
		const url = anchorEl.href;
		const openInNewTab = mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.button === 1;
		const targetAttr = (anchorEl.getAttribute('target') || '').trim().toLowerCase();

		if (openInNewTab || targetAttr === '_blank') {
			window.open(url, '_blank', 'noopener,noreferrer');
			return;
		}
		if (targetAttr === '_parent' || targetAttr === '_top') {
			window.open(url, targetAttr);
			return;
		}
		window.location.assign(url);
	}

	/** Footer menu (Page Designer): link click or plain label click — one beacon per intentional interaction. */
	$(document).on('click', '#footercontent .footer-menu-item[data-footer-analytics]', function onFooterMenuItemAnalytics(e) {
		const $item = $(this);
		const hasLink = $item.attr('data-footer-has-link') === 'true';
		const $clickedLink = $(e.target).closest('a[href]');

		if (hasLink) {
			if (!$clickedLink.length || !$.contains($item[0], $clickedLink[0])) {
				return;
			}
		}

		e.preventDefault();

		const labelAttr = $item.attr('data-footer-item-label') || '';

		gtmUtils.sendUtagLinkEvent({
			event_name: 'footer',
			category: sanitizeLinkText(labelAttr)
		});

		if (hasLink && $clickedLink.length) {
			navigateAfterTrackedLink(e, $clickedLink[0]);
		}
	});

	/** Services component (Page Designer): link click or plain label click — one beacon per intentional interaction. */
	$(document).on('click', '.lvz-service .lvz-service-item[data-service-analytics]', function onServiceItemAnalytics(e) {
		const $item = $(this);
		const hasLink = $item.attr('data-service-has-link') === 'true';
		const $clickedLink = $(e.target).closest('a[href]');

		e.preventDefault();

		const labelAttr = $item.attr('data-service-item-label') || '';

		gtmUtils.sendUtagLinkEvent({
			event_name: 'pre_footer',
			category: sanitizeLinkText(labelAttr)
		});

		if (hasLink && $clickedLink.length) {
			navigateAfterTrackedLink(e, $clickedLink[0]);
		}
	});

	/** Tracking Header */
	$('.header-nav-tab-item[data-category]').on('click', function onHeaderNavTabItemClick(e) {
		const $item = $(this);
		const category = $item.data('category');

		gtmUtils.sendUtagLinkEvent({
			event_name: 'header',
			category: sanitizeLinkText(category)
		});
	});

	$(document).on('click', '.tealium-header-item', function onHeaderItemClick(e) {
		const $item = $(this);
		if ($item.closest('#footercontent').length) {
			return;
		}
		const $labelEl = $item.find('[data-text]').first();
		const labelAttr = ($labelEl.length ? $labelEl.attr('data-text') : $item.attr('data-text')) || '';
		const $link = $item.is('a[href]') ? $item : $(e.target).closest('a[href]');
		const linkEl = ($link.length && $.contains($item[0], $link[0])) || $item.is($link) ? $link[0] : null;
		const href = linkEl ? String(linkEl.getAttribute('href') || '').trim() : '';

		gtmUtils.sendUtagLinkEvent({
			event_name: 'header',
			category: sanitizeLinkText(labelAttr)
		});

		if (linkEl && href && href !== '#') {
			e.preventDefault();
			navigateAfterTrackedLink(e, linkEl);
		}
	});

	/** Main nav — catalog (`IncludeHeaderDinamicMenu`) + Page Designer L1 bar (`menu_level_1_bar` → menuL1 / menuL2 / menuL3). */
	function getNavbarDynamicMenuRoot($from) {
		// Wrapper from IncludeHeaderDinamicMenu; ignores Page Designer L1 region.
		return $from.closest('[data-lvz-header-menu="true"]');
	}

	function getNavbarMegamenuLevel1PositionFromLi($li) {
		// Yellow row label — `.dd--label` under `.dd__toggle` (catalog + editorial).
		if (!$li || !$li.length) {
			return '';
		}
		const $label = $li.find('.dd__toggle .dd--label').first();
		const fromData = ($label.attr('data-text') || '').trim();
		return fromData || ($label.text() || '').replace(/\s+/g, ' ').trim();
	}

	function getNavbarMegamenuLevel1PositionFromContext($ctx) {
		return getNavbarMegamenuLevel1PositionFromLi($ctx.closest('li.navbar-item-level-1'));
	}

	function buildNavbarMenuPayload(step, positionRaw, categoryRaw, labelRaw, $clickTarget) {
		const position = trackingEditorialAttrSerialized(String(positionRaw || '').toLowerCase());
		const category = trackingEditorialAttrSerialized(String(categoryRaw || '').toLowerCase());
		const label = trackingEditorialAttrSerialized(String(labelRaw || '').toLowerCase());
		// Megamenu PD small cards (`.lavazza-card`) / Bootstrap `.card`; plain links are `text`.
		const type =
			$clickTarget && ($clickTarget.closest('.lavazza-card').length || $clickTarget.closest('.card').length)
				? 'card'
				: 'text';
		const action = [position, category, label].filter(item => item).join('|');
		return {
			event_name: 'menu',
			position: position,
			category: category,
			label: label,
			type: type,
			step: step,
			action: action
		};
	}

	/**
	 * Editorial menuL2: strip `.menu--description` so category / L2 reflects title only (PD megamenu).
	 */
	function getNavbarMegamenuPlainTextWithoutMenuDescription($root, stripDescriptions) {
		if (!$root || !$root.length) {
			return '';
		}
		if (!stripDescriptions) {
			return ($root.text() || '').replace(/\s+/g, ' ').trim();
		}
		const $clone = $root.clone();
		$clone.find('.menu--description').remove();
		return ($clone.text() || '').replace(/\s+/g, ' ').trim();
	}

	/** menuL2 `category`: `.leading-7` title only; never `.menu--description` (independent of `stripDesc`). */
	function getNavbarMegamenuLevel2CategoryTitle($root) {
		if (!$root || !$root.length) {
			return '';
		}
		const $title = $root.find('.leading-7').first();
		if ($title.length) {
			return ($title.text() || '').replace(/\s+/g, ' ').trim();
		}
		return getNavbarMegamenuPlainTextWithoutMenuDescription($root, true);
	}

	/**
	 * Link text for menu tracking: `.lavazza-card` uses title only (not subtitle / body copy).
	 * Other anchors use full text, or strip `.menu--description` when `stripMenuDescription` is true.
	 */
	function getNavbarMegamenuLinkTextForTracking($link, stripMenuDescription) {
		if (!$link || !$link.length) {
			return '';
		}
		if ($link.hasClass('lavazza-card')) {
			const fromAttr = ($link.attr('data-tealium-label') || '').trim();
			if (fromAttr) {
				return fromAttr;
			}
			return ($link.find('.lavazza-card-body-content-title').first().text() || '').replace(/\s+/g, ' ').trim();
		}
		if (stripMenuDescription) {
			return getNavbarMegamenuPlainTextWithoutMenuDescription($link, true);
		}
		return ($link.text() || '').replace(/\s+/g, ' ').trim();
	}

	/**
	 * Shared L2/L3 rules for accordion megamenus (catalog column or `.editor-menu`).
	 * @param {jQuery} $target event.target
	 * @param {jQuery} $scope Column root (must contain the click target when scoped).
	 * @param {string} positionRaw L1 label for this branch.
	 * @param {{ skipMenuitemL1Links?: boolean, stripAccordionDescriptions?: boolean }} opts
	 */
	function getNavbarMegamenuAccordionPayload($target, $scope, positionRaw, opts) {
		const options = $.extend(
			{
				skipMenuitemL1Links: false,
				stripAccordionDescriptions: false
			},
			opts || {}
		);
		const stripDesc = options.stripAccordionDescriptions;

		const $link = $target.closest('a[href]');
		if ($link.length && $scope[0].contains($link[0])) {
			if (options.skipMenuitemL1Links && $link.hasClass('menuitem-level1')) {
				return null;
			}
			if ($link.closest('.accordion__panel').length) {
				const labelRaw = getNavbarMegamenuLinkTextForTracking($link, stripDesc);
				const $accTitle = $link.closest('.accordion__item').find('.accordion__toggle .accordion__title').first();
				const categoryRaw = getNavbarMegamenuLevel2CategoryTitle($accTitle);
				return buildNavbarMenuPayload('third_level', positionRaw, categoryRaw, labelRaw, $target);
			}

			if ($link.closest('.accordion__title').length) {
				const categoryRaw = getNavbarMegamenuLevel2CategoryTitle($link);
				return buildNavbarMenuPayload('second_level', positionRaw, categoryRaw, '', $target);
			}

			const categoryRaw = getNavbarMegamenuLevel2CategoryTitle($link);
			return buildNavbarMenuPayload('second_level', positionRaw, categoryRaw, '', $target);
		}

		const $toggle = $target.closest('button.accordion__toggle');
		if ($toggle.length && $scope[0].contains($toggle[0])) {
			const $item = $toggle.closest('.accordion__item');
			const isCollapseClick = $item.children('.accordion__panel').length > 0 && !$item.hasClass('is-open');
			if (isCollapseClick) {
				return null;
			}
			const $accTitle = $toggle.find('.accordion__title').first();
			const categoryRaw = getNavbarMegamenuLevel2CategoryTitle($accTitle);
			return buildNavbarMenuPayload('second_level', positionRaw, categoryRaw, '', $target);
		}

		return null;
	}

	/** Editorial megamenu: `smallCardItem` (`a.lavazza-card--sm`) → step `fourth_level`, label = card title. */
	function getNavbarEditorialMenuSmallCardPayload($target, $card, positionRaw) {
		const titleRaw = getNavbarMegamenuLinkTextForTracking($card, false);

		const $accordionItem = $card.closest('.accordion__item');
		let categoryRaw = '';
		if ($accordionItem.length) {
			const $accTitle = $accordionItem.find('.accordion__toggle .accordion__title').first();
			categoryRaw = getNavbarMegamenuLevel2CategoryTitle($accTitle);
		}

		return buildNavbarMenuPayload('second_level', positionRaw, titleRaw, '', $target);
	}

	function getNavbarDynamicMenuTrackingPayload(e) {
		const $target = $(e.target);
		const $dynamicRoot = getNavbarDynamicMenuRoot($target);
		if (!$dynamicRoot.length) {
			return null;
		}

		const $l1Anchor = $target.closest('a.menuitem-level1');
		if ($l1Anchor.length && $dynamicRoot[0].contains($l1Anchor[0])) {
			return buildNavbarMenuPayload('first_level', getNavbarMegamenuLevel1PositionFromContext($l1Anchor), '', '', $target);
		}

		const $inner = $target.closest('.navigation-menu-column__inner');
		if (!$inner.length || !$dynamicRoot[0].contains($inner[0])) {
			return null;
		}

		const positionRaw = getNavbarMegamenuLevel1PositionFromContext($inner);
		return getNavbarMegamenuAccordionPayload($target, $inner, positionRaw, {
			skipMenuitemL1Links: true
		});
	}

	function getNavbarEditorialMenuTrackingPayload(e) {
		const $target = $(e.target);
		const $li = $target.closest('li.navbar-item-level-1');
		if (!$li.length || $li.closest('[data-lvz-header-menu="true"]').length) {
			return null;
		}
		if ($li.find('ul.dynamic-menu').length) {
			return null;
		}

		const positionRaw = getNavbarMegamenuLevel1PositionFromLi($li);
		const hasEditorMenu = $li.find('.dd__menu.editor-menu').length > 0;

		if (!hasEditorMenu) {
			const $l1Toggle = $target.closest('.dd__toggle');
			if ($l1Toggle.length && $li[0].contains($l1Toggle[0])) {
				return buildNavbarMenuPayload('first_level', positionRaw, '', '', $target);
			}
			return null;
		}

		const $editorMenu = $target.closest('.editor-menu');
		if (!$editorMenu.length) {
			const $l1Toggle = $target.closest('.dd__toggle');
			if ($l1Toggle.length && $li[0].contains($l1Toggle[0])) {
				return buildNavbarMenuPayload('first_level', positionRaw, '', '', $target);
			}
			return null;
		}

		if (!$li[0].contains($editorMenu[0])) {
			return null;
		}

		const $smallCard = $target.closest('a.lavazza-card--sm');
		if ($smallCard.length && $editorMenu[0].contains($smallCard[0])) {
			return getNavbarEditorialMenuSmallCardPayload($target, $smallCard, positionRaw);
		}

		return getNavbarMegamenuAccordionPayload($target, $editorMenu, positionRaw, {
			stripAccordionDescriptions: true
		});
	}

	function getNavbarMenuTrackingPayload(e) {
		return getNavbarDynamicMenuTrackingPayload(e) || getNavbarEditorialMenuTrackingPayload(e);
	}

	const navbarMenuClickSelector = [
		'.lavazza-navbar [data-lvz-header-menu] a.menuitem-level1',
		'.lavazza-navbar [data-lvz-header-menu] .navigation-menu-column__inner a[href]',
		'.lavazza-navbar [data-lvz-header-menu] .navigation-menu-column__inner button.accordion__toggle',
		'.lavazza-navbar li.navbar-item-level-1 .editor-menu a[href]',
		'.lavazza-navbar li.navbar-item-level-1 .editor-menu button.accordion__toggle',
		'.lavazza-navbar li.navbar-item-level-1 .dd__toggle'
	].join(',');

	// Delegated so clicks work if the menu HTML is injected after load.
	$(document).on('click', navbarMenuClickSelector, function onNavbarMenuTracking(e) {
		const payload = getNavbarMenuTrackingPayload(e);
		if (!payload) {
			return;
		}
		gtmUtils.sendUtagLinkEvent(payload);
	});

	// Top banner component — same payload shape as `.tealium-editorial-component`; `data-tealium-text` may contain markup.
	$('.lvz-top-banner__text').on('click', function onTopBannerClick(e) {
		e.preventDefault();
		const $link = $(e.target).closest('a');
		if (!$link.length) {
			return;
		}

		const $item = $(this);
		const $labelEl = $item.find('[data-tealium-text]').first();

		const labelRaw = ($labelEl.length ? $labelEl.attr('data-tealium-text') : $item.attr('data-tealium-text')) || '';

		gtmUtils.sendUtagLinkEvent({
			event_name: 'header_promo',
			type: '',
			category: trackingEditorialAttrSerialized(labelRaw)
		});
		navigateAfterTrackedLink(e, $link[0]);
	});

	// Footer social links component
	$('.footer-brand-bar-social-item').on('click', function onFooterSocialLinksClick(e) {
		const $item = $(this);
		const $labelEl = $item.find('[data-tealium-text]').first();
		const labelAttr = ($labelEl.length ? $labelEl.attr('data-tealium-text') : $item.attr('data-tealium-text')) || '';

		gtmUtils.sendUtagLinkEvent({
			event_name: 'social',
			category: trackingEditorialAttrSerialized(labelAttr)
		});
	});

	// Country selector
	function distributionCountryFromAnchor($anchor) {
		const fromAttr = ($anchor.attr('data-country-code') || '').trim().toLowerCase();
		if (fromAttr) {
			return fromAttr;
		}
		const localeId = ($anchor.attr('data-locale') || '').trim();
		const parts = localeId.split(/[_-]/);
		if (parts.length >= 2) {
			return parts[parts.length - 1].toLowerCase();
		}
		return '';
	}

	$(document).on('click', '.tealium-country-selector-item', function onTrackingCountrySelectorClick(e) {
		e.preventDefault();
		const $anchor = $(this);
		const anchorEl = $anchor[0];
		const category = ($anchor.attr('data-language') || '').trim();

		// PDF: "website|<site>" when the country changes (splash page), "language|<language>" otherwise.
		gtmUtils.sendUtagLinkEvent({
			event_name: 'change_country_site_language',
			distribution_country: distributionCountryFromAnchor($anchor),
			category: ($anchor.attr('data-tealium-category') || '').trim() || 'language|' + category
		});

		navigateAfterTrackedLink(e, anchorEl);
	});

	// Distributors page
	$('.tealium-distributors-accordion-item').on('click', function onTrackingDistributorsAccordionItemClick(e) {
		e.preventDefault();
		const $item = $(this);
		const countryCode = $item.attr('data-tealium').toLowerCase();

		gtmUtils.sendUtagLinkEvent({
			event_name: 'distributor_layer_open',
			distribution_country: countryCode
		});
	});

	$(document).on('click', '.tealium-distributor-email-item', function onTrackingDistributorsEmailClick(e) {
		e.preventDefault();
		const $item = $(this);
		const anchorEl = $item[0];
		const countryCode = ($item.attr('data-country-code') || '').trim().toLowerCase();
		const distributionName = ($item.attr('data-distribution-name') || '').trim();

		gtmUtils.sendUtagLinkEvent({
			event_name: 'distributor_contact_email',
			distribution_country: countryCode,
			distribution_name: trackingEditorialAttrSerialized(distributionName)
		});

		navigateAfterTrackedLink(e, anchorEl);
	});

	$(document).on('click', '.tealium-distributor-website-item', function onTrackingDistributorsWebsiteClick(e) {
		e.preventDefault();
		const $item = $(this);
		const anchorEl = $item[0];
		const countryCode = ($item.attr('data-country-code') || '').trim().toLowerCase();
		const distributionName = ($item.attr('data-distribution-name') || '').trim();

		gtmUtils.sendUtagLinkEvent({
			event_name: 'distributor_contact_website',
			distribution_country: countryCode,
			distribution_name: trackingEditorialAttrSerialized(distributionName)
		});

		navigateAfterTrackedLink(e, anchorEl);
	});

	$(document).on('click', '.lvz-announcement-banner__text-wrap a', function onTrackingAnnouncementBannerClick(e) {
		const $item = $(this);
		const anchorEl = $item[0];
		const label = trackingEditorialAttrSerialized($item.text().trim());

		// PDF (v2, 22/05): label and category are both the CTA text.
		gtmUtils.sendUtagLinkEvent({
			event_name: 'widget',
			type: 'announcement_banner',
			label: label,
			category: label
		});

		navigateAfterTrackedLink(e, anchorEl);
	});

	// Editorial cards + Full Card CTAs (`button[data-lvz-cta-button]` in `[data-lvz-full-card]`; nav: `lvzFullCard.js`).
	$(document).on(
		'click',
		'.tealium-editorial-card, [data-lvz-full-card="true"] [data-lvz-cta-button="true"]',
		function onEditorialCardTrackingClick(e) {
			const $scope = $(this);
			const $target = $(e.target);
			const isCta = $scope.is('[data-lvz-cta-button="true"]');
			const $gridCardItem = $scope.closest('.lvz-grid-card-item.tealium-editorial-card');

			if (isInsideNavbarMegamenu($scope)) {
				return;
			}

			let $link = $();
			if (!isCta) {
				$link = resolveNestedHrefLink($scope, $target);
				if (!$link.length && shouldSkipNestedInteractiveClick($scope, $target)) {
					return;
				}
			}

			// Grid card items are <article> with an inner CTA link — keep card attrs on the article.
			const $attrRoot = resolveEditorialCardAttrRoot($scope, $link, isCta, $gridCardItem);
			const $wrap = findLavazzaEditorialCardContainer($attrRoot);
			const eventName = lavazzaEditorialCardAttr($attrRoot, $wrap, 'data-tealium-card-event-name');
			const type = resolveEditorialCardType($wrap, $attrRoot);
			const labelRaw = resolveEditorialCardLabel($scope, $attrRoot, isCta && !$gridCardItem.length);
			const categoryRaw = resolveEditorialCardCategory($wrap, $attrRoot);
			const labelRawResolved = resolveMagazineEditorialCardLabel($attrRoot, $link, labelRaw, type);

			const labelPlain = trackingMarkupToPlainText(labelRawResolved);
			const categoryPlain = trackingMarkupToPlainText(categoryRaw);
			if (!(eventName || type || labelPlain || categoryPlain)) {
				return;
			}

			if ($link.length && $link.is('a[href]')) {
				e.preventDefault();
			}

			gtmUtils.sendUtagLinkEvent({
				event_name: eventName,
				type: type,
				label: trackingEditorialAttrSerialized(labelRawResolved),
				category: trackingEditorialAttrSerialized(categoryRaw)
			});

			if ($link.length && $link.is('a[href]')) {
				navigateAfterTrackedLink(e, $link[0]);
			}
		}
	);

	// Generic Tracking for Editorial Components
	// Link: <a> may carry the class + data-*, or a wrapper may carry them with <a> inside.
	// Non-link: click on the tealium-marked area without an anchor still sends data-* from the wrapper (no navigation).
	// Without a link: skip clicks on nested controls (e.g. close inside a tealium wrapper). If the tealium
	// root itself is the button/input (CTA), still send the beacon — do not skip.
	$(document).on('click', '.tealium-editorial-component', function onGenericTrackingEditorialComponentClick(e) {
		const $scope = $(this);
		const $target = $(e.target);

		// Grid card CTA is tracked by the editorial-card handler as type grid_card.
		if ($scope.closest('.lvz-grid-card-item.tealium-editorial-card').length) {
			return;
		}

		// Megamenu uses dedicated `menu` tracking; do not send generic editorial/widget here.
		if (isInsideNavbarMegamenu($scope)) {
			return;
		}

		const $link = resolveNestedHrefLink($scope, $target);

		if (!$link.length && shouldSkipNestedInteractiveClick($scope, $target)) {
			return;
		}

		const attrs = readGenericEditorialTrackingAttrs($scope, $link);
		const labelPlain = trackingMarkupToPlainText(attrs.labelRaw);
		const categoryPlain = trackingMarkupToPlainText(attrs.categoryRaw);
		const label = trackingEditorialAttrSerialized(attrs.labelRaw);
		const category = trackingEditorialAttrSerialized(attrs.categoryRaw);

		const hasPayload = !!(attrs.eventName || attrs.type || labelPlain || categoryPlain);
		if (!hasPayload) {
			return;
		}

		if ($link.length) {
			e.preventDefault();
		}

		const payload = {
			event_name: attrs.eventName,
			type: attrs.type,
			label: label
		};

		// Components without a category in the plan (e.g. FAQ "Read all FAQs") must not send an empty one.
		if (category) {
			payload.category = category;
		}

		gtmUtils.sendUtagLinkEvent(payload);

		if ($link.length) {
			navigateAfterTrackedLink(e, $link[0]);
		}
	});

	// Discover banner
	$(document).on(
		'click',
		'.lvz-discover-banner__ctas[data-discover-banner-analytics="true"] [data-lvz-cta-button="true"], .lvz-discover-banner__ctas[data-discover-banner-analytics="true"] a[href]',
		function onTrackingDiscoverBannerCtaClick(e) {
			const $cta = $(this);
			const $container = $cta.closest('.lvz-discover-banner__ctas[data-discover-banner-analytics="true"]');
			if (!$container.length) {
				return;
			}

			// A CTA button may wrap a link: both selectors match the same click, track the innermost only.
			if ($(e.target).closest('[data-lvz-cta-button="true"], a[href]')[0] !== this) {
				return;
			}

			const eventName = normalizeWidgetToken($container.attr('data-tealium-event-name') || '');
			const type = normalizeWidgetToken($container.attr('data-tealium-type') || '');
			const category = ($container.attr('data-tealium-category') || '').trim();
			// PDF label = CTA text; aria-label is the accessibility sentence ("Discover more about ...").
			const label = (
				$cta.attr('data-tealium-label') ||
				$cta.find('.btn__label').first().text() ||
				$cta.text() ||
				$cta.attr('aria-label') ||
				''
			).trim();

			gtmUtils.sendUtagLinkEvent({
				event_name: eventName,
				type: type,
				label: trackingEditorialAttrSerialized(label),
				category: trackingEditorialAttrSerialized(category)
			});
		}
	);

	const popover = document.querySelector('.minicart .popover');
	let hasTrackedMinicartViewThisPage = false;

	function getMinicartProducts(panel) {
		return gtmUtils.parseFormattedProductModules($(panel).attr('data-tealium-product-modules'));
	}

	function trackMinicartViewCart(minicartPanel) {
		if (hasTrackedMinicartViewThisPage || !minicartPanel) {
			return;
		}

		hasTrackedMinicartViewThisPage = true;

		gtmUtils.sendUtagLinkEvent({
			event_name: 'minicart_view-cart',
			product_impression_type: 'cart',
			products: getMinicartProducts(minicartPanel)
		});
	}

	if (popover) {
		const observer = new MutationObserver(function (mutations) {
			mutations.forEach(function (mutation) {
				if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
					if (popover.classList.contains('show')) {
						trackMinicartViewCart(popover.querySelector('.minicart-panel'));
					}
					return;
				}

				if (mutation.type === 'childList' && mutation.addedNodes.length) {
					mutation.addedNodes.forEach(function (node) {
						if (node.nodeType === 1) {
							const minicartPanel = node.classList.contains('minicart-panel')
								? node
								: node.querySelector('.minicart-panel');

							if (minicartPanel) {
								trackMinicartViewCart(minicartPanel);
							}
						}
					});
				}
			});
		});

		observer.observe(popover, {
			attributes: true,
			attributeFilter: ['class'],
			childList: true,
			subtree: true
		});
	}

	$(document).on('click', '.btn.minicart-footer__checkout-link', function onTrackingMinicartCheckoutClick(e) {
		e.preventDefault();
		const $item = $(this);
		const anchorEl = $item[0];

		try {
			const $minicartPanel = $('.minicart-panel');
			const products = getMinicartProducts($minicartPanel);

			gtmUtils.sendUtagLinkEvent({
				event_name: 'minicart_go-to-checkout',
				products: products
			});
		} catch (err) {
			// never block minicart checkout
		}

		navigateAfterTrackedLink(e, anchorEl);
	});

	$(document).on('click', '.btn.minicart-go-to-cart-btn', function onTrackingMinicartGoToCartClick(e) {
		e.preventDefault();
		const $item = $(this);
		const anchorEl = $item[0];

		try {
			const $minicartPanel = $('.minicart-panel');
			const products = getMinicartProducts($minicartPanel);

			gtmUtils.sendUtagLinkEvent({
				event_name: 'minicart_go-to-cart',
				products: products
			});
		} catch (err) {
			// never block minicart navigation
		}

		navigateAfterTrackedLink(e, anchorEl);
	});

	$(document).on('click', '.product-detail [data-lvz-product-features-toggle]', function onTrackingPdpProductInfoClick() {
		let $button = $(this);

		if ($button.attr('aria-expanded') !== 'true') {
			return;
		}

		let $productDetail = $button.closest('.product-detail');
		let $features = $button.closest('[data-lvz-product-features]');
		let label = $productDetail.find('h1.product-name').first().text().trim();
		let category = String($productDetail.data('pid') || '').trim();
		let type = $features.find('.lvz-product-features__title').first().text().trim();

		gtmUtils.sendUtagLinkEvent({
			event_name: 'view_more_info',
			label: sanitizeLinkText(label),
			category: sanitizeLinkText(category),
			type: sanitizeLinkText(type)
		});
	});

	$(document).on('click', '.js-lvz-title-text-banner__toggle', function onTrackingCategorySeoToggleClick() {
		let $button = $(this);
		let isExpanded = $button.attr('aria-expanded') === 'true';

		let label = isExpanded ? 'read_more' : 'read_less';
		let category = $button.closest('.js-lvz-title-text-banner').find('.lvz-title-text-banner__title').first().text().trim();

		gtmUtils.sendUtagLinkEvent({
			event_name: 'widget',
			type: 'long_description',
			label: label,
			category: gtmUtils.sanitizeLinkText(category)
		});
	});

	$(document).on('search:showMore', function onTrackingLoadMorePLP() {
		gtmUtils.sendUtagLinkEvent({
			event_name: 'load_more'
		});
	});
});

/**
 * Global tracking entrypoint.
 *
 * Tracking logic and Tealium payload construction must stay in `gtm.js` / `gtmUtils.js`.
 * When possible, prefer binding the tracking directly here in `gtm.js` on the actual UI event that must be tracked (`click`, `change`, page view, etc.), so the tracking both starts and ends here.
 * If that is not practical because the same application event also drives other logic outside GTM (for example Gigya login/register callbacks or complex subscription flow transitions), other modules may call `window.lavazzaGtm.*` to signal the tracking moment.
 * `window.lavazzaGtm.*` is preferred over custom `trigger` / `emit` tracking events because the call path is explicit, easier to follow, and avoids fragile string-based event bridges created only to forward tracking from one file to another.
 *
 * External modules must not build tracking payloads or emit custom events created only for GTM.
 *
 * Application/business events (e.g. cart/update, checkout stage changes) can still be observed here when they already exist as neutral shared signals in the application flow.
 */

function exposeGtmApi() {
	window.lavazzaGtm = window.lavazzaGtm || {};
	window.lavazzaGtm.checkout = {
		setPendingApplyPromoCode: setPendingApplyPromoCode,
		clearPendingApplyPromoCode: clearPendingApplyPromoCode,
		trackApplyPromo: trackApplyPromo,
		trackApplyPromoIfPending: trackApplyPromoIfPending,
		trackProceedAsGuest: trackProceedAsGuest,
		trackPaypalExpressCart: function trackPaypalExpressCartPublic() {
			trackPaypalExpressCartFromUserIntent('lavazzaGtm.checkout.trackPaypalExpressCart');
		}
	};
	window.lavazzaGtm.auth = {
		trackLoginOk: trackLoginOk,
		handleRegistrationAfterSubmit: handleRegistrationAfterSubmit,
		handleRegistrationScreenLoad: handleRegistrationScreenLoad,
		handleSocialRegistrationLogin: handleSocialRegistrationLogin,
		handleForgotPasswordAfterSubmit: handleForgotPasswordAfterSubmit,
		handleForgotPasswordScreenLoad: handleForgotPasswordScreenLoad
	};
	window.lavazzaGtm.subscription = {
		trackSubscriptionContinueLink: trackSubscriptionContinueLink,
		trackSubscriptionStepView: trackSubscriptionStepView,
		trackSubscriptionCoffeeCustomStepView: trackSubscriptionCoffeeCustomStepView,
		trackSubscriptionDiscover: trackSubscriptionDiscover,
		trackSubscriptionResetAssortment: trackSubscriptionResetAssortment,
		trackSubscriptionChangingTier: trackSubscriptionChangingTier,
		trackSubscriptionLeavingTierChange: trackSubscriptionLeavingTierChange,
		trackSubscriptionTierChange: trackSubscriptionTierChange,
		trackSubscriptionAbandonModal: trackSubscriptionAbandonModal,
		trackSubscriptionLeavingFlowChoice: trackSubscriptionLeavingFlowChoice,
		trackSubscriptionReplaceModal: trackSubscriptionReplaceModal,
		trackSubscriptionReplaceFlowChoice: trackSubscriptionReplaceFlowChoice,
		trackSubscriptionViewEditSummary: trackSubscriptionViewEditSummary,
		trackSubscriptionEditFromSummary: trackSubscriptionEditFromSummary,
		trackSubscriptionChangingFrequency: trackSubscriptionChangingFrequency,
		trackSubscriptionLeavingFrequencyChange: trackSubscriptionLeavingFrequencyChange,
		trackSubscriptionFrequencyChange: trackSubscriptionFrequencyChange,
		trackSubscriptionAddToCart: trackSubscriptionAddToCart,
		markCoffeeCustomStepViewSent: markCoffeeCustomStepViewSent,
		trackSubscriptionManagementEvent: trackSubscriptionManagementEvent,
		trackSubscriptionEditFrequencySelected: trackSubscriptionEditFrequencySelected
	};
	window.lavazzaGtm.ecommerce = {
		syncTileTealiumProduct: gtmUtils.syncTileTealiumProduct,
		trackProductTileClick: trackProductClick,
		trackNotifyMeOpen: trackNotifyMeOpen,
		trackNotifyMeSubmit: trackNotifyMeSubmit
	};
	window.lavazzaGtm.plp = {
		trackFilterCommit: trackPlpFilterCommit,
		trackVariantSelected: trackPlpVariantSelected
	};
	window.lavazzaGtm.search = {
		trackLayerOpen: trackSearchLayerOpen,
		trackLayerClose: trackSearchLayerClose,
		trackDirect: trackSearchDirect,
		trackAssisted: trackSearchAssisted,
		trackQuery: trackSearchQuery
	};
	window.lavazzaGtm.media = {
		trackVideoStart: trackVideoStart,
		trackVideoPlay: trackVideoPlay,
		trackVideoPause: trackVideoPause,
		trackVideoEnded: trackVideoEnded,
		trackPdpGalleryView: trackPdpGalleryView
	};
}

(function initAnalyticsFeatureModules() {
	initCheckoutExpressTracking();
	exposeGtmApi();
	initVideoTracking();
	initSocialProviderCapture();
	initCheckoutStepTracking();
	initCheckoutGuestTracking();
	initCartSubscriptionTracking();
	initEcommerceTracking();
	initSearchLayerTracking();
	initCheckOrderStatusTracking();
	initMachineRegistrationTracking();
	initSubscriptionManagementTracking();
	initRecurringPurchaseTracking();
	initSubscriptionEditSaveTracking();
	initDownloadTracking();
	initPdp3dArTracking();
	initBundleMoreInfoTracking();
	initMagazineTracking();
	initFaqTracking();
	initMyLavazzaTracking();
	initChatMokyOpenTracking();
	initContactUsStipTracking();
	initLeadFormTracking();
	initPlpFilterTracking();
	scheduleAnalyticsPageView();
})();
